import { useEffect, useMemo, useRef, useState, lazy, Suspense } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import { Document, Page, pdfjs } from "react-pdf";
import AppShell from "../components/layout/AppShell";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Icon from "../components/ui/Icon";
import Modal from "../components/ui/Modal";
import BiometricSettingsModal from "../components/verification/BiometricSettingsModal";
import { Parse } from "../lib/parse";
import { MAX_DOCUMENT_FILE_SIZE_MB } from "../config/uploadLimits";
import {
  extractVerificationConfig,
  resolveRequireDocumentBack
} from "../components/verification/verificationUtils";
import { useAlert } from "../contexts/AlertContext";

// Lazy load VerificationModal for performance
const VerificationModal = lazy(() => import("../components/verification/VerificationModal"));

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/pdf.worker.min.mjs`;

const TOOL_DEFINITIONS = [
  { type: "signature", icon: "signature", label: "Signature" },
  { type: "stamp", icon: "check", label: "Stamp" },
  { type: "initials", icon: "signature", label: "Initials" },
  { type: "name", icon: "user", label: "Name" },
  { type: "job title", icon: "templateDoc", label: "Job title" },
  { type: "company", icon: "layout", label: "Company" },
  { type: "date", icon: "clock", label: "Date" },
  { type: "text", icon: "templateDoc", label: "Text" },
  { type: "checkbox", icon: "check", label: "Checkbox" },
  { type: "image", icon: "templateDoc", label: "Image" },
  { type: "email", icon: "mail", label: "Email" }
];

const TOOL_TYPES = new Set(TOOL_DEFINITIONS.map((toolDefinition) => toolDefinition.type));
const IMAGE_VALUE_TYPES = new Set(["signature", "stamp", "image"]);
const TEXT_VALUE_TYPES = new Set(["text", "name", "job title", "company", "email", "initials"]);
const RECIPIENT_AUTOFILL_TYPES = new Set(["email"]);
const RESIZABLE_TYPES = new Set(TOOL_TYPES);
const MIN_ZOOM = 0.6;
const MAX_ZOOM = 2;
const ZOOM_STEP = 0.1;
const DEFAULT_INITIAL_ZOOM = 0.82;
const MIN_PLACEHOLDER_WIDTH = 0.08;
const MIN_PLACEHOLDER_HEIGHT = 0.02;
const MAX_FORWARD_RECIPIENTS = 10;
const REQUEST_SIGN_SENT_REDIRECT = "/report/1MwEuxLEkF";
const EMAIL_TEMPLATE_MODE = {
  HTML: "html",
  PLAIN: "plain"
};
const EMAIL_TEMPLATE_CONTENT_START = "<!-- DANA_CUSTOM_CONTENT_START -->";
const EMAIL_TEMPLATE_CONTENT_END = "<!-- DANA_CUSTOM_CONTENT_END -->";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REQUEST_EMAIL_SOURCE = {
  CORPORATE: "corporate",
  PERSONAL: "personal"
};
const PERSONAL_REQUEST_EMAIL_ADMIN_ROLES = new Set(["contracts_Admin", "contracts_OrgAdmin"]);
const PERSONAL_REQUEST_EMAIL_EDITOR_ROLES = new Set(["contracts_Editor"]);
const PERSONAL_REQUEST_EMAIL_USER_ROLES = new Set(["contracts_User"]);
const TOOL_DRAG_MIME = "application/x-danasign-tool";
const CONTACTS_REPORT_ID = "contacts";
const normalizeEmailAddress = (value) => String(value || "").trim().toLowerCase();
const RECIPIENT_COLOR_THEMES = [
  {
    key: "indigo",
    base: "bg-indigo-100 text-indigo-700 border-indigo-200",
    menu: "bg-indigo-100 text-indigo-500 border-indigo-200",
    widget: "bg-indigo-200/40 text-indigo-700 border-indigo-300"
  },
  {
    key: "emerald",
    base: "bg-emerald-100 text-emerald-700 border-emerald-200",
    menu: "bg-emerald-100 text-emerald-500 border-emerald-200",
    widget: "bg-emerald-200/40 text-emerald-700 border-emerald-300"
  },
  {
    key: "sky",
    base: "bg-sky-100 text-sky-700 border-sky-200",
    menu: "bg-sky-100 text-sky-500 border-sky-200",
    widget: "bg-sky-200/40 text-sky-700 border-sky-300"
  },
  {
    key: "amber",
    base: "bg-amber-100 text-amber-700 border-amber-200",
    menu: "bg-amber-100 text-amber-500 border-amber-200",
    widget: "bg-amber-200/40 text-amber-700 border-amber-300"
  },
  {
    key: "rose",
    base: "bg-rose-100 text-rose-700 border-rose-200",
    menu: "bg-rose-100 text-rose-500 border-rose-200",
    widget: "bg-rose-200/40 text-rose-700 border-rose-300"
  },
  {
    key: "violet",
    base: "bg-violet-100 text-violet-700 border-violet-200",
    menu: "bg-violet-100 text-violet-500 border-violet-200",
    widget: "bg-violet-200/40 text-violet-700 border-violet-300"
  }
];
const RECIPIENT_COLORS = RECIPIENT_COLOR_THEMES.map((theme) => theme.base);
const DEFAULT_SIGNATURE_VALIDATION = {
  minArea: 1500,
  minLength: 180
};

const normalizeRightAngle = (value) => {
  const raw = Number(value);
  if (!Number.isFinite(raw)) {
    return 0;
  }
  const snapped = Math.round(raw / 90) * 90;
  return ((snapped % 360) + 360) % 360;
};

const DEFAULT_WIDGET_SIZE = {
  signature: { width: 0.26, height: 0.045 },
  stamp: { width: 0.22, height: 0.04 },
  initials: { width: 0.16, height: 0.035 },
  name: { width: 0.24, height: 0.03 },
  "job title": { width: 0.24, height: 0.03 },
  company: { width: 0.24, height: 0.03 },
  text: { width: 0.24, height: 0.03 },
  date: { width: 0.2, height: 0.03 },
  checkbox: { width: 0.2, height: 0.022 },
  image: { width: 0.2, height: 0.05 },
  email: { width: 0.28, height: 0.03 }
};

const TEXT_STYLE_WIDGET_TYPES = new Set([...TEXT_VALUE_TYPES, "date"]);
const TEXT_STYLE_FONT_CHOICES = [
  { value: "helvetica", label: "Helvetica", css: "Helvetica, Arial, sans-serif" },
  { value: "times", label: "Times", css: "\"Times New Roman\", Times, serif" },
  { value: "courier", label: "Courier", css: "\"Courier New\", Courier, monospace" }
];
const TEXT_STYLE_FONT_SET = new Set(TEXT_STYLE_FONT_CHOICES.map((fontItem) => fontItem.value));
const DEFAULT_WIDGET_TEXT_STYLE = {
  fontFamily: "helvetica",
  fontSize: 12,
  color: "#1f2937",
  bold: false,
  italic: false,
  underline: false
};

const normalizeBooleanValue = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized) return fallback;
  if (["1", "true", "yes", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "off"].includes(normalized)) return false;
  return fallback;
};

const normalizeHexColor = (value, fallback = DEFAULT_WIDGET_TEXT_STYLE.color) => {
  const normalized = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(normalized)) {
    return normalized.toLowerCase();
  }
  if (/^#[0-9a-fA-F]{3}$/.test(normalized)) {
    const compact = normalized.slice(1);
    return `#${compact[0]}${compact[0]}${compact[1]}${compact[1]}${compact[2]}${compact[2]}`.toLowerCase();
  }
  return fallback;
};

const resolveFontCssFamily = (fontFamily = DEFAULT_WIDGET_TEXT_STYLE.fontFamily) => {
  const normalizedFont = String(fontFamily || "").trim().toLowerCase();
  const matched = TEXT_STYLE_FONT_CHOICES.find((fontItem) => fontItem.value === normalizedFont);
  return matched?.css || TEXT_STYLE_FONT_CHOICES[0].css;
};

const sanitizeTextStyle = (rawStyle = {}) => {
  const source =
    rawStyle && typeof rawStyle === "object" && !Array.isArray(rawStyle) ? rawStyle : {};
  const fontFamily = String(source.fontFamily || source.font || "").trim().toLowerCase();
  const normalizedFontFamily = TEXT_STYLE_FONT_SET.has(fontFamily)
    ? fontFamily
    : DEFAULT_WIDGET_TEXT_STYLE.fontFamily;
  const parsedFontSize = Number.parseInt(source.fontSize, 10);
  const fontSize = Number.isFinite(parsedFontSize)
    ? clamp(parsedFontSize, 8, 48)
    : DEFAULT_WIDGET_TEXT_STYLE.fontSize;
  const color = normalizeHexColor(source.color || source.textColor, DEFAULT_WIDGET_TEXT_STYLE.color);
  const bold = normalizeBooleanValue(source.bold ?? source.isBold, DEFAULT_WIDGET_TEXT_STYLE.bold);
  const italic = normalizeBooleanValue(
    source.italic ?? source.isItalic,
    DEFAULT_WIDGET_TEXT_STYLE.italic
  );
  const underline = normalizeBooleanValue(
    source.underline ?? source.isUnderline,
    DEFAULT_WIDGET_TEXT_STYLE.underline
  );

  return {
    fontFamily: normalizedFontFamily,
    fontSize,
    color,
    bold,
    italic,
    underline
  };
};

const resolveWidgetTextStyle = (widget = {}) => {
  const widgetOptions =
    widget?.options && typeof widget.options === "object" && !Array.isArray(widget.options)
      ? widget.options
      : {};
  const explicitStyle = widget?.textStyle || widget?.TextStyle || widgetOptions?.textStyle || widgetOptions?.TextStyle;
  if (explicitStyle && typeof explicitStyle === "object" && !Array.isArray(explicitStyle)) {
    return sanitizeTextStyle(explicitStyle);
  }
  const inlineStyleCandidate = {
    fontFamily:
      widget?.fontFamily ||
      widget?.font ||
      widgetOptions?.fontFamily ||
      widgetOptions?.font,
    fontSize: widget?.fontSize || widgetOptions?.fontSize,
    color: widget?.color || widget?.textColor || widgetOptions?.color || widgetOptions?.textColor,
    bold: widget?.bold ?? widget?.isBold ?? widgetOptions?.bold ?? widgetOptions?.isBold,
    italic: widget?.italic ?? widget?.isItalic ?? widgetOptions?.italic ?? widgetOptions?.isItalic,
    underline:
      widget?.underline ??
      widget?.isUnderline ??
      widgetOptions?.underline ??
      widgetOptions?.isUnderline
  };
  return sanitizeTextStyle(inlineStyleCandidate);
};

const hasExplicitWidgetTextStyle = (widget = {}) => {
  if (!widget || typeof widget !== "object") {
    return false;
  }
  const options =
    widget?.options && typeof widget.options === "object" && !Array.isArray(widget.options)
      ? widget.options
      : {};
  const explicitObjectStyle =
    (widget?.textStyle && typeof widget.textStyle === "object") ||
    (widget?.TextStyle && typeof widget.TextStyle === "object") ||
    (options?.textStyle && typeof options.textStyle === "object") ||
    (options?.TextStyle && typeof options.TextStyle === "object");
  if (explicitObjectStyle) {
    return true;
  }
  const inlineKeys = [
    "fontFamily",
    "font",
    "fontSize",
    "color",
    "textColor",
    "bold",
    "isBold",
    "italic",
    "isItalic",
    "underline",
    "isUnderline"
  ];
  return inlineKeys.some((key) => widget?.[key] !== undefined || options?.[key] !== undefined);
};

const hexToPdfRgb = (hexColor) => {
  const normalized = normalizeHexColor(hexColor, DEFAULT_WIDGET_TEXT_STYLE.color);
  const red = Number.parseInt(normalized.slice(1, 3), 16) / 255;
  const green = Number.parseInt(normalized.slice(3, 5), 16) / 255;
  const blue = Number.parseInt(normalized.slice(5, 7), 16) / 255;
  return { red, green, blue };
};

const parseStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const parseThreshold = (value, fallback) => {
  const parsedValue = Number.parseInt(value, 10);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

const extractSignatureThresholds = (source = {}) => ({
  minArea: parseThreshold(source?.MinSignatureArea, DEFAULT_SIGNATURE_VALIDATION.minArea),
  minLength: parseThreshold(source?.MinSignatureLength, DEFAULT_SIGNATURE_VALIDATION.minLength)
});

const getImageSize = (src) =>
  new Promise((resolve, reject) => {
    if (!src) {
      resolve({ width: 0, height: 0 });
      return;
    }
    const image = new Image();
    image.onload = () =>
      resolve({
        width: image.naturalWidth || image.width || 0,
        height: image.naturalHeight || image.height || 0
      });
    image.onerror = () => reject(new Error("image-load-failed"));
    image.src = src;
  });

const validateDrawnSignature = (canvasRef, minAreaThreshold, minLengthThreshold) => {
  if (!canvasRef?.current || typeof canvasRef.current.toData !== "function") {
    return false;
  }
  const strokes = canvasRef.current.toData();
  const points = [];
  strokes.forEach((stroke) => {
    (stroke.points || stroke || []).forEach((point) => points.push(point));
  });
  if (!points.length) {
    return false;
  }

  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const area = (maxX - minX) * (maxY - minY);

  let length = 0;
  strokes.forEach((stroke) => {
    const strokePoints = stroke.points || stroke || [];
    for (let i = 1; i < strokePoints.length; i += 1) {
      const dx = strokePoints[i].x - strokePoints[i - 1].x;
      const dy = strokePoints[i].y - strokePoints[i - 1].y;
      length += Math.sqrt(dx * dx + dy * dy);
    }
  });

  return area >= minAreaThreshold && length >= minLengthThreshold;
};

const validateImageSignature = (naturalWH, minAreaThreshold, minLengthThreshold) => {
  const area = (naturalWH?.width || 0) * (naturalWH?.height || 0);
  const longestSide = Math.max(naturalWH?.width || 0, naturalWH?.height || 0);
  return area >= minAreaThreshold && longestSide >= minLengthThreshold;
};

const normalizeDocumentPayload = (response) => {
  if (!response) return null;
  if (response?.error) return null;
  if (response?.result?.error) return null;
  return response?.result || response;
};

const getParseFunctionBaseUrl = () => {
  const baseUrl = String(localStorage.getItem("baseUrl") || "").trim();
  if (!baseUrl) {
    return "";
  }
  if (baseUrl.startsWith(":")) {
    return `${window.location.protocol}//${window.location.hostname}${baseUrl}`;
  }
  return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
};

const getParseFunctionHeaders = () => {
  const parseAppId = String(localStorage.getItem("parseAppId") || "").trim();
  const sessionToken = String(localStorage.getItem("accesstoken") || "").trim();
  return {
    "content-type": "Application/json",
    ...(parseAppId ? { "X-Parse-Application-Id": parseAppId } : {}),
    ...(sessionToken ? { sessionToken } : {}),
    ...(sessionToken ? { "X-Parse-Session-Token": sessionToken } : {})
  };
};

const buildSignedUrlCandidates = (rawUrl) => {
  const candidates = new Set();
  if (!rawUrl) {
    return [];
  }
  candidates.add(String(rawUrl).trim());
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    const addPathVariant = (pathname) => {
      if (!pathname) {
        return;
      }
      candidates.add(`${parsed.origin}${pathname}`);
    };

    addPathVariant(parsed.pathname);
    let decodedPath = parsed.pathname;
    for (let i = 0; i < 2; i += 1) {
      let nextPath = decodedPath;
      try {
        nextPath = decodeURIComponent(decodedPath);
      } catch (error) {
        break;
      }
      if (!nextPath || nextPath === decodedPath) {
        break;
      }
      decodedPath = nextPath;
      addPathVariant(decodedPath);
    }
  } catch (error) {
    // Keep original candidate only.
  }
  return Array.from(candidates).filter(Boolean);
};

const requestFreshSignedUrl = async (rawUrl, docId = "", templateId = "") => {
  const baseUrl = getParseFunctionBaseUrl();
  if (!rawUrl || !baseUrl) {
    return "";
  }
  const signingCandidates = buildSignedUrlCandidates(rawUrl);
  for (const candidate of signingCandidates) {
    try {
      const axiosRes = await axios.post(
        `${baseUrl}functions/getsignedurl`,
        {
          url: candidate,
          docId: docId || "",
          templateId: templateId || ""
        },
        {
          headers: getParseFunctionHeaders()
        }
      );
      const signedUrl = String(axiosRes?.data?.result || axiosRes?.data || "").trim();
      if (signedUrl) {
        return signedUrl;
      }
    } catch (error) {
      // Try the next candidate variant.
    }
  }
  return "";
};

const resolveDocumentUrl = async (rawUrl, docId = "") => {
  void docId;
  if (!rawUrl) {
    return "";
  }
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    const hasLocalToken = parsed.searchParams.has("token");
    if (parsed.pathname?.includes("/files/") && !hasLocalToken) {
      const cloudResponse = await Parse.Cloud.run("fileupload", { url: rawUrl });
      if (cloudResponse?.url) {
        return cloudResponse.url;
      }
      return rawUrl;
    }
    // Keep S3/document URLs exactly as returned by backend (legacy behavior).
    return rawUrl;
  } catch (error) {
    console.error("Document URL signing warning", error);
  }
  return rawUrl;
};

const fetchDocumentByRestEndpoint = async (docId) => {
  const baseUrl = getParseFunctionBaseUrl();
  if (!baseUrl) {
    return null;
  }
  const axiosRes = await axios.post(
    `${baseUrl}functions/getDocument`,
    { docId },
    {
      headers: getParseFunctionHeaders()
    }
  );
  return normalizeDocumentPayload(axiosRes?.data);
};

const fetchDocumentPayload = async (docId) => {
  try {
    const restDoc = await fetchDocumentByRestEndpoint(docId);
    if (restDoc) {
      return restDoc;
    }
  } catch (restError) {
    console.error("REST getDocument failed, trying Cloud function fallback", restError);
  }

  try {
    const response = await Parse.Cloud.run("getDocument", { docId });
    const cloudDoc = normalizeDocumentPayload(response);
    if (cloudDoc) {
      return cloudDoc;
    }
  } catch (cloudError) {
    console.error("Cloud getDocument failed", cloudError);
  }
  return null;
};

const normalizeUnitValue = (value, fallback = 0) => {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return fallback;
  if (parsed <= 1) return Math.max(0, Math.min(1, parsed));
  if (parsed <= 100) return Math.max(0, Math.min(1, parsed / 100));
  return fallback;
};

const parseCheckboxResponse = (value) => {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "number") {
    return value === 1;
  }
  if (Array.isArray(value)) {
    if (!value.length) return false;
    return value.some((item) => parseCheckboxResponse(item));
  }
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return ["1", "true", "yes", "checked", "on"].includes(normalized);
};

const looksLikeBooleanCheckboxValue = (value) => {
  if (typeof value === "boolean" || typeof value === "number") {
    return true;
  }
  if (typeof value !== "string") {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return ["1", "0", "true", "false", "yes", "no", "checked", "on", "off"].includes(normalized);
};

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const fitPlaceholderToCanvas = (placeholder = {}) => {
  const safeWidth = clamp(
    Number.isFinite(Number(placeholder?.width)) ? Number(placeholder.width) : MIN_PLACEHOLDER_WIDTH,
    MIN_PLACEHOLDER_WIDTH,
    1
  );
  const safeHeight = clamp(
    Number.isFinite(Number(placeholder?.height)) ? Number(placeholder.height) : MIN_PLACEHOLDER_HEIGHT,
    MIN_PLACEHOLDER_HEIGHT,
    1
  );
  const maxX = Math.max(0, 1 - safeWidth);
  const maxY = Math.max(0, 1 - safeHeight);
  const safeX = clamp(Number.isFinite(Number(placeholder?.x)) ? Number(placeholder.x) : 0, 0, maxX);
  const safeY = clamp(Number.isFinite(Number(placeholder?.y)) ? Number(placeholder.y) : 0, 0, maxY);
  return {
    ...placeholder,
    x: safeX,
    y: safeY,
    width: safeWidth,
    height: safeHeight
  };
};

const BOUNDS_EPSILON = 0.000001;

const areBoundsEqual = (left = {}, right = {}) =>
  Math.abs(Number(left?.x || 0) - Number(right?.x || 0)) < BOUNDS_EPSILON &&
  Math.abs(Number(left?.y || 0) - Number(right?.y || 0)) < BOUNDS_EPSILON &&
  Math.abs(Number(left?.width || 0) - Number(right?.width || 0)) < BOUNDS_EPSILON &&
  Math.abs(Number(left?.height || 0) - Number(right?.height || 0)) < BOUNDS_EPSILON;

const normalizePlaceholderBounds = (items = []) => {
  if (!Array.isArray(items) || !items.length) {
    return items;
  }
  let changed = false;
  const normalized = items.map((item) => {
    const fitted = fitPlaceholderToCanvas(item);
    if (!areBoundsEqual(item, fitted)) {
      changed = true;
      return fitted;
    }
    return item;
  });
  return changed ? normalized : items;
};

const parseDataUrlImage = (source) => {
  const value = String(source || "");
  const match = value.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,(.+)$/);
  if (!match) {
    return null;
  }
  const [, mime, rawBase64] = match;
  try {
    const binary = atob(rawBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return { mime: mime.toLowerCase(), bytes, base64: rawBase64 };
  } catch (error) {
    return null;
  }
};

const delay = (ms) =>
  new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });

const normalizeRecipients = (rawRecipients) => {
  const seen = new Set();
  return String(rawRecipients || "")
    .split(/[\s,;]+/)
    .map((value) => value.trim().toLowerCase())
    .filter((value) => Boolean(value))
    .filter((value) => {
      if (!EMAIL_REGEX.test(value)) {
        return false;
      }
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    })
    .slice(0, MAX_FORWARD_RECIPIENTS);
};

const normalizeEmailTemplateMode = (value) => {
  const normalized = String(value || "").toLowerCase();
  return normalized === EMAIL_TEMPLATE_MODE.PLAIN ? EMAIL_TEMPLATE_MODE.PLAIN : EMAIL_TEMPLATE_MODE.HTML;
};

const toPlainTextFromHtml = (value) =>
  String(value || "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

const extractTemplateContent = (value) => {
  const raw = String(value || "");
  const startIndex = raw.indexOf(EMAIL_TEMPLATE_CONTENT_START);
  const endIndex = raw.indexOf(EMAIL_TEMPLATE_CONTENT_END);
  if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
    return raw
      .slice(startIndex + EMAIL_TEMPLATE_CONTENT_START.length, endIndex)
      .trim();
  }
  return raw;
};

const ensureRequestSigningUrlPlaceholder = (body) => {
  const normalized = String(body || "").trim();
  if (!normalized) {
    return "{{signing_url}}";
  }
  if (normalized.includes("{{signing_url}}")) {
    return normalized;
  }
  return `${normalized}\n\n{{signing_url}}`;
};

const replaceTemplateVariables = (template, variables) => {
  let output = String(template || "");
  Object.keys(variables || {}).forEach((variableName) => {
    const regex = new RegExp(`{{${variableName}}}`, "g");
    output = output.replace(regex, String(variables[variableName] || ""));
  });
  return output;
};

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const resolveCloudResultStatus = (response) =>
  String(response?.status || response?.result?.status || "").toLowerCase();

const resolveTenantPersonalEmailPermissions = (tenant = {}) => {
  const hasAdminPermission = tenant?.AllowPersonalEmailTemplatesForAdmin !== undefined;
  const hasEditorPermission = tenant?.AllowPersonalEmailTemplatesForEditor !== undefined;
  const hasUserPermission = tenant?.AllowPersonalEmailTemplatesForUser !== undefined;
  const legacyPermission = Boolean(tenant?.AllowPersonalEmailTemplatesForEditorOrgAdmin);

  return {
    allowAdmin: hasAdminPermission
      ? Boolean(tenant?.AllowPersonalEmailTemplatesForAdmin)
      : legacyPermission,
    allowEditor: hasEditorPermission
      ? Boolean(tenant?.AllowPersonalEmailTemplatesForEditor)
      : legacyPermission,
    allowUser: hasUserPermission ? Boolean(tenant?.AllowPersonalEmailTemplatesForUser) : false
  };
};

const canUsePersonalRequestEmailTemplates = (user = {}, tenant = {}) => {
  const role = String(user?.UserRole || "").trim();
  const permissions = resolveTenantPersonalEmailPermissions(tenant);
  if (PERSONAL_REQUEST_EMAIL_ADMIN_ROLES.has(role)) {
    return permissions.allowAdmin;
  }
  if (PERSONAL_REQUEST_EMAIL_EDITOR_ROLES.has(role)) {
    return permissions.allowEditor;
  }
  if (PERSONAL_REQUEST_EMAIL_USER_ROLES.has(role)) {
    return permissions.allowUser;
  }
  return false;
};

const hasPersonalRequestEmailTemplate = (user = {}) =>
  Boolean(String(user?.PersonalRequestSubject || "").trim() && String(user?.PersonalRequestBody || "").trim());

const resolveRecipientColorTheme = (recipientOrColorClass) => {
  const colorClass =
    typeof recipientOrColorClass === "string"
      ? recipientOrColorClass
      : String(recipientOrColorClass?.colorClass || "");
  const normalized = String(colorClass || "").toLowerCase();
  const matchedTheme = RECIPIENT_COLOR_THEMES.find((theme) =>
    normalized.includes(`bg-${theme.key}-`)
  );
  if (matchedTheme) {
    return matchedTheme;
  }
  return {
    base: "bg-slate-100 text-slate-700 border-slate-300",
    menu: "bg-slate-100 text-slate-500 border-slate-300",
    widget: "bg-slate-200/40 text-slate-700 border-slate-300"
  };
};

const DUPLICATE_WIDGET_OPTIONS = [
  "all-pages",
  "all-pages-but-last",
  "all-pages-but-first",
  "next-to-current-widget"
];

const toRecipientLabel = (recipient) => {
  const name = String(recipient?.name || recipient?.Name || "").trim();
  const email = String(recipient?.email || recipient?.Email || "").trim().toLowerCase();
  if (name && email) {
    return `${name} (${email})`;
  }
  return name || email;
};

const escapeRegex = (value) => String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const extractEmailFromText = (value) => {
  const query = String(value || "").trim();
  if (!query) {
    return "";
  }
  if (EMAIL_REGEX.test(query)) {
    return query.toLowerCase();
  }
  const tokens = query
    .replace(/[<>()]/g, " ")
    .split(/[\s,;]+/)
    .map((token) => token.trim().replace(/^["']|["']$/g, ""))
    .filter(Boolean);
  const manualEmail = tokens.find((token) => EMAIL_REGEX.test(token));
  return manualEmail ? String(manualEmail).toLowerCase() : "";
};

const normalizeRecipientEntry = (recipient, index = 0) => {
  const email = String(recipient?.email || recipient?.Email || "").trim().toLowerCase();
  const name = String(recipient?.name || recipient?.Name || "").trim() || email || `Signer ${index + 1}`;
  const fallbackId = String(recipient?.id || recipient?.Id || "").trim();
  const objectId = String(recipient?.objectId || recipient?.signerObjId || "").trim();
  const normalizedNameKey = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const rawRequireBiometric = recipient?.requireBiometric;
  const rawSettings = recipient?.verificationSettings || {};
  
  // Derive requireBiometric: if explicitly set use that, otherwise infer from verificationSettings.enabled
  const requireBiometric = typeof rawRequireBiometric === "boolean"
    ? rawRequireBiometric
    : Boolean(rawSettings.enabled);
  
  // Only force enabled:false when requireBiometric was explicitly set to false
  const verificationSettings = typeof rawRequireBiometric === "boolean" && !rawRequireBiometric
    ? { ...rawSettings, enabled: false }
    : rawSettings;
  
  return {
    id: fallbackId || objectId || email || (normalizedNameKey ? `role-${normalizedNameKey}` : "") || `recipient-${index + 1}`,
    name,
    email,
    objectId,
    colorClass: recipient?.colorClass || RECIPIENT_COLORS[index % RECIPIENT_COLORS.length],
    requireBiometric,
    verificationSettings
  };
};

const normalizeRecipientList = (rawRecipients = []) => {
  if (!Array.isArray(rawRecipients)) {
    return [];
  }
  const seen = new Set();
  return rawRecipients
    .map((recipient, index) => normalizeRecipientEntry(recipient, index))
    .filter((recipient) => {
      const key =
        recipient.objectId ||
        recipient.email ||
        recipient.id ||
        String(recipient?.name || "")
          .trim()
          .toLowerCase();
      if (!key) {
        return false;
      }
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

const normalizeCloudResult = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload?.result)) {
    return payload.result;
  }
  return [];
};

const normalizeContactbookRows = (rows = []) => {
  const seenEmails = new Set();
  return (Array.isArray(rows) ? rows : [])
    .map((row) => ({
      objectId: String(row?.objectId || row?.id || "").trim(),
      Name: String(row?.Name || row?.name || "").trim(),
      Email: String(row?.Email || row?.email || "")
        .trim()
        .toLowerCase(),
      Phone: String(row?.Phone || row?.phone || "").trim()
    }))
    .filter((row) => row.Email)
    .filter((row) => {
      if (seenEmails.has(row.Email)) {
        return false;
      }
      seenEmails.add(row.Email);
      return true;
    });
};

const drawTextInWidget = ({
  page,
  font,
  text,
  x,
  y,
  width,
  height,
  fontSize,
  color,
  underline = false
}) => {
  const value = String(text || "").trim();
  if (!value) {
    return;
  }
  const lineHeight = Math.max(11, fontSize + 2);
  const paddingX = 2;
  const paddingTop = 2;
  const maxWidth = Math.max(10, width - paddingX * 2);

  const splitLineToWidth = (line) => {
    const words = String(line || "").split(/\s+/).filter(Boolean);
    if (!words.length) {
      return [""];
    }
    const lines = [];
    let current = words[0];
    for (let i = 1; i < words.length; i += 1) {
      const next = `${current} ${words[i]}`;
      if (font.widthOfTextAtSize(next, fontSize) <= maxWidth) {
        current = next;
      } else {
        lines.push(current);
        current = words[i];
      }
    }
    lines.push(current);
    return lines;
  };

  const lines = value
    .split("\n")
    .flatMap((line) => splitLineToWidth(line))
    .filter((line) => line !== "");

  if (!lines.length) {
    return;
  }

  let currentY = y + height - fontSize - paddingTop;
  const minY = y + 1;
  for (let i = 0; i < lines.length; i += 1) {
    if (currentY < minY) {
      break;
    }
    page.drawText(lines[i], {
      x: x + paddingX,
      y: currentY,
      size: fontSize,
      font,
      color
    });
    if (underline) {
      const underlineY = Math.max(minY, currentY - Math.max(1, fontSize * 0.12));
      const textWidth = Math.min(maxWidth, font.widthOfTextAtSize(lines[i], fontSize));
      page.drawLine({
        start: { x: x + paddingX, y: underlineY },
        end: { x: x + paddingX + textWidth, y: underlineY },
        thickness: Math.max(0.7, fontSize * 0.07),
        color
      });
    }
    currentY -= lineHeight;
  }
};

const getToolLabel = (toolType, t) => {
  const definition = TOOL_DEFINITIONS.find((item) => item.type === toolType);
  return t(`new-ui.individual-signing.tool-${toolType}`, {
    defaultValue: definition?.label || toolType
  });
};

function HelpHint({ text = "" }) {
  const [isOpen, setIsOpen] = useState(false);

  if (!text) {
    return null;
  }

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <button
        type="button"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:text-slate-600 transition-colors"
        aria-label={text}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 20 20" 
          fill="currentColor" 
          className="w-4 h-4"
        >
          <path 
            fillRule="evenodd" 
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z" 
            clipRule="evenodd" 
          />
        </svg>
      </button>
      {isOpen ? (
        <span className="pointer-events-none absolute right-0 top-[calc(100%+8px)] z-[9999] w-[320px] rounded-lg border border-slate-200 bg-white p-3 text-left text-xs leading-relaxed text-slate-700 shadow-lg">
          {text}
        </span>
      ) : null}
    </span>
  );
}

const buildInitials = (name) => {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) {
    return "";
  }
  return parts
    .slice(0, 3)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
};

const getDefaultPlaceholderText = (toolType, extUser, t) => {
  const fallbackName = extUser?.Name || localStorage.getItem("username") || "";
  const fallbackEmail = extUser?.Email || localStorage.getItem("userEmail") || "";
  const fallbackCompany = extUser?.TenantId?.TenantName || localStorage.getItem("TenantName") || "";
  const fallbackJobTitle = extUser?.JobTitle || extUser?.Designation || "";

  switch (toolType) {
    case "date":
      return new Date().toISOString().slice(0, 10);
    case "text":
      return t("new-ui.individual-signing.default-text", { defaultValue: "Text" });
    case "name":
      return fallbackName;
    case "job title":
      return fallbackJobTitle;
    case "company":
      return fallbackCompany;
    case "email":
      return fallbackEmail;
    case "checkbox":
      return t("new-ui.individual-signing.checkbox-label-default", { defaultValue: "I agree" });
    case "initials":
      return buildInitials(fallbackName);
    case "stamp":
      return t("new-ui.individual-signing.default-stamp", { defaultValue: "Stamp" });
    default:
      return "";
  }
};

const getRecipientAutofillValue = (toolType, recipient) => {
  const recipientName = String(recipient?.name || recipient?.Name || "").trim();
  const recipientEmail = normalizeEmailAddress(recipient?.email || recipient?.Email);
  switch (toolType) {
    case "name":
      return recipientName;
    case "initials":
      return "";
    case "email":
      return recipientEmail;
    default:
      return "";
  }
};

const normalizeRoleKey = (value) => String(value || "").trim().toLowerCase();

const isPrefillTextPlaceholder = (item = {}, pageEntry = {}) => {
  const widgetType = String(item?.type || "").trim().toLowerCase();
  const role = String(item?.Role || item?.role || pageEntry?.Role || pageEntry?.role || "")
    .trim()
    .toLowerCase();
  return (
    widgetType === "text" &&
    (item?.isPrefill === true || item?.options?.isPrefill === true || role === "prefill")
  );
};

// Widget types whose response is an image (signature, stamp, etc.)
// and should always be cleared when loading templates.
const imageWidgetTypesForCleanup = ['signature', 'initials', 'stamp', 'image'];

const flattenExistingPlaceholders = (grouped = []) => {
  if (!Array.isArray(grouped)) return [];
  const flat = [];
  grouped.forEach((entry) => {
    const groupRole = String(entry?.Role || entry?.role || "").trim();
    const groupSignerObjId = String(entry?.signerObjId || entry?.signerPtr?.objectId || "").trim();
    const groupEmail = String(entry?.email || "").trim().toLowerCase();
    const pageEntries = Array.isArray(entry?.placeHolder) ? entry.placeHolder : [entry];
    pageEntries.forEach((pageEntry) => {
      const pageRole = String(pageEntry?.Role || pageEntry?.role || groupRole || "").trim();
      const pageSignerObjId = String(
        pageEntry?.signerObjId || pageEntry?.signerPtr?.objectId || groupSignerObjId || ""
      ).trim();
      const pageEmail = String(pageEntry?.email || groupEmail || "").trim().toLowerCase();
      const pageNumber = Number.parseInt(pageEntry?.pageNumber, 10) || 1;
      const positions = Array.isArray(pageEntry?.pos) ? pageEntry.pos : [];
      positions.forEach((pos, index) => {
      const type = String(pos?.type || "signature").toLowerCase();
      const isPrefill = isPrefillTextPlaceholder(pos, entry);
      
      // Clean signature/image responses from templates to prevent pre-filled signatures
      const cleanedPos = { ...pos };
      if (imageWidgetTypesForCleanup.includes(type)) {
        cleanedPos.SignUrl = undefined;
        if (cleanedPos.options) {
          cleanedPos.options = { ...cleanedPos.options };
          delete cleanedPos.options.response;
          delete cleanedPos.options.SignUrl;
          delete cleanedPos.options.defaultValue;
        }
      }
      
      const styleCandidate = {
        ...cleanedPos,
        Role: pageRole || groupRole,
        options: {
          ...(cleanedPos?.options || {}),
          textStyle: cleanedPos?.options?.textStyle || cleanedPos?.textStyle || pageEntry?.textStyle || entry?.textStyle
        }
      };
      const textStyle =
        TEXT_STYLE_WIDGET_TYPES.has(type) && hasExplicitWidgetTextStyle(styleCandidate)
          ? resolveWidgetTextStyle(styleCandidate)
          : undefined;
      const sizeDefaults = DEFAULT_WIDGET_SIZE[type] || DEFAULT_WIDGET_SIZE.signature;
      const optionResponse = cleanedPos?.options?.response;
      const responseAsString = typeof optionResponse === "string" ? optionResponse : "";
      const checkboxLabelFromOptions =
        typeof cleanedPos?.options?.label === "string"
          ? cleanedPos.options.label
          : typeof cleanedPos?.options?.defaultValue === "string"
            ? cleanedPos.options.defaultValue
            : "";
      const signUrl =
        cleanedPos?.SignUrl ||
        (IMAGE_VALUE_TYPES.has(type) &&
        (responseAsString.startsWith("data:image") || /^https?:\/\//i.test(responseAsString))
          ? responseAsString
          : "");
      const existingIdentityVerification =
        pos?.IdentityVerification &&
        typeof pos.IdentityVerification === "object" &&
        !Array.isArray(pos.IdentityVerification)
          ? pos.IdentityVerification
          : null;
      const existingRequiresIdentityVerification =
        typeof pos?.RequiresIdentityVerification === "boolean"
          ? pos.RequiresIdentityVerification
          : existingIdentityVerification
            ? true
            : undefined;
      flat.push(fitPlaceholderToCanvas({
        id: pos?.key || `${pageNumber}-${index}-${Date.now()}`,
        pageNumber,
        type,
        x: normalizeUnitValue(pos?.xPosition, 0.3),
        y: normalizeUnitValue(pos?.yPosition, 0.3),
        width: normalizeUnitValue(pos?.Width, sizeDefaults.width),
        height: normalizeUnitValue(pos?.Height, sizeDefaults.height),
        signUrl,
        textValue:
          type === "checkbox"
            ? checkboxLabelFromOptions ||
              (typeof optionResponse === "string" &&
              !optionResponse.startsWith("data:image") &&
              !looksLikeBooleanCheckboxValue(optionResponse)
                ? optionResponse
                : "")
            : typeof optionResponse === "string"
              ? optionResponse.startsWith("data:image")
                ? ""
                : optionResponse
              : Array.isArray(optionResponse)
                ? optionResponse.join(", ")
                : "",
        checked: type === "checkbox" ? parseCheckboxResponse(optionResponse) : false,
        required: pos?.options?.status === "required",
        recipientId: String(
          isPrefill
            ? ""
            : pos?.options?.recipientId ||
              pos?.recipientId ||
              pos?.signerObjId ||
              pos?.signerPtr?.objectId ||
              pageSignerObjId ||
              ""
        ).trim(),
        recipientEmail: String(
          isPrefill ? "" : pos?.options?.recipientEmail || pos?.email || pageEmail || ""
        ).trim().toLowerCase(),
        recipientName: String(
          isPrefill
            ? ""
            : pos?.options?.recipientName ||
              pos?.Role ||
              pos?.role ||
              pageRole ||
              groupRole ||
              ""
        ).trim(),
        identityVerification: existingIdentityVerification,
        requiresIdentityVerification: existingRequiresIdentityVerification,
        ...(pos?.options?.name ? { apiName: pos.options.name } : {}),
        ...(type === "text" && pos?.placeholderText ? { placeholderText: pos.placeholderText } : {}),
        ...(textStyle ? { textStyle } : {}),
        isPrefill
      }));
    });
    });
  });
  return flat;
};

const hydratePlaceholderRecipientsByRole = (placeholders = [], recipients = []) => {
  if (!Array.isArray(placeholders) || !placeholders.length) {
    return [];
  }
  if (!Array.isArray(recipients) || !recipients.length) {
    return placeholders;
  }

  const recipientByRoleKey = new Map();
  const knownRecipientIds = new Set();
  recipients.forEach((recipient) => {
    const roleKey = normalizeRoleKey(recipient?.name || recipient?.Name || recipient?.role || recipient?.Role);
    if (roleKey && !recipientByRoleKey.has(roleKey)) {
      recipientByRoleKey.set(roleKey, recipient);
    }
    const rid = String(recipient?.id || "").trim();
    if (rid) {
      knownRecipientIds.add(rid);
    }
    const oid = String(recipient?.objectId || "").trim();
    if (oid) {
      knownRecipientIds.add(oid);
    }
  });

  return placeholders.map((item) => {
    if (isPrefillTextPlaceholder(item)) {
      return item;
    }
    const existingId = String(item?.recipientId || "").trim();
    // If the widget already has a recipientId that matches a current
    // recipient, keep it as-is.  Otherwise fall through to role-based
    // matching so that stale IDs inherited from a template are replaced.
    if (existingId && knownRecipientIds.has(existingId)) {
      return item;
    }
    const roleKey = normalizeRoleKey(item?.recipientName || item?.Role || item?.role);
    if (!roleKey) {
      return item;
    }
    const matchedRecipient = recipientByRoleKey.get(roleKey);
    if (!matchedRecipient?.id) {
      return item;
    }
    return {
      ...item,
      recipientId: String(matchedRecipient.id).trim(),
      recipientEmail: normalizeEmailAddress(matchedRecipient.email || matchedRecipient.Email),
      recipientName:
        String(matchedRecipient.name || matchedRecipient.Name || "").trim() ||
        String(item?.recipientName || "").trim()
    };
  });
};

const buildGroupedPlaceholdersPayload = (placeholders, recipients = []) => {
  const byPage = new Map();
  placeholders.forEach((item) => {
    const boundedItem = fitPlaceholderToCanvas(item);
    const normalizedType = String(boundedItem?.type || "").trim().toLowerCase();
    const textStyle = TEXT_STYLE_WIDGET_TYPES.has(normalizedType) && hasExplicitWidgetTextStyle(boundedItem)
      ? sanitizeTextStyle(boundedItem?.textStyle)
      : null;
    const pageNumber = Number.parseInt(boundedItem?.pageNumber, 10) || 1;
    const isPrefill = isPrefillTextPlaceholder(boundedItem);
    if (!byPage.has(pageNumber)) {
      byPage.set(pageNumber, []);
    }
    
    // Find the recipient for this placeholder to get IdentityVerification settings
    const recipient = isPrefill
      ? null
      : recipients.find((r) => r.id === boundedItem.recipientId || r.objectId === boundedItem.recipientId);
    const recipientIdentityVerification =
      !isPrefill && recipient?.requireBiometric === true
        ? {
            enabled: true,
            threshold: recipient?.verificationSettings?.threshold || 85,
            requireDocumentBack: resolveRequireDocumentBack(
              recipient?.verificationSettings?.requireDocumentBack,
              true
            ),
            notes: recipient?.verificationSettings?.notes || ""
          }
        : null;
    const existingIdentityVerification =
      !isPrefill &&
      boundedItem?.identityVerification &&
      typeof boundedItem.identityVerification === "object" &&
      !Array.isArray(boundedItem.identityVerification)
        ? {
            enabled: boundedItem.identityVerification.enabled !== false,
            threshold: Number.parseInt(boundedItem.identityVerification.threshold, 10) || 85,
            requireDocumentBack: resolveRequireDocumentBack(
              boundedItem.identityVerification.requireDocumentBack,
              true
            ),
            notes: String(boundedItem.identityVerification.notes || "").trim()
          }
        : null;
    const identityVerification = recipientIdentityVerification
      || (!isPrefill && recipient?.requireBiometric === false ? null : existingIdentityVerification)
      || null;
    const requiresIdentityVerification =
      !isPrefill && typeof recipient?.requireBiometric === "boolean"
        ? recipient.requireBiometric
        : !isPrefill && typeof boundedItem?.requiresIdentityVerification === "boolean"
          ? boundedItem.requiresIdentityVerification
          : identityVerification
            ? true
            : undefined;
    
    byPage.get(pageNumber).push({
      key: boundedItem.id,
      type: boundedItem.type,
      ...(isPrefill ? { Role: "prefill", isPrefill: true } : {}),
      xPosition: boundedItem.x,
      yPosition: boundedItem.y,
      Width: boundedItem.width,
      Height: boundedItem.height,
      scale: 1,
      ...(boundedItem.signUrl ? { SignUrl: boundedItem.signUrl } : {}),
      ...(boundedItem.type === "text" && boundedItem.placeholderText
        ? { placeholderText: boundedItem.placeholderText }
        : {}),
      ...(identityVerification ? { IdentityVerification: identityVerification } : {}),
      ...(typeof requiresIdentityVerification === "boolean"
        ? { RequiresIdentityVerification: requiresIdentityVerification }
        : {}),
      ...(textStyle ? { textStyle } : {}),
      options: {
        status: boundedItem.required ? "required" : "optional",
        ...(boundedItem.apiName ? { name: boundedItem.apiName } : {}),
        response: IMAGE_VALUE_TYPES.has(boundedItem.type)
          ? boundedItem.signUrl || ""
          : boundedItem.type === "checkbox"
            ? Boolean(boundedItem.checked)
            : boundedItem.textValue || "",
        ...(textStyle ? { textStyle } : {}),
        ...(isPrefill ? { isPrefill: true } : {}),
        ...(!isPrefill && boundedItem.recipientId
          ? {
              recipientId: boundedItem.recipientId,
              recipientEmail: boundedItem.recipientEmail || "",
              recipientName: boundedItem.recipientName || "",
              ...(recipient?.objectId ? { contactBookId: recipient.objectId } : {})
            }
          : {}),
        ...(boundedItem.type === "checkbox"
          ? {
              label: boundedItem.textValue || "",
              defaultValue: boundedItem.textValue || ""
            }
          : {})
      }
    });
  });

  return Array.from(byPage.entries()).map(([pageNumber, pos]) => ({ pageNumber, pos }));
};

const buildTypedSignatureDataUrl = (name) => {
  const value = String(name || "").trim();
  if (!value) return "";
  const canvas = document.createElement("canvas");
  canvas.width = 720;
  canvas.height = 220;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#0f172a";
  ctx.font = "700 76px Roboto, sans-serif";
  ctx.textBaseline = "middle";
  ctx.fillText(value, 30, canvas.height / 2);
  return canvas.toDataURL("image/png");
};

const fetchPdfArrayBuffer = async (url) => {
  if (!url) {
    return null;
  }
  const attempts = [() => fetch(url, { method: "GET" })];

  for (const runAttempt of attempts) {
    try {
      const response = await runAttempt();
      if (!response.ok) {
        continue;
      }
      return await response.arrayBuffer();
    } catch (fetchError) {
      // Avoid noisy loops in the console; load error handling happens in the caller.
    }
  }
  return null;
};

const loadPdfBinaryWithFallback = async ({ primaryUrl = "", fallbackUrl = "", docId = "" }) => {
  const candidates = [primaryUrl, fallbackUrl].filter(Boolean);
  const attemptedUrls = new Set();
  const tryFetchOnce = async (url) => {
    if (!url || attemptedUrls.has(url)) {
      return null;
    }
    attemptedUrls.add(url);
    return fetchPdfArrayBuffer(url);
  };

  for (const candidate of candidates) {
    const candidateBuffer = await tryFetchOnce(candidate);
    if (candidateBuffer) {
      return { buffer: candidateBuffer, url: candidate };
    }

    const refreshedSignedUrl = await resolveDocumentUrl(candidate, docId);
    if (refreshedSignedUrl && refreshedSignedUrl !== candidate) {
      const refreshedBuffer = await tryFetchOnce(refreshedSignedUrl);
      if (refreshedBuffer) {
        return { buffer: refreshedBuffer, url: refreshedSignedUrl };
      }
    }

    const cloudSignedUrl = await requestFreshSignedUrl(candidate, docId);
    if (cloudSignedUrl && cloudSignedUrl !== candidate && cloudSignedUrl !== refreshedSignedUrl) {
      const cloudSignedBuffer = await tryFetchOnce(cloudSignedUrl);
      if (cloudSignedBuffer) {
        return { buffer: cloudSignedBuffer, url: cloudSignedUrl };
      }
    }
  }
  return { buffer: null, url: primaryUrl || fallbackUrl || "" };
};

function IndividualSigningPage({ editorMode = "self", backTo = "/form/sHAnZphf69" }) {
  const { t } = useTranslation();
  const { docId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showError, showSuccess } = useAlert();
  const viewerRef = useRef(null);
  const pageLayerRef = useRef(null);
  const signaturePadRef = useRef(null);
  const imageInputRef = useRef(null);
  const imagePickerTargetIdRef = useRef("");
  const addPagesInputRef = useRef(null);
  const signatureUploadInputRef = useRef(null);
  const isPdfMutationInFlightRef = useRef(false);
  const pdfBytesRef = useRef(null);
  const isPdfModifiedRef = useRef(false);
  const uploadedModifiedPdfUrlRef = useRef(null);
  const pageRotationsRef = useRef({});
  const currentPageRef = useRef(1);
  const resizeStateRef = useRef(null);

  const [extUser, setExtUser] = useState(() => {
    const extClass = parseStoredJson("Extand_Class");
    return Array.isArray(extClass) ? extClass?.[0] || {} : {};
  });
  const canManageTemplates = useMemo(
    () => String(extUser?.UserRole || "").trim() !== "contracts_User",
    [extUser]
  );
  const [allowPersonalRequestEmailForAdminByTenant, setAllowPersonalRequestEmailForAdminByTenant] = useState(() => {
    const permissions = resolveTenantPersonalEmailPermissions(extUser?.TenantId || {});
    return permissions.allowAdmin;
  });
  const [allowPersonalRequestEmailForEditorByTenant, setAllowPersonalRequestEmailForEditorByTenant] = useState(() => {
    const permissions = resolveTenantPersonalEmailPermissions(extUser?.TenantId || {});
    return permissions.allowEditor;
  });
  const [allowPersonalRequestEmailForUserByTenant, setAllowPersonalRequestEmailForUserByTenant] = useState(() => {
    const permissions = resolveTenantPersonalEmailPermissions(extUser?.TenantId || {});
    return permissions.allowUser;
  });
  const userCanUsePersonalRequestEmail = useMemo(
    () =>
      canUsePersonalRequestEmailTemplates(extUser, {
        ...(extUser?.TenantId || {}),
        AllowPersonalEmailTemplatesForAdmin: allowPersonalRequestEmailForAdminByTenant,
        AllowPersonalEmailTemplatesForEditor: allowPersonalRequestEmailForEditorByTenant,
        AllowPersonalEmailTemplatesForUser: allowPersonalRequestEmailForUserByTenant,
        AllowPersonalEmailTemplatesForEditorOrgAdmin:
          allowPersonalRequestEmailForAdminByTenant || allowPersonalRequestEmailForEditorByTenant
      }),
    [
      extUser,
      allowPersonalRequestEmailForAdminByTenant,
      allowPersonalRequestEmailForEditorByTenant,
      allowPersonalRequestEmailForUserByTenant
    ]
  );
  const userHasPersonalRequestEmailTemplate = useMemo(
    () => hasPersonalRequestEmailTemplate(extUser),
    [extUser]
  );
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const templateEditQueryFlag = String(searchParams.get("templateEdit") || "").trim().toLowerCase();
  const templateEditRequested =
    templateEditQueryFlag === "1" ||
    templateEditQueryFlag === "true";
  const [signatureValidation, setSignatureValidation] = useState(() => extractSignatureThresholds(extUser));

  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [documentName, setDocumentName] = useState("");
  const [numPages, setNumPages] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [zoomScale, setZoomScale] = useState(DEFAULT_INITIAL_ZOOM);
  const [pageWidth, setPageWidth] = useState(980);
  const [placeholders, setPlaceholders] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [isLoadingDoc, setIsLoadingDoc] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isMutatingPdf, setIsMutatingPdf] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [signatureMode, setSignatureMode] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");

  // Fix canvas offset: sync internal canvas dimensions with CSS layout
  useEffect(() => {
    if (signatureMode !== "draw" || !signaturePadRef.current) return;
    const syncCanvasSize = () => {
      const canvas = signaturePadRef.current?.getCanvas();
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (rect.width > 0 && canvas.width !== Math.floor(rect.width)) {
        canvas.width = Math.floor(rect.width);
        canvas.height = Math.floor(rect.height || 420);
        signaturePadRef.current.clear();
      }
    };
    const timer = setTimeout(syncCanvasSize, 100);
    window.addEventListener("resize", syncCanvasSize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", syncCanvasSize);
    };
  }, [signatureMode, isSignatureModalOpen]);
  const [uploadedSignatureDataUrl, setUploadedSignatureDataUrl] = useState("");
  const [profileSignatureUrl, setProfileSignatureUrl] = useState("");
  const [profileSignatureRecordId, setProfileSignatureRecordId] = useState("");
  const [saveSignatureToProfile, setSaveSignatureToProfile] = useState(false);
  const [isSavingSignatureChoice, setIsSavingSignatureChoice] = useState(false);
  const [draggingId, setDraggingId] = useState("");
  const [draggingToolType, setDraggingToolType] = useState("");
  const [draggingToolIsPrefill, setDraggingToolIsPrefill] = useState(false);
  const [resizingId, setResizingId] = useState("");
  const [rawDocumentUrl, setRawDocumentUrl] = useState("");
  const [hasTriedPdfRecovery, setHasTriedPdfRecovery] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [isPostFinalizeModalOpen, setIsPostFinalizeModalOpen] = useState(false);
  const [isSendingFinalizeEmail, setIsSendingFinalizeEmail] = useState(false);
  const [isRequestFinalizeModalOpen, setIsRequestFinalizeModalOpen] = useState(false);
  const [isSendingRequestEmail, setIsSendingRequestEmail] = useState(false);
  const [requestSendError, setRequestSendError] = useState("");
  const [requestEmailSource, setRequestEmailSource] = useState(REQUEST_EMAIL_SOURCE.CORPORATE);
  const [shouldSendCompletionEmail, setShouldSendCompletionEmail] = useState(true);
  const [completionRecipientsInput, setCompletionRecipientsInput] = useState("");
  const [completionEmailError, setCompletionEmailError] = useState("");
  const [finalizedDoc, setFinalizedDoc] = useState(null);
  const [isDeletePageModalOpen, setIsDeletePageModalOpen] = useState(false);
  const [deletePageInput, setDeletePageInput] = useState("");
  const [deletePageMax, setDeletePageMax] = useState(1);
  const [deletePageModalError, setDeletePageModalError] = useState("");
  const [requestRecipients, setRequestRecipients] = useState([]);
  const [activeRecipientId, setActiveRecipientId] = useState("");
  const [recipientQueryInput, setRecipientQueryInput] = useState("");
  const [roleNameInput, setRoleNameInput] = useState("");
  const [isRecipientDropdownOpen, setIsRecipientDropdownOpen] = useState(false);
  const [recipientError, setRecipientError] = useState("");
  const [contactbookRows, setContactbookRows] = useState([]);
  const [isLoadingContactbook, setIsLoadingContactbook] = useState(false);
  const [isAddContactModalOpen, setIsAddContactModalOpen] = useState(false);
  const [isSavingRecipientContact, setIsSavingRecipientContact] = useState(false);
  const [recipientContactForm, setRecipientContactForm] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [recipientContactError, setRecipientContactError] = useState("");
  const [recipientContactFieldErrors, setRecipientContactFieldErrors] = useState({
    name: "",
    email: ""
  });
  const [isWidgetEditorModalOpen, setIsWidgetEditorModalOpen] = useState(false);
  const [isDuplicateWidgetModalOpen, setIsDuplicateWidgetModalOpen] = useState(false);
  const [duplicateWidgetSourceId, setDuplicateWidgetSourceId] = useState("");
  const [isMobileToolboxOpen, setIsMobileToolboxOpen] = useState(false);
  const [templateEditTemplateId, setTemplateEditTemplateId] = useState(() =>
    String(location?.state?.templateId || searchParams.get("templateId") || "").trim()
  );

  // Biometric verification state
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [verificationComplete, setVerificationComplete] = useState(false);
  const [verificationEvidence, setVerificationEvidence] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationConfig, setVerificationConfig] = useState({ threshold: 85, requireDocument: true });
  const recipientDropdownRef = useRef(null);
  const mobileToolboxRef = useRef(null);

  // Biometric settings modal state
  const [isBiometricSettingsOpen, setIsBiometricSettingsOpen] = useState(false);
  const [selectedRecipientForBiometric, setSelectedRecipientForBiometric] = useState(null);
  const [isDeleteRecipientModalOpen, setIsDeleteRecipientModalOpen] = useState(false);
  const [recipientPendingDelete, setRecipientPendingDelete] = useState(null);

  const isRequestMode = editorMode === "request";
  const isTemplateEditMode = Boolean(isRequestMode && templateEditRequested);
  const stateBackTo = String(
    location?.state?.backTo || location?.state?.returnTo || location?.state?.fromPath || ""
  ).trim();
  const defaultBackTo = isTemplateEditMode
    ? "/report/6TeaPr321t"
    : isRequestMode
      ? "/form/8mZzFxbG1z"
      : "/form/sHAnZphf69";
  const resolvedBackTo =
    stateBackTo.startsWith("/") ? stateBackTo : String(backTo || "").trim().startsWith("/") ? backTo : defaultBackTo;

  useEffect(() => {
    let cancelled = false;
    const loadTenantPersonalEmailPermission = async () => {
      try {
        const userId = Parse.User.current()?.id;
        if (!userId) {
          return;
        }
        const tenantResponse = await Parse.Cloud.run("gettenant", { userId });
        const tenant = typeof tenantResponse?.toJSON === "function" ? tenantResponse.toJSON() : tenantResponse;
        if (cancelled) {
          return;
        }
        const resolvedPermissions = resolveTenantPersonalEmailPermissions(tenant || {});
        setAllowPersonalRequestEmailForAdminByTenant(resolvedPermissions.allowAdmin);
        setAllowPersonalRequestEmailForEditorByTenant(resolvedPermissions.allowEditor);
        setAllowPersonalRequestEmailForUserByTenant(resolvedPermissions.allowUser);
      } catch (tenantPermissionError) {
        console.error("Failed to load tenant personal email permission", tenantPermissionError);
      }
    };

    loadTenantPersonalEmailPermission();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedPlaceholder = useMemo(
    () => placeholders.find((item) => item.id === selectedId) || null,
    [placeholders, selectedId]
  );

  const duplicateWidgetSource = useMemo(
    () => placeholders.find((item) => item.id === duplicateWidgetSourceId) || null,
    [duplicateWidgetSourceId, placeholders]
  );

  const currentPagePlaceholders = useMemo(
    () => placeholders.filter((item) => item.pageNumber === currentPageNumber),
    [placeholders, currentPageNumber]
  );

  const recipientFieldCount = useMemo(() => {
    const counts = {};
    placeholders.forEach((item) => {
      const recipientId = String(item?.recipientId || "").trim();
      if (!recipientId) {
        return;
      }
      counts[recipientId] = (counts[recipientId] || 0) + 1;
    });
    return counts;
  }, [placeholders]);

  const recipientById = useMemo(() => {
    const map = new Map();
    requestRecipients.forEach((recipient) => {
      if (recipient?.id) {
        map.set(recipient.id, recipient);
      }
    });
    return map;
  }, [requestRecipients]);

  const currentUserEmail = useMemo(
    () =>
      normalizeEmailAddress(
        extUser?.Email || Parse.User.current()?.get?.("email") || localStorage.getItem("userEmail")
      ),
    [extUser?.Email]
  );

  const isCurrentUserRecipient = (recipient) => {
    if (!currentUserEmail) {
      return false;
    }
    const recipientEmail = normalizeEmailAddress(recipient?.email || recipient?.Email);
    return Boolean(recipientEmail && recipientEmail === currentUserEmail);
  };

  const canEditSignaturePlaceholder = (placeholder) => {
    if (!placeholder || placeholder.type !== "signature") {
      return false;
    }
    if (!isRequestMode) {
      return true;
    }
    const recipientId = String(placeholder?.recipientId || "").trim();
    if (!recipientId) {
      return false;
    }
    const recipient = recipientById.get(recipientId);
    return isCurrentUserRecipient(recipient);
  };

  const filteredContactbook = useMemo(() => {
    const query = String(recipientQueryInput || "").trim().toLowerCase();
    const availableContacts = contactbookRows.filter(
      (contact) => !requestRecipients.some((recipient) => recipient.email === contact.Email)
    );
    if (!query) {
      return [];
    }
    return availableContacts.filter((contact) =>
      `${contact.Name} ${contact.Email} ${contact.Phone}`.toLowerCase().includes(query)
    ).slice(0, 8);
  }, [recipientQueryInput, contactbookRows, requestRecipients]);

  const getRecipientToneClass = (recipient) => {
    return resolveRecipientColorTheme(recipient).widget;
  };

  const getRecipientMenuToneClass = (recipient) => {
    return resolveRecipientColorTheme(recipient).menu;
  };

  const getPlaceholderToneClass = (placeholder) => {
    if (!isRequestMode) {
      return "border-indigo-400 bg-indigo-100/50 text-indigo-800";
    }
    if (isPrefillTextPlaceholder(placeholder)) {
      return "border-slate-300 bg-slate-100/70 text-slate-700";
    }
    const recipientId = String(placeholder?.recipientId || "").trim();
    if (!recipientId) {
      return "border-amber-300 bg-amber-100/45 text-amber-700";
    }
    const recipient = recipientById.get(recipientId);
    return getRecipientToneClass(recipient);
  };

  const documentFileSource = useMemo(() => {
    if (pdfFile) {
      return pdfFile;
    }
    return pdfUrl || null;
  }, [pdfFile, pdfUrl]);

  const normalizePdfBytes = (sourceBytes) => {
    if (!sourceBytes) {
      return null;
    }
    if (sourceBytes instanceof Uint8Array) {
      return sourceBytes;
    }
    if (sourceBytes instanceof ArrayBuffer) {
      return new Uint8Array(sourceBytes);
    }
    if (ArrayBuffer.isView(sourceBytes)) {
      return new Uint8Array(sourceBytes.buffer);
    }
    return null;
  };

  const setCurrentPage = (nextPage) => {
    const normalized = Math.max(1, Number(nextPage) || 1);
    currentPageRef.current = normalized;
    setCurrentPageNumber(normalized);
  };

  const updateCurrentPage = (updater) => {
    setCurrentPageNumber((current) => {
      const resolved = typeof updater === "function" ? updater(current) : updater;
      const normalized = Math.max(1, Number(resolved) || 1);
      currentPageRef.current = normalized;
      return normalized;
    });
  };

  const editorLocked = isFinalized || isSaving || isSendingFinalizeEmail || isSendingRequestEmail;

  const ensureSignatureImageMeetsThresholds = async (sourceUrl) => {
    const source = String(sourceUrl || "").trim();
    if (!source) {
      return false;
    }
    try {
      const measurableSource = source.startsWith("data:image") ? source : await resolveDocumentUrl(source, docId);
      const naturalWH = await getImageSize(measurableSource);
      return validateImageSignature(
        naturalWH,
        signatureValidation.minArea,
        signatureValidation.minLength
      );
    } catch (error) {
      throw new Error(
        t("new-ui.individual-signing.image-read-error", {
          defaultValue: "Could not read the image file."
        })
      );
    }
  };

  const uploadSignatureAsset = async (source, signatureName = "signature") => {
    const value = String(source || "").trim();
    if (!value) {
      return "";
    }
    if (/^https?:\/\//i.test(value)) {
      return value;
    }
    if (!value.startsWith("data:image/")) {
      throw new Error(
        t("new-ui.individual-signing.image-type-error", {
          defaultValue: "Please upload a valid image file."
        })
      );
    }

    const response = await fetch(value);
    const blob = await response.blob();
    const extension = String(blob.type || "image/png").split("/")[1] || "png";
    const safeName =
      String(signatureName || "signature")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_-]/g, "") || "signature";

    const parseFile = new Parse.File(`${safeName}.${extension}`, blob);
    const savedFile = await parseFile.save();
    const rawUrl = savedFile?.url?.() || "";
    if (!rawUrl) {
      throw new Error(t("something-went-wrong-mssg", { defaultValue: "Something went wrong." }));
    }
    if (rawUrl.includes("/files/")) {
      try {
        const secured = await Parse.Cloud.run("fileupload", { url: rawUrl });
        if (secured?.url) {
          return secured.url;
        }
      } catch (secureError) {
        console.error("Failed to secure uploaded signature URL", secureError);
      }
    }
    return rawUrl;
  };

  const saveSignatureInProfile = async (sourceSignUrl) => {
    const currentUser = Parse.User.current();
    if (!currentUser?.id) {
      return sourceSignUrl;
    }

    const normalizedName =
      String(currentUser.get("name") || localStorage.getItem("username") || "Signature").trim() || "Signature";
    const finalSignatureUrl = await uploadSignatureAsset(sourceSignUrl, normalizedName);
    const userPointer = {
      __type: "Pointer",
      className: "_User",
      objectId: currentUser.id
    };

    const signQuery = new Parse.Query("contracts_Signature");
    signQuery.equalTo("UserId", userPointer);
    const signRecord = await signQuery.first();
    const signatureObject = signRecord || new Parse.Object("contracts_Signature");

    if (signRecord?.id) {
      signatureObject.id = signRecord.id;
    }

    signatureObject.set("SignatureName", normalizedName);
    signatureObject.set("ImageURL", finalSignatureUrl);
    signatureObject.set("UserId", userPointer);

    const existingInitials =
      signRecord && typeof signRecord.get === "function"
        ? String(signRecord.get("Initials") || "").trim()
        : "";
    if (existingInitials) {
      signatureObject.set("Initials", existingInitials);
    }

    const savedSignature = await signatureObject.save();
    setProfileSignatureUrl(finalSignatureUrl);
    setProfileSignatureRecordId(savedSignature?.id || signRecord?.id || "");
    return finalSignatureUrl;
  };

  const goToCompletedView = (snapshot = finalizedDoc) => {
    const nextSnapshot = snapshot || {};
    navigate(`/signaturePdf/${docId}/completed`, {
      replace: true,
      state: {
        docId,
        name: nextSnapshot?.name || documentName,
        signedUrl: nextSnapshot?.signedUrl || "",
        certificateUrl: nextSnapshot?.certificateUrl || ""
      }
    });
  };

  const sendCompletionAttachments = async ({ recipients, snapshot }) => {
    await Parse.Cloud.run("forwarddoc", {
      docId,
      recipients
    });

    if (!snapshot?.certificateUrl) {
      return;
    }

    const senderName = extUser?.Name || "DANASign";
    const senderEmail = extUser?.Email || localStorage.getItem("userEmail") || "";
    const certificateSubject = t("new-ui.individual-signing.certificate-email-subject", {
      defaultValue: "Completion certificate - {{document}}",
      document: snapshot?.name || t("document-title", { defaultValue: "Document" })
    });
    const certificateBody = t("new-ui.individual-signing.certificate-email-body", {
      defaultValue:
        "<p>Hello,</p><p>Attached is the completion certificate for <strong>{{document}}</strong>.</p><p>Regards,<br/>{{sender}}</p>",
      document: snapshot?.name || t("document-title", { defaultValue: "Document" }),
      sender: senderName
    });

    await Promise.all(
      recipients.map((recipient) =>
        Parse.Cloud.run("sendmailv3", {
          recipient,
          url: snapshot.certificateUrl,
          pdfName: `${snapshot?.name || "document"}-certificate`,
          subject: certificateSubject,
          html: certificateBody,
          from: "DANASign",
          replyto: senderEmail
        })
      )
    );
  };

  const getRequestEmailTemplateConfig = (
    docPayload,
    sourcePreference = REQUEST_EMAIL_SOURCE.CORPORATE
  ) => {
    const senderUser = {
      ...(extUser || {}),
      ...((docPayload?.ExtUserPtr || {}))
    };
    const tenant = {
      ...(extUser?.TenantId || {}),
      ...(senderUser?.TenantId || {}),
      AllowPersonalEmailTemplatesForAdmin:
        senderUser?.TenantId?.AllowPersonalEmailTemplatesForAdmin ??
        extUser?.TenantId?.AllowPersonalEmailTemplatesForAdmin ??
        allowPersonalRequestEmailForAdminByTenant,
      AllowPersonalEmailTemplatesForEditor:
        senderUser?.TenantId?.AllowPersonalEmailTemplatesForEditor ??
        extUser?.TenantId?.AllowPersonalEmailTemplatesForEditor ??
        allowPersonalRequestEmailForEditorByTenant,
      AllowPersonalEmailTemplatesForUser:
        senderUser?.TenantId?.AllowPersonalEmailTemplatesForUser ??
        extUser?.TenantId?.AllowPersonalEmailTemplatesForUser ??
        allowPersonalRequestEmailForUserByTenant,
      AllowPersonalEmailTemplatesForEditorOrgAdmin:
        senderUser?.TenantId?.AllowPersonalEmailTemplatesForEditorOrgAdmin ??
        extUser?.TenantId?.AllowPersonalEmailTemplatesForEditorOrgAdmin ??
        (allowPersonalRequestEmailForAdminByTenant || allowPersonalRequestEmailForEditorByTenant)
    };
    const defaultSubject = t("new-ui.settings.email.defaults.request.subject", {
      defaultValue: "{{sender_name}} requested your signature on {{document_title}}"
    });
    const defaultHtmlBody = t("new-ui.settings.email.defaults.request.body", {
      defaultValue:
        "<p>Hello {{receiver_name}},</p><p>{{sender_name}} has requested your signature on <strong>{{document_title}}</strong>.</p><p>Please review and sign using this link:</p><p>{{signing_url}}</p><p>Thanks,<br/>{{appName}} team</p>"
    });

    const normalizeTemplateConfig = (subjectRaw, bodyRaw, modeRaw) => {
      const mode = normalizeEmailTemplateMode(modeRaw);
      const subjectTemplate = String(subjectRaw || "").trim() || defaultSubject;
      const storedBody = String(bodyRaw || "").trim();

      if (mode === EMAIL_TEMPLATE_MODE.PLAIN) {
        const plainBodySource = extractTemplateContent(storedBody || toPlainTextFromHtml(defaultHtmlBody));
        const normalizedPlainBody = toPlainTextFromHtml(plainBodySource) || plainBodySource;
        return {
          mode,
          subjectTemplate,
          bodyTemplate: ensureRequestSigningUrlPlaceholder(normalizedPlainBody)
        };
      }

      const htmlBodySource = String(storedBody || defaultHtmlBody).trim();
      const bodyTemplate = htmlBodySource.includes("{{signing_url}}")
        ? htmlBodySource
        : `${htmlBodySource}<br/><br/>{{signing_url}}`;
      return {
        mode,
        subjectTemplate,
        bodyTemplate
      };
    };

    const wantsPersonal = sourcePreference === REQUEST_EMAIL_SOURCE.PERSONAL;
    const canUsePersonal = wantsPersonal && canUsePersonalRequestEmailTemplates(senderUser, tenant);
    const personalSubject = String(
      senderUser?.PersonalRequestSubject || extUser?.PersonalRequestSubject || ""
    ).trim();
    const personalBody = String(senderUser?.PersonalRequestBody || extUser?.PersonalRequestBody || "").trim();

    if (canUsePersonal && personalSubject && personalBody) {
      return normalizeTemplateConfig(
        personalSubject,
        personalBody,
        senderUser?.PersonalRequestBodyMode || extUser?.PersonalRequestBodyMode
      );
    }

    return normalizeTemplateConfig(tenant?.RequestSubject, tenant?.RequestBody, tenant?.RequestBodyMode);
  };

  const sendRequestSignatureEmails = async () => {
    const normalizedRecipients = normalizeRecipientList(requestRecipients).filter((recipient) =>
      EMAIL_REGEX.test(String(recipient.email || "").trim().toLowerCase())
    );
    if (!normalizedRecipients.length) {
      throw new Error(
        t("new-ui.request-sign.recipients-required", {
          defaultValue: "Add at least one recipient before saving."
        })
      );
    }
    if (normalizedRecipients.length > MAX_FORWARD_RECIPIENTS) {
      throw new Error(
        t("new-ui.individual-signing.email-max-recipients", {
          defaultValue: "You can send up to {{count}} recipients at a time.",
          count: MAX_FORWARD_RECIPIENTS
        })
      );
    }

    const docPayload = await fetchDocumentPayload(docId);
    if (!docPayload) {
      throw new Error(
        t("new-ui.individual-signing.document-not-found", {
          defaultValue: "Document not found or access denied."
        })
      );
    }

    const senderName = docPayload?.ExtUserPtr?.Name || extUser?.Name || "DANASign";
    const senderEmail = docPayload?.ExtUserPtr?.Email || extUser?.Email || localStorage.getItem("userEmail") || "";
    const senderPhone = docPayload?.ExtUserPtr?.Phone || extUser?.Phone || "";
    const senderLanguage = docPayload?.ExtUserPtr?.Language || extUser?.Language || 'es';
    const companyName =
      docPayload?.ExtUserPtr?.Company || extUser?.TenantId?.TenantName || localStorage.getItem("TenantName") || "";
    const documentTitle = docPayload?.Name || documentName || t("document", { defaultValue: "Document" });
    const note = docPayload?.Note || "";
    const expiryRaw = docPayload?.ExpiryDate?.iso || docPayload?.ExpiryDate;
    const expiryDate = expiryRaw
      ? new Date(expiryRaw).toLocaleDateString(undefined, {
          day: "numeric",
          month: "long",
          year: "numeric"
        })
      : "";
    const { mode, subjectTemplate, bodyTemplate } = getRequestEmailTemplateConfig(
      docPayload,
      requestEmailSource
    );

    // Translate button text based on sender's language
    const buttonTextTranslations = {
      es: 'Firmar aquí',
      en: 'Sign here',
      fr: 'Signer ici',
      de: 'Hier unterschreiben',
      it: 'Firma qui'
    };
    const signButtonText = buttonTextTranslations[senderLanguage] || buttonTextTranslations['es'];

    let quotaReached = false;
    let hasFailures = false;

    // When SendinOrder is enabled, only send to the first recipient.
    // Subsequent recipients will receive their email after the previous one signs.
    const isSendInOrder = Boolean(docPayload?.SendinOrder);
    const recipientsToEmail = isSendInOrder
      ? normalizedRecipients.slice(0, 1)
      : normalizedRecipients;

    for (const recipient of recipientsToEmail) {
      const recipientEmail = String(recipient.email || "").trim().toLowerCase();
      const recipientObjectId = String(recipient.objectId || "").trim();
      // When recipient has no contactbook objectId, use email as identifier.
      // ExternalSignerPage handles email-as-contactBookId via includes("@") check.
      const contactIdentifier = recipientObjectId || recipientEmail;
      const encoded = btoa(`${docId}/${recipientEmail}/${contactIdentifier}`);
      const signUrl = `${window.location.origin}/login/${encoded}`;

      const variables = {
        document_title: documentTitle,
        note,
        sender_name: senderName,
        sender_mail: senderEmail,
        sender_phone: senderPhone,
        receiver_name: recipient.name || recipientEmail,
        receiver_email: recipientEmail,
        receiver_phone: "",
        expiry_date: expiryDate,
        company_name: companyName,
        appName: "DANASign",
        signing_url:
          mode === EMAIL_TEMPLATE_MODE.PLAIN
            ? signUrl
            : `<a href="${signUrl}" target="_blank" rel="noopener noreferrer">${signButtonText}</a>`
      };

      const subject = replaceTemplateVariables(subjectTemplate, variables);
      const body = replaceTemplateVariables(bodyTemplate, variables);

      const payload = {
        extUserId: docPayload?.ExtUserPtr?.objectId || extUser?.objectId || "",
        recipient: recipientEmail,
        subject,
        from: senderName || "DANASign",
        replyto: senderEmail
      };
      if (mode === EMAIL_TEMPLATE_MODE.PLAIN) {
        payload.text = body;
        payload.html = `<pre style="font-family:inherit;white-space:pre-wrap;margin:0;">${escapeHtml(body)}</pre>`;
      } else {
        payload.html = body;
        payload.text = toPlainTextFromHtml(body);
      }

      const sendResult = await Parse.Cloud.run("sendmailv3", payload);
      const status = resolveCloudResultStatus(sendResult);
      if (status === "quota-reached") {
        quotaReached = true;
      } else if (status !== "success") {
        hasFailures = true;
      }
    }

    const docUpdate = new Parse.Object("contracts_Document");
    docUpdate.id = docId;
    docUpdate.set("IsDraft", false);
    docUpdate.set("SentToOthers", true);
    const storedSignedUrl = String(docPayload?.SignedUrl || "").trim();
    const storedSourceUrl = String(docPayload?.URL || "").trim();
    if (!storedSignedUrl && storedSourceUrl) {
      // Keep compatibility with legacy reports that require SignedUrl to be present in "In progress".
      docUpdate.set("SignedUrl", storedSourceUrl);
    }
    if (!expiryRaw) {
      const defaultDays = Number.parseInt(docPayload?.TimeToCompleteDays, 10) || 15;
      const nextExpiry = new Date(Date.now() + defaultDays * 24 * 60 * 60 * 1000);
      docUpdate.set("ExpiryDate", nextExpiry);
    }
    if (!quotaReached && !hasFailures) {
      docUpdate.set("SendMail", true);
    }
    await docUpdate.save();

    if (quotaReached) {
      throw new Error(t("quota-mail", { defaultValue: "You reached your email sending limit." }));
    }
    if (hasFailures) {
      throw new Error(
        t("mail-not-delivered", {
          defaultValue: "Some emails could not be delivered."
        })
      );
    }
  };

  const sendRequestNow = async () => {
    setRequestSendError("");
    setIsSendingRequestEmail(true);
    try {
      await sendRequestSignatureEmails();
      setIsRequestFinalizeModalOpen(false);
      setIsFinalized(true);
      showSuccess(t("mails-sent", { defaultValue: "Mails sent" }));
      navigate(REQUEST_SIGN_SENT_REDIRECT, { replace: true });
    } catch (sendError) {
      console.error("Failed to send request-sign emails", sendError);
      setRequestSendError(
        sendError?.message ||
          t("mail-not-delivered", {
            defaultValue: "Some emails could not be delivered."
          })
      );
    } finally {
      setIsSendingRequestEmail(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (recipientDropdownRef.current && !recipientDropdownRef.current.contains(event.target)) {
        setIsRecipientDropdownOpen(false);
      }
    };

    if (isRecipientDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isRecipientDropdownOpen]);

  useEffect(() => {
    if (!isMobileToolboxOpen) {
      return undefined;
    }

    const closeOnOutside = (event) => {
      if (mobileToolboxRef.current && !mobileToolboxRef.current.contains(event.target)) {
        setIsMobileToolboxOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsMobileToolboxOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutside);
    document.addEventListener("touchstart", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutside);
      document.removeEventListener("touchstart", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [isMobileToolboxOpen]);

  useEffect(() => {
    if (!docId) return;
    let cancelled = false;
    const loadDocument = async () => {
      setIsLoadingDoc(true);
      setHasTriedPdfRecovery(false);
      try {
        const doc = await fetchDocumentPayload(docId);
        if (!doc) {
          throw new Error(
            t("new-ui.individual-signing.document-not-found", {
              defaultValue: "Document not found or access denied."
            })
          );
        }
        if (doc?.IsCompleted) {
          if (!cancelled) {
            goToCompletedView({
              name: doc?.Name || "",
              signedUrl: doc?.SignedUrl || doc?.URL || "",
              certificateUrl: doc?.CertificateUrl || ""
            });
          }
          return;
        }
        if (isRequestMode) {
          const isAlreadySentForSignatures =
            doc?.SendMail === true || (doc?.SentToOthers === true && doc?.IsDraft === false);
          if (isAlreadySentForSignatures) {
            if (!cancelled) {
              navigate(REQUEST_SIGN_SENT_REDIRECT, { replace: true });
            }
            return;
          }
        }
        const originalDocumentUrl = doc?.SignedUrl || doc?.URL || "";
        const nextPdfUrl = await resolveDocumentUrl(originalDocumentUrl, docId);
        if (!nextPdfUrl) {
          throw new Error(
            t("new-ui.individual-signing.pdf-missing", {
              defaultValue: "Document file URL is missing."
            })
          );
        }
        const binaryResult = await loadPdfBinaryWithFallback({
          primaryUrl: nextPdfUrl,
          fallbackUrl: originalDocumentUrl,
          docId
        });
        if (!cancelled) {
          setRawDocumentUrl(originalDocumentUrl);
          setPdfUrl(binaryResult.url || nextPdfUrl);
          if (binaryResult.buffer) {
            const loadedBytes = new Uint8Array(binaryResult.buffer);
            pdfBytesRef.current = loadedBytes;
            setPdfFile({ data: loadedBytes });
          } else {
            pdfBytesRef.current = null;
            setPdfFile(null);
          }
          setDocumentName(doc?.Name || "");
          if (isTemplateEditMode && !templateEditTemplateId) {
            const linkedTemplateId = String(doc?.TemplateId?.objectId || "").trim();
            if (linkedTemplateId) {
              setTemplateEditTemplateId(linkedTemplateId);
            }
          }
          const nextPlaceholders = flattenExistingPlaceholders(doc?.Placeholders || []);
          let resolvedPlaceholders = nextPlaceholders;
          if (isRequestMode) {
            const stateRecipients = normalizeRecipientList(location.state?.recipients || []);
            const docRecipients = normalizeRecipientList(
              doc?.NewUIRecipients ||
                (Array.isArray(doc?.Signers)
                  ? doc.Signers.map((signer, index) => ({
                      id: signer?.objectId || signer?.Id || `recipient-${index + 1}`,
                      objectId: signer?.objectId || "",
                      name: signer?.Name || signer?.name || signer?.Email || signer?.email || "",
                      email: signer?.Email || signer?.email || ""
                    }))
                  : [])
            );
            const mergedRecipients = docRecipients.length ? docRecipients : stateRecipients;
            resolvedPlaceholders = hydratePlaceholderRecipientsByRole(nextPlaceholders, mergedRecipients);
            setRequestRecipients(mergedRecipients);
            const fallbackRecipientId =
              mergedRecipients[0]?.id ||
              String(resolvedPlaceholders[0]?.recipientId || "").trim() ||
              "";
            setActiveRecipientId(fallbackRecipientId);
          }
          setPlaceholders(resolvedPlaceholders);
          setCurrentPage(1);
          setZoomScale(DEFAULT_INITIAL_ZOOM);
          setCompletionRecipientsInput(
            String(extUser?.Email || localStorage.getItem("userEmail") || "").toLowerCase()
          );
          const docExtUser = doc?.ExtUserPtr || {};
          setSignatureValidation(
            extractSignatureThresholds({
              MinSignatureArea:
                docExtUser?.TenantId?.MinSignatureArea !== undefined
                  ? docExtUser.TenantId.MinSignatureArea
                  : docExtUser?.MinSignatureArea !== undefined
                    ? docExtUser.MinSignatureArea
                    : extUser?.TenantId?.MinSignatureArea !== undefined
                      ? extUser.TenantId.MinSignatureArea
                      : extUser?.MinSignatureArea,
              MinSignatureLength:
                docExtUser?.TenantId?.MinSignatureLength !== undefined
                  ? docExtUser.TenantId.MinSignatureLength
                  : docExtUser?.MinSignatureLength !== undefined
                    ? docExtUser.MinSignatureLength
                    : extUser?.TenantId?.MinSignatureLength !== undefined
                      ? extUser.TenantId.MinSignatureLength
                      : extUser?.MinSignatureLength
            })
          );
          pageRotationsRef.current = {};

          // Check if biometric verification is required for current user (self-signing mode only)
          if (!isRequestMode) {
            const currentUser = Parse.User.current();
            const currentUserEmail = normalizeEmailAddress(
              currentUser?.get?.("email") || localStorage.getItem("userEmail") || ""
            );
            const currentUserObjectId = currentUser?.id || "";

            // Build signer object for verification check
            const signer = {
              email: currentUserEmail,
              objectId: currentUserObjectId,
              name: currentUser?.get?.("name") || localStorage.getItem("username") || ""
            };

            // Extract verification configuration
            const config = extractVerificationConfig(doc, signer);
            setVerificationConfig(config);
            setVerificationRequired(config.enabled);
            setVerificationComplete(false);
            setVerificationEvidence(null);
          } else {
            // Request mode - verification not applicable
            setVerificationRequired(false);
            setVerificationComplete(false);
            setVerificationEvidence(null);
          }
        }
      } catch (loadError) {
        console.error("Failed to load signing document", loadError);
        if (!cancelled) {
          showError(
            loadError?.message ||
              t("new-ui.dashboard.errors.load-failed", {
                defaultValue: "Could not load the dashboard from backend."
              })
          );
        }
      } finally {
        if (!cancelled) {
          setIsLoadingDoc(false);
        }
      }
    };
    loadDocument();
    return () => {
      cancelled = true;
    };
  }, [docId, extUser?.Email, isRequestMode, isTemplateEditMode, templateEditTemplateId, location.key, location.state, t]);

  useEffect(() => {
    let cancelled = false;

    const loadProfileSignature = async () => {
      try {
        const currentUser = Parse.User.current();
        if (!currentUser?.id) {
          if (!cancelled) {
            setProfileSignatureUrl("");
            setProfileSignatureRecordId("");
          }
          return;
        }

        const userPointer = {
          __type: "Pointer",
          className: "_User",
          objectId: currentUser.id
        };
        const signQuery = new Parse.Query("contracts_Signature");
        signQuery.equalTo("UserId", userPointer);
        const signRecord = await signQuery.first();
        if (cancelled) {
          return;
        }

        const signatureUrl = String(signRecord?.get?.("ImageURL") || "").trim();
        setProfileSignatureUrl(signatureUrl);
        setProfileSignatureRecordId(signRecord?.id || "");
      } catch (signatureError) {
        console.error("Failed to load profile signature in signaturePdf", signatureError);
      }
    };

    loadProfileSignature();
    return () => {
      cancelled = true;
    };
  }, [docId]);

  useEffect(() => {
    if (isRequestMode) {
      return;
    }
    if (!profileSignatureUrl) {
      return;
    }
    setPlaceholders((current) =>
      current.map((item) =>
        item.type === "signature" && !String(item.signUrl || "").trim()
          ? { ...item, signUrl: profileSignatureUrl }
          : item
      )
    );
  }, [isRequestMode, profileSignatureUrl]);

  useEffect(() => {
    if (!isRequestMode) {
      return;
    }
    if (!requestRecipients.length) {
      setActiveRecipientId("");
      return;
    }
    const activeExists = requestRecipients.some((recipient) => recipient.id === activeRecipientId);
    if (!activeExists) {
      setActiveRecipientId(requestRecipients[0].id);
    }
  }, [activeRecipientId, isRequestMode, requestRecipients]);

  useEffect(() => {
    if (!isSignatureModalOpen) {
      return;
    }
    if (!selectedPlaceholder || selectedPlaceholder.type !== "signature") {
      setIsSignatureModalOpen(false);
      return;
    }
    if (!isRequestMode) {
      return;
    }
    const recipientId = String(selectedPlaceholder?.recipientId || "").trim();
    const recipient = recipientById.get(recipientId);
    const recipientEmail = normalizeEmailAddress(recipient?.email || recipient?.Email);
    if (!currentUserEmail || !recipientEmail || recipientEmail !== currentUserEmail) {
      setIsSignatureModalOpen(false);
    }
  }, [isRequestMode, isSignatureModalOpen, selectedPlaceholder, recipientById, currentUserEmail]);

  useEffect(() => {
    if (!isRequestMode || isTemplateEditMode) {
      if (isTemplateEditMode) {
        setContactbookRows([]);
        setIsLoadingContactbook(false);
      }
      return;
    }
    let cancelled = false;
    const loadContactbook = async () => {
      setIsLoadingContactbook(true);
      try {
        let normalizedContacts = [];

        try {
          const cloudResponse = await Parse.Cloud.run("getReport", {
            reportId: CONTACTS_REPORT_ID,
            skip: 0,
            limit: 300
          });
          normalizedContacts = normalizeContactbookRows(normalizeCloudResult(cloudResponse));
        } catch (reportError) {
          normalizedContacts = [];
        }

        if (!normalizedContacts.length) {
          const queryLimit = 300;
          const currentUser = Parse.User.current();
          const tenantId = String(extUser?.TenantId?.objectId || localStorage.getItem("TenantId") || "").trim();
          const tenantPointer = tenantId
            ? Parse.Object.extend("partners_Tenant").createWithoutData(tenantId)
            : null;

          if (currentUser) {
            try {
              const byUserQuery = new Parse.Query("contracts_Contactbook");
              byUserQuery.equalTo("CreatedBy", currentUser);
              byUserQuery.notEqualTo("IsDeleted", true);
              byUserQuery.descending("updatedAt");
              byUserQuery.limit(queryLimit);
              const byUserRows = await byUserQuery.find();
              normalizedContacts = normalizeContactbookRows(byUserRows);
            } catch (userQueryError) {
              normalizedContacts = [];
            }
          }

          if (!normalizedContacts.length && tenantPointer) {
            try {
              const byTenantQuery = new Parse.Query("contracts_Contactbook");
              byTenantQuery.equalTo("TenantId", tenantPointer);
              byTenantQuery.notEqualTo("IsDeleted", true);
              byTenantQuery.descending("updatedAt");
              byTenantQuery.limit(queryLimit);
              const byTenantRows = await byTenantQuery.find();
              normalizedContacts = normalizeContactbookRows(byTenantRows);
            } catch (tenantQueryError) {
              normalizedContacts = [];
            }
          }

          if (!normalizedContacts.length) {
            const baseUrl = getParseFunctionBaseUrl();
            if (baseUrl) {
              try {
                const signerResponse = await axios.post(
                  `${baseUrl}functions/getsigners`,
                  { search: "" },
                  { headers: getParseFunctionHeaders() }
                );
                normalizedContacts = normalizeContactbookRows(signerResponse?.data?.result || []);
              } catch (signersError) {
                normalizedContacts = [];
              }
            }
          }
        }

        if (!cancelled) {
          setContactbookRows(normalizedContacts);
        }
      } catch (contactError) {
        console.error("Request-sign editor contactbook load failed", contactError);
        if (!cancelled) {
          setContactbookRows([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingContactbook(false);
        }
      }
    };

    loadContactbook();
    return () => {
      cancelled = true;
    };
  }, [isRequestMode, isTemplateEditMode, extUser]);

  useEffect(() => {
    if (!selectedPlaceholder && isWidgetEditorModalOpen) {
      setIsWidgetEditorModalOpen(false);
    }
  }, [isWidgetEditorModalOpen, selectedPlaceholder]);

  useEffect(() => {
    const normalized = normalizePlaceholderBounds(placeholders);
    if (normalized !== placeholders) {
      setPlaceholders(normalized);
    }
  }, [placeholders]);

  useEffect(() => {
    if (!isRequestMode && isWidgetEditorModalOpen) {
      setIsWidgetEditorModalOpen(false);
    }
  }, [isRequestMode, isWidgetEditorModalOpen]);

  useEffect(() => {
    if (duplicateWidgetSourceId && !duplicateWidgetSource) {
      setIsDuplicateWidgetModalOpen(false);
      setDuplicateWidgetSourceId("");
    }
  }, [duplicateWidgetSource, duplicateWidgetSourceId]);

  useEffect(() => {
    if (!viewerRef.current) return undefined;
    const host = viewerRef.current;
    const updateWidth = () => {
      const width = Math.max(360, Math.min(860, host.clientWidth - 40));
      setPageWidth(width);
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!numPages) {
      return;
    }
    updateCurrentPage((current) => Math.max(1, Math.min(numPages, current)));
  }, [numPages]);

  useEffect(() => {
    if (!numPages) {
      return;
    }
    setDeletePageMax(numPages);
  }, [numPages]);

  useEffect(() => {
    currentPageRef.current = currentPageNumber;
  }, [currentPageNumber]);

  useEffect(() => {
    if (editorLocked) {
      return undefined;
    }
    if (!resizingId) {
      return undefined;
    }

    const onMouseMove = (event) => {
      const resizeState = resizeStateRef.current;
      const pageRect = pageLayerRef.current?.getBoundingClientRect();
      if (!resizeState || !pageRect?.width || !pageRect?.height) {
        return;
      }
      const deltaX = (event.clientX - resizeState.startClientX) / pageRect.width;
      const deltaY = (event.clientY - resizeState.startClientY) / pageRect.height;
      const maxWidth = Math.max(MIN_PLACEHOLDER_WIDTH, 1 - resizeState.startX);
      const maxHeight = Math.max(MIN_PLACEHOLDER_HEIGHT, 1 - resizeState.startY);
      const nextWidth = clamp(
        resizeState.startWidth + deltaX,
        MIN_PLACEHOLDER_WIDTH,
        maxWidth
      );
      const nextHeight = clamp(
        resizeState.startHeight + deltaY,
        MIN_PLACEHOLDER_HEIGHT,
        maxHeight
      );

      setPlaceholders((current) =>
        current.map((item) =>
          item.id === resizeState.id && item.pageNumber === resizeState.pageNumber
            ? fitPlaceholderToCanvas({ ...item, width: nextWidth, height: nextHeight })
            : item
        )
      );
    };

    const stopResize = () => {
      resizeStateRef.current = null;
      setResizingId("");
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", stopResize);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", stopResize);
    };
  }, [editorLocked, resizingId]);

  const addPlaceholderFromTool = (toolType, pageNumber, event = null, { isPrefill = false } = {}) => {
    if (editorLocked) return;
    if (!toolType) return;
    if (resizingId) return;
    if (!TOOL_TYPES.has(toolType)) return;
    const shouldCreatePrefill = Boolean(isRequestMode && isPrefill && toolType === "text");
    const container = event?.currentTarget || pageLayerRef.current;
    const rect = container?.getBoundingClientRect?.() || null;
    const canUsePointerPosition =
      Number.isFinite(event?.clientX) &&
      Number.isFinite(event?.clientY) &&
      rect?.width > 0 &&
      rect?.height > 0;
    const x = canUsePointerPosition
      ? Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
      : 0.5;
    const y = canUsePointerPosition
      ? Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height))
      : 0.5;
    const toolSize = DEFAULT_WIDGET_SIZE[toolType] || DEFAULT_WIDGET_SIZE.signature;
    const selectedRecipientForField = isRequestMode
      ? requestRecipients.find((recipient) => recipient.id === activeRecipientId) || null
      : null;
    const initialTextValue = shouldCreatePrefill
      ? ""
      : isRequestMode
      ? getRecipientAutofillValue(toolType, selectedRecipientForField)
      : getDefaultPlaceholderText(toolType, extUser, t);
    const initialSignUrl = isRequestMode ? "" : toolType === "signature" ? profileSignatureUrl : "";
    const nextPlaceholder = {
      id: `${toolType}-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
      pageNumber,
      type: toolType,
      x: Math.max(0, Math.min(1 - toolSize.width, x - toolSize.width / 2)),
      y: Math.max(0, Math.min(1 - toolSize.height, y - toolSize.height / 2)),
      width: toolSize.width,
      height: toolSize.height,
      signUrl: initialSignUrl,
      textValue: initialTextValue,
      checked: toolType === "checkbox" ? false : false,
      required: true,
      recipientId: shouldCreatePrefill ? "" : selectedRecipientForField?.id || "",
      recipientEmail: shouldCreatePrefill ? "" : selectedRecipientForField?.email || "",
      recipientName: shouldCreatePrefill ? "" : selectedRecipientForField?.name || "",
      isPrefill: shouldCreatePrefill
    };
    const boundedPlaceholder = fitPlaceholderToCanvas(nextPlaceholder);
    setPlaceholders((current) => [...current, boundedPlaceholder]);
    setSelectedId(boundedPlaceholder.id);
    showSuccess("");
  };

  const addPlaceholderFromMobileTool = (toolType, { isPrefill = false } = {}) => {
    addPlaceholderFromTool(toolType, currentPageNumber, null, { isPrefill });
    setIsMobileToolboxOpen(false);
  };

  const onToolDragStart = (toolType, event, { isPrefill = false } = {}) => {
    if (editorLocked || !toolType) {
      event.preventDefault();
      return;
    }
    setDraggingId("");
    setDraggingToolType(toolType);
    setDraggingToolIsPrefill(Boolean(isPrefill));
    event.dataTransfer.effectAllowed = "copy";
    event.dataTransfer.setData(TOOL_DRAG_MIME, toolType);
    event.dataTransfer.setData("application/x-danasign-tool-prefill", isPrefill ? "1" : "0");
    event.dataTransfer.setData("text/plain", toolType);
  };

  const resolveIncomingToolDrag = (dataTransfer) => {
    const incomingToolFromMime = String(dataTransfer?.getData(TOOL_DRAG_MIME) || "").trim();
    const incomingToolFromText = String(dataTransfer?.getData("text/plain") || "").trim();
    const incomingPrefillFlag = String(
      dataTransfer?.getData("application/x-danasign-tool-prefill") || ""
    ).trim();
    const incomingTool =
      (TOOL_TYPES.has(incomingToolFromMime) && incomingToolFromMime) ||
      (TOOL_TYPES.has(incomingToolFromText) && incomingToolFromText) ||
      (TOOL_TYPES.has(draggingToolType) ? draggingToolType : "");
    const incomingIsPrefill =
      incomingPrefillFlag === "1" ||
      (Boolean(incomingTool) && incomingTool === draggingToolType && draggingToolIsPrefill);
    return { incomingTool, incomingIsPrefill };
  };

  const setPlaceholderPosition = (placeholderId, pageNumber, event) => {
    if (editorLocked) return;
    const container = event.currentTarget;
    const rect = container.getBoundingClientRect();
    setPlaceholders((current) =>
      current.map((item) => {
        if (item.id !== placeholderId || item.pageNumber !== pageNumber) {
          return item;
        }
        const nextX = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width - item.width / 2));
        const nextY = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height - item.height / 2));
        return fitPlaceholderToCanvas({
          ...item,
          x: Math.max(0, Math.min(1 - item.width, nextX)),
          y: Math.max(0, Math.min(1 - item.height, nextY))
        });
      })
    );
  };

  const startResizePlaceholder = (placeholder, event) => {
    if (editorLocked) {
      return;
    }
    if (!placeholder || !RESIZABLE_TYPES.has(placeholder.type)) {
      return;
    }
    const pageRect = pageLayerRef.current?.getBoundingClientRect();
    if (!pageRect?.width || !pageRect?.height) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    resizeStateRef.current = {
      id: placeholder.id,
      pageNumber: placeholder.pageNumber,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startX: placeholder.x,
      startY: placeholder.y,
      startWidth: placeholder.width,
      startHeight: placeholder.height
    };
    setSelectedId(placeholder.id);
    setResizingId(placeholder.id);
  };

  const getCurrentPdfBytes = async () => {
    const refBytes = normalizePdfBytes(pdfBytesRef.current);
    if (refBytes?.length) {
      return refBytes;
    }
    const inMemoryBytes = normalizePdfBytes(pdfFile?.data);
    if (inMemoryBytes?.length) {
      pdfBytesRef.current = inMemoryBytes;
      return inMemoryBytes;
    }
    const recovery = await loadPdfBinaryWithFallback({
      primaryUrl: pdfUrl,
      fallbackUrl: rawDocumentUrl,
      docId
    });
    if (!recovery.buffer) {
      return null;
    }
    const recoveredBytes = new Uint8Array(recovery.buffer);
    pdfBytesRef.current = recoveredBytes;
    return recoveredBytes;
  };

  const applyPdfMutation = async (mutateFn) => {
    if (editorLocked) {
      return null;
    }
    if (isPdfMutationInFlightRef.current) {
      return null;
    }
    isPdfMutationInFlightRef.current = true;
    showSuccess("");
    setIsMutatingPdf(true);
    try {
      const bytes = await getCurrentPdfBytes();
      if (!bytes) {
        throw new Error(
          t("new-ui.individual-signing.pdf-render-error", {
            defaultValue: "Could not render the document. Please reload or upload again."
          })
        );
      }
      const { PDFDocument, degrees } = await import("pdf-lib");
      const pdfDoc = await PDFDocument.load(bytes);
      const currentPageCount = pdfDoc.getPageCount();
      await mutateFn(pdfDoc, { degrees, sourceBytes: bytes, pageCount: currentPageCount });
      const nextBytes = await pdfDoc.save();
      const nextBytesUint8 = new Uint8Array(nextBytes);
      pdfBytesRef.current = nextBytesUint8;
      isPdfModifiedRef.current = true;
      const nextPageCount = pdfDoc.getPageCount();
      setPdfFile({ data: nextBytesUint8 });
      setPdfUrl("");
      setNumPages(nextPageCount);

      // Upload modified PDF to server immediately after mutation
      if (docId) {
        try {
          let binary = "";
          const chunkSize = 8192;
          for (let i = 0; i < nextBytesUint8.length; i += chunkSize) {
            binary += String.fromCharCode.apply(
              null,
              nextBytesUint8.subarray(i, i + chunkSize)
            );
          }
          const fileBase64 = btoa(binary);
          const uploadName = `${docId}_modified.pdf`;
          const saveResult = await Parse.Cloud.run("savefile", {
            fileBase64,
            fileName: uploadName
          });
          const savedUrl = saveResult?.url || "";
          if (savedUrl) {
            uploadedModifiedPdfUrlRef.current = savedUrl;
            console.log("[applyPdfMutation] PDF uploaded:", savedUrl);
          }
        } catch (uploadErr) {
          console.error("[applyPdfMutation] Upload failed:", uploadErr);
        }
      }
      showSuccess(
        t("new-ui.individual-signing.page-updated", {
          defaultValue: "Page updated."
        })
      );
      return { pageCount: nextPageCount };
    } catch (mutationError) {
      console.error("Failed to mutate pdf", mutationError);
      showError(
        mutationError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
      return null;
    } finally {
      setIsMutatingPdf(false);
      isPdfMutationInFlightRef.current = false;
    }
  };

  const openAddPagesPicker = () => addPagesInputRef.current?.click();

  const onAddPages = async (event) => {
    if (editorLocked) {
      return;
    }
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    const isPdfType = String(file.type || "").toLowerCase().includes("pdf");
    const isPdfExtension = String(file.name || "").toLowerCase().endsWith(".pdf");
    if (!isPdfType && !isPdfExtension) {
      showError(
        t("new-ui.individual-signing.add-pages-pdf-only", {
          defaultValue: "Only PDF files are supported when adding pages."
        })
      );
      return;
    }
    const fileSizeMb = file.size / (1024 * 1024);
    if (fileSizeMb > MAX_DOCUMENT_FILE_SIZE_MB) {
      showError(
        `${t("file-alert-1", { defaultValue: "Selected file is too large." })} ${MAX_DOCUMENT_FILE_SIZE_MB} MB.`
      );
      return;
    }
    await applyPdfMutation(async (pdfDoc) => {
      const { PDFDocument } = await import("pdf-lib");
      const uploadedBytes = new Uint8Array(await file.arrayBuffer());
      const uploadedPdfDoc = await PDFDocument.load(uploadedBytes, { ignoreEncryption: true });
      const copiedPages = await pdfDoc.copyPages(uploadedPdfDoc, uploadedPdfDoc.getPageIndices());
      copiedPages.forEach((page) => pdfDoc.addPage(page));
    });
  };

  const goToPreviousPage = () => {
    if (editorLocked) {
      return;
    }
    updateCurrentPage((current) => Math.max(1, current - 1));
  };

  const goToNextPage = () => {
    if (editorLocked) {
      return;
    }
    updateCurrentPage((current) => Math.min(numPages || 1, current + 1));
  };

  const goToFirstPage = () => {
    if (editorLocked) {
      return;
    }
    updateCurrentPage(1);
  };

  const goToLastPage = () => {
    if (editorLocked) {
      return;
    }
    updateCurrentPage(numPages || 1);
  };

  const zoomIn = () => {
    setZoomScale((current) => Math.min(MAX_ZOOM, Number((current + ZOOM_STEP).toFixed(2))));
  };

  const zoomOut = () => {
    setZoomScale((current) => Math.max(MIN_ZOOM, Number((current - ZOOM_STEP).toFixed(2))));
  };

  const rotateCurrentPage = async (rotateDegree) => {
    if (editorLocked) {
      return;
    }
    if (!numPages) {
      return;
    }
    let targetPageNumber = currentPageNumber;
    const result = await applyPdfMutation(async (pdfDoc, { degrees, pageCount }) => {
      targetPageNumber = Math.max(1, Math.min(currentPageNumber, pageCount));
      const page = pdfDoc.getPage(targetPageNumber - 1);
      const cachedRotation = pageRotationsRef.current[targetPageNumber];
      const baseRotation = Number.isFinite(cachedRotation)
        ? normalizeRightAngle(cachedRotation)
        : normalizeRightAngle(page.getRotation().angle || 0);
      const nextRotation = normalizeRightAngle(baseRotation + rotateDegree);
      page.setRotation(degrees(nextRotation));
      pageRotationsRef.current[targetPageNumber] = nextRotation;
    });
    if (!result) {
      return;
    }
    setPlaceholders((current) => current.filter((item) => item.pageNumber !== targetPageNumber));
    if (selectedPlaceholder?.pageNumber === targetPageNumber) {
      setSelectedId("");
    }
    updateCurrentPage((current) => Math.max(1, Math.min(current, result.pageCount)));
  };

  const getLivePdfPageCount = async () => {
    const bytes = await getCurrentPdfBytes();
    if (!bytes) {
      return Math.max(1, numPages || 1);
    }
    const { PDFDocument } = await import("pdf-lib");
    const sourcePdfDoc = await PDFDocument.load(bytes);
    return Math.max(1, sourcePdfDoc.getPageCount());
  };

  const deletePageByNumber = async (targetPageNumberInput) => {
    if (editorLocked) {
      return false;
    }
    if (isPdfMutationInFlightRef.current) {
      return false;
    }
    const parsedPage = Number.parseInt(targetPageNumberInput, 10);
    if (!Number.isFinite(parsedPage)) {
      return false;
    }
    const targetPageSnapshot = Math.max(1, parsedPage);
    let pageToDelete = targetPageSnapshot;
    isPdfMutationInFlightRef.current = true;
    showError("");
    showSuccess("");
    setIsMutatingPdf(true);
    try {
      const bytes = await getCurrentPdfBytes();
      if (!bytes) {
        throw new Error(
          t("new-ui.individual-signing.pdf-render-error", {
            defaultValue: "Could not render the document. Please reload or upload again."
          })
        );
      }
      const { PDFDocument } = await import("pdf-lib");
      const sourcePdfDoc = await PDFDocument.load(bytes);
      const pageCount = sourcePdfDoc.getPageCount();
      if (pageCount <= 1) {
        throw new Error(
          t("new-ui.individual-signing.delete-page-last-error", {
            defaultValue: "You cannot delete the last page."
          })
        );
      }
      pageToDelete = Math.max(1, Math.min(targetPageSnapshot, pageCount));
      const keepPageIndices = Array.from({ length: pageCount }, (_, index) => index).filter(
        (index) => index !== pageToDelete - 1
      );
      const rebuiltPdfDoc = await PDFDocument.create();
      const copiedPages = await rebuiltPdfDoc.copyPages(sourcePdfDoc, keepPageIndices);
      copiedPages.forEach((page) => rebuiltPdfDoc.addPage(page));
      const nextBytes = await rebuiltPdfDoc.save();
      const nextBytesUint8 = new Uint8Array(nextBytes);
      pdfBytesRef.current = nextBytesUint8;
      isPdfModifiedRef.current = true;
      const nextPageCount = rebuiltPdfDoc.getPageCount();
      setPdfFile({ data: nextBytesUint8 });
      setPdfUrl("");
      setNumPages(nextPageCount);
      setDeletePageMax(nextPageCount);

      // Upload modified PDF to server immediately after deletion
      if (docId) {
        try {
          let binary = "";
          const chunkSize = 8192;
          for (let i = 0; i < nextBytesUint8.length; i += chunkSize) {
            binary += String.fromCharCode.apply(
              null,
              nextBytesUint8.subarray(i, i + chunkSize)
            );
          }
          const fileBase64 = btoa(binary);
          const uploadName = `${docId}_modified.pdf`;
          const saveResult = await Parse.Cloud.run("savefile", {
            fileBase64,
            fileName: uploadName
          });
          const savedUrl = saveResult?.url || "";
          if (savedUrl) {
            uploadedModifiedPdfUrlRef.current = savedUrl;
            console.log("[deletePage] PDF uploaded:", savedUrl);
          }
        } catch (uploadErr) {
          console.error("[deletePage] Upload failed:", uploadErr);
        }
      }
      showSuccess(
        t("new-ui.individual-signing.page-updated", {
          defaultValue: "Page updated."
        })
      );
      const nextRotationMap = {};
      Object.entries(pageRotationsRef.current).forEach(([key, value]) => {
        const pageNumber = Number.parseInt(key, 10);
        if (!Number.isFinite(pageNumber) || pageNumber === pageToDelete) {
          return;
        }
        const nextPageNumber = pageNumber > pageToDelete ? pageNumber - 1 : pageNumber;
        nextRotationMap[nextPageNumber] = normalizeRightAngle(value);
      });
      pageRotationsRef.current = nextRotationMap;
      setPlaceholders((current) =>
        current
          .filter((item) => item.pageNumber !== pageToDelete)
          .map((item) =>
            item.pageNumber > pageToDelete
              ? {
                  ...item,
                  pageNumber: item.pageNumber - 1
                }
              : item
          )
      );
      if (selectedPlaceholder?.pageNumber === pageToDelete) {
        setSelectedId("");
      }
      setCurrentPage(Math.max(1, Math.min(pageToDelete, nextPageCount)));
      return true;
    } catch (mutationError) {
      console.error("Failed to delete page", mutationError);
      showError(
        mutationError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
      return false;
    } finally {
      setIsMutatingPdf(false);
      isPdfMutationInFlightRef.current = false;
    }
  };

  const openDeletePageModal = async () => {
    if (editorLocked) {
      return;
    }
    setDeletePageModalError("");
    const fallbackCount = Math.max(1, numPages || 1);
    let liveCount = fallbackCount;
    try {
      liveCount = await getLivePdfPageCount();
    } catch (pageCountError) {
      console.error("Failed to read live page count", pageCountError);
    }
    setDeletePageMax(liveCount);
    setDeletePageInput(String(Math.max(1, Math.min(currentPageRef.current, liveCount))));
    setIsDeletePageModalOpen(true);
  };

  const confirmDeletePageFromModal = async () => {
    if (editorLocked) {
      return;
    }
    let liveCount = Math.max(1, deletePageMax || numPages || 1);
    try {
      liveCount = await getLivePdfPageCount();
    } catch (pageCountError) {
      console.error("Failed to refresh live page count", pageCountError);
    }
    setDeletePageMax(liveCount);
    const parsedPage = Number.parseInt(deletePageInput, 10);
    if (!Number.isFinite(parsedPage) || parsedPage < 1 || parsedPage > liveCount) {
      setDeletePageModalError(
        t("new-ui.individual-signing.delete-page-invalid", {
          defaultValue: "Enter a valid page number."
        })
      );
      return;
    }
    setDeletePageModalError("");
    const success = await deletePageByNumber(parsedPage);
    if (success) {
      setIsDeletePageModalOpen(false);
      setDeletePageInput("");
    }
  };

  const updateSelectedPlaceholder = (updates) => {
    if (editorLocked) return;
    if (!selectedId) return;
    setPlaceholders((current) =>
      current.map((item) =>
        item.id === selectedId ? fitPlaceholderToCanvas({ ...item, ...updates }) : item
      )
    );
  };

  const removePlaceholderById = (placeholderId) => {
    if (editorLocked || !placeholderId) {
      return;
    }
    setPlaceholders((current) => current.filter((item) => item.id !== placeholderId));
    if (selectedId === placeholderId) {
      setSelectedId("");
      setIsWidgetEditorModalOpen(false);
    }
    if (duplicateWidgetSourceId === placeholderId) {
      setDuplicateWidgetSourceId("");
      setIsDuplicateWidgetModalOpen(false);
    }
  };

  const openWidgetEditorFor = (placeholderId) => {
    if (editorLocked) {
      return;
    }
    if (!placeholderId) {
      return;
    }
    const placeholder = placeholders.find((item) => item.id === placeholderId) || null;
    if (!placeholder) {
      return;
    }
    setSelectedId(placeholderId);
    if (!isRequestMode) {
      return;
    }
    setIsWidgetEditorModalOpen(true);
  };

  const openDuplicateWidgetModalFor = (placeholderId) => {
    if (editorLocked || !placeholderId) {
      return;
    }
    const placeholder = placeholders.find((item) => item.id === placeholderId) || null;
    if (!placeholder) {
      return;
    }
    setDuplicateWidgetSourceId(placeholderId);
    setSelectedId(placeholderId);
    setIsDuplicateWidgetModalOpen(true);
  };

  const duplicateWidgetWithOption = (option) => {
    if (editorLocked || !duplicateWidgetSource || !DUPLICATE_WIDGET_OPTIONS.includes(option)) {
      return;
    }

    const pages = Array.from({ length: Math.max(0, numPages) }, (_, index) => index + 1);
    const sourcePage = Number(duplicateWidgetSource.pageNumber) || 1;
    let targets = [];
    let mode = option;

    if (mode === "all-pages") {
      targets = pages.filter((pageNumber) => pageNumber !== sourcePage);
    } else if (mode === "all-pages-but-last") {
      targets = pages.filter((pageNumber) => pageNumber !== sourcePage && pageNumber !== numPages);
    } else if (mode === "all-pages-but-first") {
      targets = pages.filter((pageNumber) => pageNumber !== sourcePage && pageNumber !== 1);
    } else {
      mode = "next-to-current-widget";
    }

    const clones = [];
    if (mode === "next-to-current-widget") {
      const horizontalGap = 0.02;
      let nextX = duplicateWidgetSource.x + duplicateWidgetSource.width + horizontalGap;
      if (nextX + duplicateWidgetSource.width > 1) {
        nextX = duplicateWidgetSource.x - duplicateWidgetSource.width - horizontalGap;
      }
      nextX = clamp(nextX, 0, Math.max(0, 1 - duplicateWidgetSource.width));
      clones.push({
        pageNumber: sourcePage,
        x: nextX,
        y: duplicateWidgetSource.y
      });
    } else {
      clones.push(
        ...targets.map((pageNumber) => ({
          pageNumber,
          x: duplicateWidgetSource.x,
          y: duplicateWidgetSource.y
        }))
      );
    }

    if (!clones.length) {
      showSuccess(
        t("new-ui.individual-signing.duplicate-widget-no-target", {
          defaultValue: "No target pages available for this copy option."
        })
      );
      setIsDuplicateWidgetModalOpen(false);
      return;
    }

    const timestamp = Date.now();
    const createdIds = [];
    setPlaceholders((current) => {
      const nextItems = [...current];
      clones.forEach((target, index) => {
        const cloneId = `${duplicateWidgetSource.type}-${timestamp}-${index}-${Math.floor(Math.random() * 1000)}`;
        createdIds.push(cloneId);
        nextItems.push(
          fitPlaceholderToCanvas({
          ...fitPlaceholderToCanvas(duplicateWidgetSource),
          id: cloneId,
          pageNumber: target.pageNumber,
          x: target.x,
          y: target.y
          })
        );
      });
      return nextItems;
    });

    if (createdIds[0]) {
      setSelectedId(createdIds[0]);
    }
    setIsDuplicateWidgetModalOpen(false);
    showSuccess(
      t("new-ui.individual-signing.duplicate-widget-success", {
        defaultValue: "Widget copied."
      })
    );
  };

  const appendRequestRecipient = ({ id = "", objectId = "", name = "", email = "" } = {}) => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    const normalizedName = String(name || "").trim();
    const activeRecipient =
      requestRecipients.find((recipient) => recipient.id === activeRecipientId) || null;
    const shouldReplaceActiveRole =
      isRequestMode &&
      !isTemplateEditMode &&
      Boolean(activeRecipient?.id) &&
      !normalizeEmailAddress(activeRecipient?.email || activeRecipient?.Email);

    if (!normalizedEmail) {
      setRecipientError(
        t("new-ui.request-sign.recipient-email-required", {
          defaultValue: "Signer email is required."
        })
      );
      return false;
    }
    if (!EMAIL_REGEX.test(normalizedEmail)) {
      setRecipientError(
        t("new-ui.request-sign.recipient-email-invalid", {
          defaultValue: "Signer email is invalid."
        })
      );
      return false;
    }
    if (
      requestRecipients.some(
        (recipient) => recipient.email === normalizedEmail && recipient.id !== activeRecipient?.id
      )
    ) {
      setRecipientError(
        t("new-ui.request-sign.recipient-email-duplicate", {
          defaultValue: "This recipient is already added."
        })
      );
      return false;
    }

    if (shouldReplaceActiveRole) {
      const nextRecipient = normalizeRecipientEntry(
        {
          ...activeRecipient,
          id: activeRecipient.id,
          objectId: objectId || activeRecipient.objectId || "",
          name: normalizedName || normalizedEmail,
          email: normalizedEmail
        },
        requestRecipients.findIndex((recipient) => recipient.id === activeRecipient.id)
      );
      setRequestRecipients((current) =>
        current.map((recipient) => (recipient.id === activeRecipient.id ? nextRecipient : recipient))
      );
      setActiveRecipientId(nextRecipient.id);
      setRecipientQueryInput("");
      setIsRecipientDropdownOpen(false);
      setRecipientError("");
      return true;
    }

    const nextRecipient = normalizeRecipientEntry(
      {
        id: id || `recipient-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        objectId,
        name: normalizedName || normalizedEmail,
        email: normalizedEmail,
        colorClass: RECIPIENT_COLORS[requestRecipients.length % RECIPIENT_COLORS.length]
      },
      requestRecipients.length
    );
    setRequestRecipients((current) => [...current, nextRecipient]);
    setActiveRecipientId(nextRecipient.id);
    setRecipientQueryInput("");
    setIsRecipientDropdownOpen(false);
    setRecipientError("");
    return true;
  };

  const addTemplateRole = () => {
    if (!isTemplateEditMode || editorLocked) {
      return;
    }
    const normalizedRoleName = String(roleNameInput || "").trim();
    if (!normalizedRoleName) {
      setRecipientError(
        t("new-ui.request-sign.role-required", {
          defaultValue: "El nombre del rol es requerido."
        })
      );
      return;
    }
    const roleAlreadyExists = requestRecipients.some(
      (recipient) => String(recipient?.name || "").trim().toLowerCase() === normalizedRoleName.toLowerCase()
    );
    if (roleAlreadyExists) {
      setRecipientError(
        t("new-ui.request-sign.role-duplicate", {
          defaultValue: "Este rol ya existe."
        })
      );
      return;
    }
    const nextRole = normalizeRecipientEntry(
      {
        id: `role-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        objectId: "",
        name: normalizedRoleName,
        email: "",
        colorClass: RECIPIENT_COLORS[requestRecipients.length % RECIPIENT_COLORS.length]
      },
      requestRecipients.length
    );
    setRequestRecipients((current) => [...current, nextRole]);
    setActiveRecipientId(nextRole.id);
    setRoleNameInput("");
    setRecipientError("");
  };

  const addRequestRecipientFromContactbook = (contact) => {
    if (!isRequestMode || editorLocked || !contact) {
      return;
    }
    appendRequestRecipient({
      id: contact.objectId,
      objectId: contact.objectId,
      name: contact.Name || contact.Email,
      email: contact.Email
    });
  };

  const addRequestRecipient = () => {
    if (!isRequestMode || editorLocked) {
      return;
    }
    const query = String(recipientQueryInput || "").trim();
    if (!query) {
      setRecipientError(
        t("new-ui.request-sign.recipient-search-required", {
          defaultValue: "Type a contact name or email."
        })
      );
      return;
    }

    const normalizedQuery = query.toLowerCase();
    const exactContact = filteredContactbook.find((contact) => {
      const contactName = String(contact.Name || "").trim().toLowerCase();
      const contactEmail = String(contact.Email || "").trim().toLowerCase();
      return (
        normalizedQuery === contactName ||
        normalizedQuery === contactEmail ||
        normalizedQuery === `${contactName} (${contactEmail})` ||
        normalizedQuery === `${contactName} <${contactEmail}>`
      );
    });

    if (exactContact) {
      addRequestRecipientFromContactbook(exactContact);
      return;
    }

    const extractedEmail = extractEmailFromText(query);
    if (!extractedEmail) {
      setRecipientError(
        t("new-ui.request-sign.recipient-search-invalid", {
          defaultValue: "Select a suggested contact or type a valid email (you can add external contacts)."
        })
      );
      return;
    }

    const inferredName = query
      .replace(new RegExp(escapeRegex(extractedEmail), "ig"), "")
      .replace(/[<>()]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    appendRequestRecipient({
      name: inferredName || extractedEmail,
      email: extractedEmail
    });
  };

  const openAddRecipientContactModal = () => {
    const extractedEmail = extractEmailFromText(recipientQueryInput);
    const inferredName = extractedEmail
      ? String(recipientQueryInput || "")
          .replace(new RegExp(escapeRegex(extractedEmail), "ig"), "")
          .replace(/[<>()]/g, " ")
          .replace(/\s+/g, " ")
          .trim()
      : "";
    setRecipientContactForm({
      name: inferredName,
      email: extractedEmail,
      phone: ""
    });
    setRecipientContactError("");
    setRecipientContactFieldErrors({ name: "", email: "" });
    setIsAddContactModalOpen(true);
  };

  const closeAddRecipientContactModal = (force = false) => {
    if (isSavingRecipientContact && !force) {
      return;
    }
    setIsAddContactModalOpen(false);
    setRecipientContactError("");
    setRecipientContactForm({ name: "", email: "", phone: "" });
    setRecipientContactFieldErrors({ name: "", email: "" });
  };

  const saveRecipientContact = async () => {
    if (!isRequestMode || editorLocked) {
      return;
    }
    const name = String(recipientContactForm?.name || "").trim();
    const email = String(recipientContactForm?.email || "")
      .trim()
      .toLowerCase();
    const phone = String(recipientContactForm?.phone || "").trim();

    const fieldErrors = {
      name: "",
      email: ""
    };
    if (!name) {
      fieldErrors.name = t("input-required", { defaultValue: "Please fill out this field." });
    }
    if (!email) {
      fieldErrors.email = t("input-required", { defaultValue: "Please fill out this field." });
    }
    if (fieldErrors.name || fieldErrors.email) {
      setRecipientContactFieldErrors(fieldErrors);
      setRecipientContactError("");
      return;
    }
    if (!EMAIL_REGEX.test(email)) {
      setRecipientContactFieldErrors({
        name: "",
        email: t("new-ui.request-sign.recipient-email-invalid", {
          defaultValue: "Signer email is invalid."
        })
      });
      setRecipientContactError("");
      return;
    }

    setIsSavingRecipientContact(true);
    setRecipientContactError("");
    setRecipientContactFieldErrors({ name: "", email: "" });
    try {
      const tenantId = String(extUser?.TenantId?.objectId || localStorage.getItem("TenantId") || "").trim();
      const savePayload = {
        name,
        email,
        phone
      };
      if (tenantId) {
        savePayload.tenantId = tenantId;
      }
      await Parse.Cloud.run("savecontact", savePayload);

      let createdContact = null;
      try {
        const signerRows = await Parse.Cloud.run("getsigners", { search: email });
        const normalizedMatches = normalizeContactbookRows(normalizeCloudResult(signerRows));
        createdContact = normalizedMatches.find((contact) => contact.Email === email) || null;
      } catch (lookupError) {
        createdContact = null;
      }

      if (!createdContact) {
        createdContact = {
          objectId: "",
          Name: name,
          Email: email,
          Phone: phone
        };
      }

      setContactbookRows((current) => normalizeContactbookRows([createdContact, ...current]));
      appendRequestRecipient({
        id: createdContact.objectId || "",
        objectId: createdContact.objectId || "",
        name: createdContact.Name || name,
        email: createdContact.Email || email
      });
      closeAddRecipientContactModal(true);
    } catch (saveError) {
      const message = String(saveError?.message || "");
      if (message.toLowerCase().includes("already exists")) {
        setRecipientContactError(t("add-signer-alert", { defaultValue: "Contact already exists!" }));
      } else {
        setRecipientContactError(
          message ||
            t("something-went-wrong-mssg", {
              defaultValue: "Something went wrong."
            })
        );
      }
    } finally {
      setIsSavingRecipientContact(false);
    }
  };

  const removeRequestRecipient = (recipientId) => {
    if (!isRequestMode || editorLocked) {
      return;
    }
    setRequestRecipients((current) => current.filter((recipient) => recipient.id !== recipientId));
    setPlaceholders((current) =>
      current.map((placeholder) =>
        placeholder.recipientId === recipientId
          ? {
              ...placeholder,
              recipientId: "",
              recipientEmail: "",
              recipientName: ""
            }
          : placeholder
      )
    );
    if (selectedPlaceholder?.recipientId === recipientId) {
      updateSelectedPlaceholder({
        recipientId: "",
        recipientEmail: "",
        recipientName: ""
      });
    }
  };

  const requestRecipientDelete = (recipient) => {
    if (!isRequestMode || editorLocked || !recipient?.id) {
      return;
    }
    setRecipientPendingDelete(recipient);
    setIsDeleteRecipientModalOpen(true);
  };

  const closeDeleteRecipientModal = () => {
    setIsDeleteRecipientModalOpen(false);
    setRecipientPendingDelete(null);
  };

  const confirmDeleteRecipient = () => {
    const recipientId = String(recipientPendingDelete?.id || "").trim();
    if (recipientId) {
      removeRequestRecipient(recipientId);
    }
    closeDeleteRecipientModal();
  };

  const openBiometricSettings = (recipient) => {
    setSelectedRecipientForBiometric(recipient);
    setIsBiometricSettingsOpen(true);
  };

  const saveBiometricSettings = (settings) => {
    if (!selectedRecipientForBiometric) {
      return;
    }
    
    setRequestRecipients((current) =>
      current.map((recipient) =>
        recipient.id === selectedRecipientForBiometric.id
          ? { ...recipient, requireBiometric: Boolean(settings?.enabled), verificationSettings: settings }
          : recipient
      )
    );
    
    setSelectedRecipientForBiometric(null);
  };

  const assignRecipientToSelected = (recipientId) => {
    if (isPrefillTextPlaceholder(selectedPlaceholder)) {
      return;
    }
    const recipient = requestRecipients.find((item) => item.id === recipientId) || null;
    const previousRecipient = selectedPlaceholder?.recipientId
      ? recipientById.get(selectedPlaceholder.recipientId)
      : null;
    const previousAutofillValue = getRecipientAutofillValue(selectedPlaceholder?.type, previousRecipient);
    const currentTextValue = String(selectedPlaceholder?.textValue || "").trim();
    const shouldApplyAutofill =
      isRequestMode &&
      RECIPIENT_AUTOFILL_TYPES.has(selectedPlaceholder?.type) &&
      (!currentTextValue || currentTextValue === String(previousAutofillValue || "").trim());

    updateSelectedPlaceholder({
      recipientId: recipient?.id || "",
      recipientEmail: normalizeEmailAddress(recipient?.email || recipient?.Email),
      recipientName: String(recipient?.name || recipient?.Name || "").trim(),
      ...(shouldApplyAutofill
        ? { textValue: getRecipientAutofillValue(selectedPlaceholder?.type, recipient) }
        : {})
    });
  };

  const prepareAndOpenSignatureModal = () => {
    setTypedSignature("");
    setUploadedSignatureDataUrl("");
    signaturePadRef.current?.clear?.();
    if (profileSignatureUrl) {
      setSignatureMode("saved");
      setSaveSignatureToProfile(false);
    } else {
      setSignatureMode("draw");
      setSaveSignatureToProfile(true);
    }
    setIsSignatureModalOpen(true);
  };

  const openSignatureModal = () => {
    if (editorLocked) {
      return;
    }
    if (!selectedPlaceholder || selectedPlaceholder.type !== "signature") {
      return;
    }
    if (!canEditSignaturePlaceholder(selectedPlaceholder)) {
      return;
    }
    prepareAndOpenSignatureModal();
  };

  const saveSignatureForSelected = async () => {
    if (editorLocked) {
      return;
    }
    if (!selectedPlaceholder) return;
    if (!canEditSignaturePlaceholder(selectedPlaceholder)) {
      setIsSignatureModalOpen(false);
      return;
    }
    setIsSavingSignatureChoice(true);
    let nextSignUrl = "";
    try {
      if (signatureMode === "saved") {
        nextSignUrl = String(profileSignatureUrl || "").trim();
        // Keep a data URL in placeholders whenever possible so final PDF embedding
        // does not depend on cross-origin fetches for remote profile images.
        if (nextSignUrl && !nextSignUrl.startsWith("data:image")) {
          try {
            const resolvedProfileUrl = await resolveDocumentUrl(nextSignUrl, docId);
            const profileResponse = await fetch(resolvedProfileUrl, { method: "GET" });
            if (profileResponse.ok) {
              const profileBlob = await profileResponse.blob();
              const profileDataUrl = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ""));
                reader.onerror = () => reject(reader.error || new Error("File read error"));
                reader.readAsDataURL(profileBlob);
              });
              if (String(profileDataUrl || "").startsWith("data:image")) {
                nextSignUrl = profileDataUrl;
              }
            }
          } catch (profileSignLoadError) {
            // Handled below with a validation error.
          }
          if (!nextSignUrl.startsWith("data:image")) {
            throw new Error(
              t("new-ui.individual-signing.saved-signature-load-error", {
                defaultValue: "Could not load the saved signature. Please draw or upload it again."
              })
            );
          }
        }
      } else if (signatureMode === "draw") {
        const pad = signaturePadRef.current;
        if (!pad || pad.isEmpty()) {
          showError(
            t("new-ui.individual-signing.signature-required", {
              defaultValue: "Please draw or type a signature."
            })
          );
          return;
        }
        const valid = validateDrawnSignature(
          signaturePadRef,
          signatureValidation.minArea,
          signatureValidation.minLength
        );
        if (!valid) {
          throw new Error(
            t("signature-too-small", {
              defaultValue: "The signature is too small. Please draw a larger signature."
            })
          );
        }
        nextSignUrl = pad.toDataURL("image/png");
      } else if (signatureMode === "upload") {
        nextSignUrl = String(uploadedSignatureDataUrl || "").trim();
      } else {
        nextSignUrl = buildTypedSignatureDataUrl(typedSignature);
      }

      if (!nextSignUrl) {
        showError(
          t("new-ui.individual-signing.signature-required", {
            defaultValue: "Please draw or type a signature."
          })
        );
        return;
      }
      if (signatureMode !== "draw") {
        const valid = await ensureSignatureImageMeetsThresholds(nextSignUrl);
        if (!valid) {
          throw new Error(
            t("signature-too-small", {
              defaultValue: "The signature is too small. Please draw a larger signature."
            })
          );
        }
      }

      let placeholderSignUrl = nextSignUrl;
      if (saveSignatureToProfile && signatureMode !== "saved") {
        await saveSignatureInProfile(nextSignUrl);
      }

      updateSelectedPlaceholder({ signUrl: placeholderSignUrl });
      setIsSignatureModalOpen(false);
      showError("");
      setUploadedSignatureDataUrl("");
      signaturePadRef.current?.clear?.();
    } catch (signatureError) {
      console.error("Failed to save signature choice", signatureError);
      showError(
        signatureError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsSavingSignatureChoice(false);
    }
  };

  const onSignatureUploadSelected = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!file.type.startsWith("image/")) {
      showError(
        t("new-ui.individual-signing.image-type-error", {
          defaultValue: "Please upload a valid image file."
        })
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || "");
      if (!value.startsWith("data:image")) {
        showError(
          t("new-ui.individual-signing.image-type-error", {
            defaultValue: "Please upload a valid image file."
          })
        );
        return;
      }
      setUploadedSignatureDataUrl(value);
      showError("");
    };
    reader.onerror = () => {
      showError(
        t("new-ui.individual-signing.image-read-error", {
          defaultValue: "Could not read the image file."
        })
      );
    };
    reader.readAsDataURL(file);
  };

  const openImagePicker = () => {
    if (editorLocked) {
      return;
    }
    if (!selectedPlaceholder || !["image", "stamp"].includes(selectedPlaceholder.type)) {
      return;
    }
    imagePickerTargetIdRef.current = selectedPlaceholder.id || "";
    imageInputRef.current?.click();
  };

  const onImageSelected = (event) => {
    if (editorLocked) {
      return;
    }
    const file = event.target.files?.[0];
    event.target.value = "";
    const targetPlaceholderId = String(imagePickerTargetIdRef.current || selectedId || "").trim();
    const targetPlaceholder = placeholders.find((item) => item.id === targetPlaceholderId) || null;
    if (!file || !targetPlaceholder || !["image", "stamp"].includes(targetPlaceholder.type)) {
      imagePickerTargetIdRef.current = "";
      return;
    }
    if (!file.type.startsWith("image/")) {
      imagePickerTargetIdRef.current = "";
      showError(
        t("new-ui.individual-signing.image-type-error", {
          defaultValue: "Please upload a valid image file."
        })
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const value = String(reader.result || "");
      if (!value.startsWith("data:image")) {
        imagePickerTargetIdRef.current = "";
        showError(
          t("new-ui.individual-signing.image-type-error", {
            defaultValue: "Please upload a valid image file."
          })
        );
        return;
      }
      setPlaceholders((current) =>
        current.map((item) => (item.id === targetPlaceholderId ? { ...item, signUrl: value } : item))
      );
      setSelectedId(targetPlaceholderId);
      imagePickerTargetIdRef.current = "";
      showError("");
    };
    reader.onerror = () => {
      imagePickerTargetIdRef.current = "";
      showError(
        t("new-ui.individual-signing.image-read-error", {
          defaultValue: "Could not read the image file."
        })
      );
    };
    reader.readAsDataURL(file);
  };

  const buildRecipientPayload = () =>
    requestRecipients.map((recipient, index) => ({
      id: recipient.id || `recipient-${index + 1}`,
      name: recipient.name || recipient.email || `Signer ${index + 1}`,
      email: recipient.email || "",
      objectId: recipient.objectId || "",
      colorClass: recipient.colorClass || RECIPIENT_COLORS[index % RECIPIENT_COLORS.length],
      requireBiometric: recipient.requireBiometric || false,
      verificationSettings: recipient.verificationSettings || {}
    }));

  const buildContactSignerPointers = (recipientPayload = []) =>
    recipientPayload
      .filter((recipient) => String(recipient.objectId || "").trim())
      .map((recipient) => ({
        __type: "Pointer",
        className: "contracts_Contactbook",
        objectId: recipient.objectId
      }));

  const saveRequestEditor = async ({
    keepAsDraft = true,
    notifySuccess = true,
    allowEmptyPrefill = false
  } = {}) => {
    showError("");
    if (notifySuccess) {
      showSuccess("");
    }
    setRecipientError("");
    setRequestSendError("");
    setIsSaving(true);
    try {
      const preparedPlaceholders =
        isTemplateEditMode && isRequestMode
          ? hydratePlaceholderRecipientsByRole(placeholders, requestRecipients)
          : placeholders;

      if (!requestRecipients.length) {
        throw new Error(
          t("new-ui.request-sign.recipients-required", {
            defaultValue: "Add at least one recipient before saving."
          })
        );
      }
      if (!preparedPlaceholders.length) {
        throw new Error(
          t("new-ui.request-sign.fields-required", {
            defaultValue: "Add at least one field before saving."
          })
        );
      }
      const placeholderWithoutRecipient = preparedPlaceholders.find(
        (item) => !isPrefillTextPlaceholder(item) && !String(item?.recipientId || "").trim()
      );
      if (placeholderWithoutRecipient) {
        throw new Error(
          t("new-ui.request-sign.assign-recipient-required", {
            defaultValue: "Every field must be assigned to a recipient."
          })
        );
      }
      if (!isTemplateEditMode) {
        const placeholderWithoutRecipientEmail = preparedPlaceholders.find((item) => {
          if (isPrefillTextPlaceholder(item)) {
            return false;
          }
          const recipientId = String(item?.recipientId || "").trim();
          if (!recipientId) {
            return false;
          }
          const recipient = recipientById.get(recipientId);
          const recipientEmail = normalizeEmailAddress(recipient?.email || recipient?.Email);
          return !recipientEmail || !EMAIL_REGEX.test(recipientEmail);
        });
        if (placeholderWithoutRecipientEmail) {
          throw new Error(
            t("new-ui.request-sign.map-role-to-recipient-required", {
              defaultValue:
                "Each assigned role must be linked to a real signer with a valid email before sending."
            })
          );
        }
      }
      const incompletePrefill =
        !allowEmptyPrefill &&
        preparedPlaceholders.find(
          (item) => isPrefillTextPlaceholder(item) && !String(item?.textValue || "").trim()
        );
      if (!allowEmptyPrefill && incompletePrefill) {
        throw new Error(
          t("new-ui.request-sign.prefill-required", {
            defaultValue: "Complete all prefilled text fields before saving."
          })
        );
      }

      const recipientPayload = buildRecipientPayload();

      // Use the pre-uploaded modified PDF URL if available (set by applyPdfMutation)
      const updatedDocumentUrl = uploadedModifiedPdfUrlRef.current || null;
      if (updatedDocumentUrl) {
        console.log("[saveRequestEditor] Using pre-uploaded PDF URL:", updatedDocumentUrl);
      }

      const draftDoc = new Parse.Object("contracts_Document");
      draftDoc.id = docId;
      draftDoc.set("Placeholders", buildGroupedPlaceholdersPayload(preparedPlaceholders, requestRecipients));
      draftDoc.set("NewUIRecipients", recipientPayload);
      draftDoc.set("IsSignyourself", false);
      draftDoc.set("SentToOthers", true);
      draftDoc.set("IsDraft", keepAsDraft);
      if (updatedDocumentUrl) {
        draftDoc.set("URL", updatedDocumentUrl);
        draftDoc.set("SignedUrl", updatedDocumentUrl);
      }
      const signersFromContacts = buildContactSignerPointers(recipientPayload);
      if (signersFromContacts.length) {
        draftDoc.set("Signers", signersFromContacts);
      }
      await draftDoc.save();
      if (notifySuccess) {
        showSuccess(
          t("new-ui.request-sign.saved", {
            defaultValue: "Recipients and fields saved."
          })
        );
      }
      return true;
    } catch (saveError) {
      console.error("Failed to save request-sign editor", saveError);
      showError(
        saveError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveTemplateEditor = async () => {
    if (!canManageTemplates) {
      showError(
        t("something-went-wrong-mssg", {
          defaultValue: "Something went wrong."
        })
      );
      return;
    }
    showError("");
    showSuccess("");
    setRecipientError("");
    setRequestSendError("");

    const requestEditorSaved = await saveRequestEditor({
      keepAsDraft: true,
      notifySuccess: false,
      allowEmptyPrefill: true
    });
    if (!requestEditorSaved) {
      return;
    }

    const templateId = String(templateEditTemplateId || "").trim();
    if (!templateId) {
      showError(
        t("template-not-found", {
          defaultValue: "Template not found."
        })
      );
      return;
    }

    setIsSaving(true);
    try {
      const recipientPayload = buildRecipientPayload();
      const signersFromContacts = buildContactSignerPointers(recipientPayload);
      const currentDraftPayload = await fetchDocumentPayload(docId);
      const currentTemplatePlaceholders = buildGroupedPlaceholdersPayload(placeholders, requestRecipients);

      // Strip signature/image responses so templates never carry filled-in signatures.
      const cleanedTemplatePlaceholders = currentTemplatePlaceholders.map((page) => {
        if (!Array.isArray(page?.pos)) return page;
        const cleanedPos = page.pos.map((widget) => {
          if (!widget) return widget;
          const wType = String(widget.type || widget.options?.type || "").toLowerCase();
          const isImage = IMAGE_VALUE_TYPES.has(wType) || wType === "initials";
          const opts = widget.options ? { ...widget.options } : {};
          if (isImage) {
            delete opts.response;
            delete opts.SignUrl;
            delete opts.defaultValue;
            const { SignUrl: _s, ...rest } = widget;
            return { ...rest, options: opts };
          }
          return widget;
        });
        return { ...page, pos: cleanedPos };
      });

      const templateObject = new Parse.Object("contracts_Template");
      templateObject.id = templateId;
      templateObject.set("Placeholders", cleanedTemplatePlaceholders);
      templateObject.set("NewUIRecipients", recipientPayload);
      templateObject.set("Signers", signersFromContacts);
      if (currentDraftPayload?.URL) {
        templateObject.set("URL", currentDraftPayload.URL);
      }
      if (currentDraftPayload?.SignedUrl) {
        templateObject.set("SignedUrl", currentDraftPayload.SignedUrl);
      }
      await templateObject.save();

      showSuccess(
        t("saved-successfully", {
          defaultValue: "Saved successfully."
        })
      );
    } catch (templateError) {
      console.error("Failed to save template editor", templateError);
      showError(
        templateError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Biometric verification handlers
  const handleVerificationSuccess = (evidence) => {
    // Update state
    setVerificationEvidence(evidence || null);
    setVerificationComplete(true);
    setShowVerificationModal(false);

    // Show success message
    showSuccess(
      t("verification.success-complete", {
        defaultValue: "Identity verification completed successfully."
      })
    );

    // Automatically proceed to finalize after verification
    setTimeout(() => {
      finalizeDocument();
    }, 500);
  };

  const handleVerificationClose = () => {
    setShowVerificationModal(false);
  };

  const finalizeDocument = async () => {
    if (isTemplateEditMode) {
      await saveTemplateEditor();
      return;
    }

    if (isRequestMode) {
      const finalized = await saveRequestEditor({
        // Keep draft until the user confirms sending in the modal.
        keepAsDraft: true,
        notifySuccess: false
      });
      if (finalized) {
        // Refresh extUser from localStorage so personal-template availability is up-to-date
        // (the user may have configured it in Account Settings during this session).
        const freshExtClass = parseStoredJson("Extand_Class");
        const freshExtUser = Array.isArray(freshExtClass) ? freshExtClass?.[0] || {} : {};
        
        // Update extUser state first to ensure useMemo recalculates
        setExtUser(freshExtUser);

        const freshHasPersonal = hasPersonalRequestEmailTemplate(freshExtUser);
        const freshCanUse = canUsePersonalRequestEmailTemplates(freshExtUser, {
          ...(freshExtUser?.TenantId || {}),
          AllowPersonalEmailTemplatesForAdmin: allowPersonalRequestEmailForAdminByTenant,
          AllowPersonalEmailTemplatesForEditor: allowPersonalRequestEmailForEditorByTenant,
          AllowPersonalEmailTemplatesForUser: allowPersonalRequestEmailForUserByTenant,
          AllowPersonalEmailTemplatesForEditorOrgAdmin:
            allowPersonalRequestEmailForAdminByTenant || allowPersonalRequestEmailForEditorByTenant
        });

        // Don't show success message yet - wait until user confirms sending in the modal
        setRequestSendError("");
        setRequestEmailSource(
          freshCanUse &&
            freshHasPersonal &&
            Boolean(freshExtUser?.UsePersonalRequestEmailByDefault)
            ? REQUEST_EMAIL_SOURCE.PERSONAL
            : REQUEST_EMAIL_SOURCE.CORPORATE
        );
        
        // Open modal after state updates are queued
        setIsRequestFinalizeModalOpen(true);
      }
      return;
    }

    // Check if biometric verification is required but not complete.
    // We require fresh evidence captured in the current signing session.
    if (verificationRequired && (!verificationComplete || !verificationEvidence)) {
      setShowVerificationModal(true);
      return;
    }

    if (isFinalized) {
      goToCompletedView(finalizedDoc);
      return;
    }
    showError("");
    showSuccess("");
    setCompletionEmailError("");
    setIsSaving(true);
    try {
      const hasSignatureWidget = placeholders.some((item) => item.type === "signature");
      if (!hasSignatureWidget) {
        throw new Error(
          t("new-ui.individual-signing.signature-widget-required", {
            defaultValue: "You must add at least one signature field before finalizing."
          })
        );
      }

      const missingField = placeholders.find((item) => {
        if (item.type === "checkbox") {
          return false;
        }
        if (IMAGE_VALUE_TYPES.has(item.type)) {
          return !String(item.signUrl || "").trim();
        }
        return !String(item.textValue || "").trim();
      });

      if (missingField) {
        throw new Error(
          t("new-ui.individual-signing.complete-all-fields", {
            defaultValue: "Complete all fields before finalizing."
          })
        );
      }
      const signatureWidgets = placeholders.filter(
        (item) => item.type === "signature" && String(item.signUrl || "").trim()
      );
      for (let i = 0; i < signatureWidgets.length; i += 1) {
        const valid = await ensureSignatureImageMeetsThresholds(signatureWidgets[i].signUrl);
        if (!valid) {
          throw new Error(
            t("signature-too-small", {
              defaultValue: "The signature is too small. Please draw a larger signature."
            })
          );
        }
      }

      const currentPdfBytes = await getCurrentPdfBytes();
      if (!currentPdfBytes) {
        throw new Error(
          t("new-ui.individual-signing.pdf-render-error", {
            defaultValue: "Could not render the document. Please reload or upload again."
          })
        );
      }

      const { PDFDocument, StandardFonts, rgb } = await import("pdf-lib");
      const pdfDoc = await PDFDocument.load(currentPdfBytes);
      const fontCache = new Map();
      const resolvePdfStandardFont = (textStyle = DEFAULT_WIDGET_TEXT_STYLE) => {
        const family = String(textStyle?.fontFamily || DEFAULT_WIDGET_TEXT_STYLE.fontFamily)
          .trim()
          .toLowerCase();
        const isBold = Boolean(textStyle?.bold);
        const isItalic = Boolean(textStyle?.italic);

        if (family === "times") {
          if (isBold && isItalic) return StandardFonts.TimesRomanBoldItalic;
          if (isBold) return StandardFonts.TimesRomanBold;
          if (isItalic) return StandardFonts.TimesRomanItalic;
          return StandardFonts.TimesRoman;
        }
        if (family === "courier") {
          if (isBold && isItalic) return StandardFonts.CourierBoldOblique;
          if (isBold) return StandardFonts.CourierBold;
          if (isItalic) return StandardFonts.CourierOblique;
          return StandardFonts.Courier;
        }
        if (isBold && isItalic) return StandardFonts.HelveticaBoldOblique;
        if (isBold) return StandardFonts.HelveticaBold;
        if (isItalic) return StandardFonts.HelveticaOblique;
        return StandardFonts.Helvetica;
      };
      const getPdfFontForStyle = async (textStyle = DEFAULT_WIDGET_TEXT_STYLE) => {
        const fontName = resolvePdfStandardFont(textStyle);
        if (fontCache.has(fontName)) {
          return fontCache.get(fontName);
        }
        const embeddedFont = await pdfDoc.embedFont(fontName);
        fontCache.set(fontName, embeddedFont);
        return embeddedFont;
      };
      const defaultFont = await getPdfFontForStyle(DEFAULT_WIDGET_TEXT_STYLE);
      const imageCache = new Map();

      const fetchImageAssetFromUrl = async (url) => {
        const targetUrl = String(url || "").trim();
        if (!targetUrl) {
          return null;
        }
        try {
          const response = await fetch(targetUrl, { method: "GET" });
          if (!response.ok) {
            return null;
          }
          const mimeHeader = String(response.headers.get("content-type") || "").toLowerCase();
          const mime = mimeHeader.includes("png") ? "image/png" : "image/jpeg";
          const bytes = new Uint8Array(await response.arrayBuffer());
          if (!bytes.length) {
            return null;
          }
          let binary = "";
          const chunkSize = 0x8000;
          for (let offset = 0; offset < bytes.length; offset += chunkSize) {
            const chunk = bytes.subarray(offset, offset + chunkSize);
            binary += String.fromCharCode(...chunk);
          }
          return { mime, bytes, base64: btoa(binary) };
        } catch (error) {
          return null;
        }
      };

      const resolveImageAsset = async (source) => {
        const src = String(source || "").trim();
        if (!src) return null;
        if (imageCache.has(src)) {
          return imageCache.get(src);
        }
        const fromDataUrl = parseDataUrlImage(src);
        if (fromDataUrl) {
          imageCache.set(src, fromDataUrl);
          return fromDataUrl;
        }
        const urlCandidates = [];
        const pushCandidate = (value) => {
          const normalized = String(value || "").trim();
          if (!normalized) {
            return;
          }
          if (!urlCandidates.includes(normalized)) {
            urlCandidates.push(normalized);
          }
        };
        try {
          const resolvedUrl = await resolveDocumentUrl(src, docId);
          pushCandidate(resolvedUrl);
        } catch (error) {
          // keep fallback candidates
        }
        pushCandidate(src);
        try {
          const freshSignedUrl = await requestFreshSignedUrl(src, docId);
          pushCandidate(freshSignedUrl);
        } catch (error) {
          // keep existing candidates
        }

        for (let i = 0; i < urlCandidates.length; i += 1) {
          const asset = await fetchImageAssetFromUrl(urlCandidates[i]);
          if (asset?.bytes?.length) {
            imageCache.set(src, asset);
            return asset;
          }
        }
        imageCache.set(src, null);
        return null;
      };

      const payload = buildGroupedPlaceholdersPayload(placeholders);
      for (let i = 0; i < payload.length; i += 1) {
        const pageItem = payload[i];
        const pageIndex = Math.max(0, Number.parseInt(pageItem.pageNumber, 10) - 1);
        const page = pdfDoc.getPage(pageIndex);
        const pageWidth = page.getWidth();
        const pageHeight = page.getHeight();

        for (let j = 0; j < pageItem.pos.length; j += 1) {
          const widget = pageItem.pos[j];
          const width = Math.max(8, (Number(widget.Width) || 0) * pageWidth);
          const height = Math.max(8, (Number(widget.Height) || 0) * pageHeight);
          const x = (Number(widget.xPosition) || 0) * pageWidth;
          const yTop = (Number(widget.yPosition) || 0) * pageHeight;
          const y = pageHeight - yTop - height;
          const type = String(widget.type || "").toLowerCase();

          if (IMAGE_VALUE_TYPES.has(type)) {
            const source = widget.SignUrl || widget.options?.response || "";
            const asset = await resolveImageAsset(source);
            if (!asset?.bytes?.length) {
              const isSignatureLike = ["signature", "initials", "stamp"].includes(type);
              const isRequired = widget?.options?.status === "required";
              if (isSignatureLike || isRequired) {
                throw new Error(
                  t("new-ui.individual-signing.signature-required", {
                    defaultValue:
                      "Could not embed one or more signatures/images in the final PDF. Please re-add the field value and try again."
                  })
                );
              }
              continue;
            }
            const normalizedAssetDataUrl = `data:${asset.mime};base64,${asset.base64}`;
            widget.SignUrl = normalizedAssetDataUrl;
            widget.options = {
              ...(widget.options || {}),
              response: normalizedAssetDataUrl
            };
            let embeddedImage = null;
            if (asset.mime.includes("png")) {
              try {
                embeddedImage = await pdfDoc.embedPng(asset.bytes);
              } catch (pngError) {
                embeddedImage = null;
              }
            }
            if (!embeddedImage) {
              try {
                embeddedImage = await pdfDoc.embedJpg(asset.bytes);
              } catch (jpgError) {
                embeddedImage = null;
              }
            }
            if (!embeddedImage) {
              throw new Error(
                t("new-ui.individual-signing.signature-required", {
                  defaultValue:
                    "Could not embed one or more signatures/images in the final PDF. Please re-add the field value and try again."
                })
              );
            }
            page.drawImage(embeddedImage, { x, y, width, height });
            continue;
          }

          if (type === "checkbox") {
            const checked = parseCheckboxResponse(widget.options?.response);
            const size = Math.max(10, Math.min(width, height, 16));
            const boxY = y + Math.max(0, (height - size) / 2);
            const checkboxLabel =
              String(
                widget.options?.label ||
                  widget.options?.defaultValue ||
                  t("new-ui.individual-signing.checkbox-label-default", {
                    defaultValue: "I agree"
                  })
              ).trim();
            page.drawRectangle({
              x,
              y: boxY,
              width: size,
              height: size,
              borderWidth: 1,
              borderColor: rgb(0.2, 0.23, 0.27)
            });
            if (checked) {
              page.drawLine({
                start: { x: x + size * 0.2, y: boxY + size * 0.55 },
                end: { x: x + size * 0.43, y: boxY + size * 0.28 },
                thickness: 1.4,
                color: rgb(0.1, 0.56, 0.2)
              });
              page.drawLine({
                start: { x: x + size * 0.43, y: boxY + size * 0.28 },
                end: { x: x + size * 0.82, y: boxY + size * 0.78 },
                thickness: 1.4,
                color: rgb(0.1, 0.56, 0.2)
              });
            }
            const textOffsetX = x + size + 4;
            const textAreaWidth = Math.max(0, width - size - 6);
            if (checkboxLabel && textAreaWidth > 6) {
              const textSize = Math.max(9, Math.min(14, Math.floor(height * 0.52)));
              drawTextInWidget({
                page,
                font: defaultFont,
                text: checkboxLabel,
                x: textOffsetX,
                y,
                width: textAreaWidth,
                height,
                fontSize: textSize,
                color: rgb(0.09, 0.12, 0.18)
              });
            }
            continue;
          }

          const value = widget.options?.response || widget.options?.defaultValue || "";
          const explicitTextStyle = hasExplicitWidgetTextStyle(widget) ? resolveWidgetTextStyle(widget) : null;
          const effectiveTextStyle = explicitTextStyle || DEFAULT_WIDGET_TEXT_STYLE;
          const textSize = explicitTextStyle
            ? clamp(Number(effectiveTextStyle.fontSize || DEFAULT_WIDGET_TEXT_STYLE.fontSize), 8, 48)
            : Math.max(10, Math.min(16, Math.floor(height * 0.58)));
          const pdfFont = explicitTextStyle
            ? await getPdfFontForStyle(effectiveTextStyle)
            : defaultFont;
          const textColorValues = hexToPdfRgb(effectiveTextStyle.color);
          drawTextInWidget({
            page,
            font: pdfFont,
            text: value,
            x,
            y,
            width,
            height,
            fontSize: textSize,
            color: rgb(textColorValues.red, textColorValues.green, textColorValues.blue),
            underline: Boolean(explicitTextStyle?.underline)
          });
        }
      }

      const draftDoc = new Parse.Object("contracts_Document");
      draftDoc.id = docId;
      draftDoc.set("Placeholders", payload);
      draftDoc.set("IsSignyourself", true);
      if (extUser?.objectId) {
        draftDoc.set("ExtUserPtr", {
          __type: "Pointer",
          className: "contracts_Users",
          objectId: extUser.objectId
        });
      }
      await draftDoc.save();

      let signatureForAudit = "";
      for (let i = 0; i < payload.length; i += 1) {
        const pagePos = Array.isArray(payload[i]?.pos) ? payload[i].pos : [];
        for (let j = 0; j < pagePos.length; j += 1) {
          const field = pagePos[j];
          if (String(field?.type || "").toLowerCase() !== "signature") {
            continue;
          }
          const response = String(field?.options?.response || field?.SignUrl || "").trim();
          if (response) {
            signatureForAudit = response;
            break;
          }
        }
        if (signatureForAudit) {
          break;
        }
      }
      const signedPdfBase64 = await pdfDoc.saveAsBase64({ useObjectStreams: false });
      const signResult = await Parse.Cloud.run("signPdf", {
        pdfFile: signedPdfBase64,
        docId,
        isCustomCompletionMail: false,
        signature: signatureForAudit,
        ...(verificationRequired && verificationEvidence
          ? { identityVerification: verificationEvidence }
          : {})
      });

      const signedUrl =
        typeof signResult === "string"
          ? signResult
          : String(signResult?.data || signResult?.signedUrl || signResult?.url || "");
      if (signedUrl) {
        setPdfUrl(signedUrl);
        pdfBytesRef.current = null;
        setPdfFile(null);
      } else {
        const fallbackBytes = await pdfDoc.save();
        const fallbackBytesUint8 = new Uint8Array(fallbackBytes);
        pdfBytesRef.current = fallbackBytesUint8;
        setPdfFile({ data: fallbackBytesUint8 });
        setPdfUrl("");
      }
      setPlaceholders([]);
      setSelectedId("");
      const finalizedSnapshot = {
        docId,
        name: documentName || t("document-title", { defaultValue: "Document" }),
        signedUrl,
        certificateUrl: ""
      };

      for (let attempt = 0; attempt < 5; attempt += 1) {
        const latestDoc = await fetchDocumentPayload(docId);
        if (latestDoc) {
          finalizedSnapshot.name =
            latestDoc?.Name || finalizedSnapshot.name || t("document-title", { defaultValue: "Document" });
          finalizedSnapshot.signedUrl =
            latestDoc?.SignedUrl || latestDoc?.URL || finalizedSnapshot.signedUrl;
          finalizedSnapshot.certificateUrl =
            latestDoc?.CertificateUrl || finalizedSnapshot.certificateUrl;
          if (latestDoc?.IsCompleted && finalizedSnapshot.signedUrl && finalizedSnapshot.certificateUrl) {
            break;
          }
        }
        await delay(900);
      }

      if (!finalizedSnapshot.certificateUrl) {
        try {
          const certificateRes = await Parse.Cloud.run("generatecertificate", { docId });
          if (certificateRes?.CertificateUrl) {
            finalizedSnapshot.certificateUrl = certificateRes.CertificateUrl;
          }
        } catch (certificateError) {
          // Certificate generation can still complete asynchronously in backend.
        }
      }

      finalizedSnapshot.signedUrl = await resolveDocumentUrl(finalizedSnapshot.signedUrl, docId);
      finalizedSnapshot.certificateUrl = await resolveDocumentUrl(
        finalizedSnapshot.certificateUrl,
        docId
      );
      setFinalizedDoc(finalizedSnapshot);
      setIsFinalized(true);
      setShouldSendCompletionEmail(true);
      setIsPostFinalizeModalOpen(true);
      showSuccess(
        t("new-ui.individual-signing.finalized", {
          defaultValue: "Document finalized successfully."
        })
      );
    } catch (finalizeError) {
      console.error("Failed to finalize document", finalizeError);
      showError(
        finalizeError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
    } finally {
      setIsSaving(false);
    }
  };

  const continueAfterFinalization = async () => {
    if (!finalizedDoc) {
      goToCompletedView();
      return;
    }

    if (!shouldSendCompletionEmail) {
      goToCompletedView(finalizedDoc);
      return;
    }

    const trimmedRecipients = String(completionRecipientsInput || "").trim();
    const rawRecipientList = trimmedRecipients
      ? trimmedRecipients
          .split(/[\s,;]+/)
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean)
      : [];
    const invalidRecipients = rawRecipientList.filter((recipient) => !EMAIL_REGEX.test(recipient));
    const validRecipients = normalizeRecipients(trimmedRecipients);

    if (!validRecipients.length) {
      setCompletionEmailError(
        t("new-ui.individual-signing.email-required", {
          defaultValue: "Add at least one valid recipient email."
        })
      );
      return;
    }
    if (invalidRecipients.length) {
      setCompletionEmailError(
        t("new-ui.individual-signing.email-invalid", {
          defaultValue: "One or more recipient emails are invalid."
        })
      );
      return;
    }
    if (rawRecipientList.length > MAX_FORWARD_RECIPIENTS) {
      setCompletionEmailError(
        t("new-ui.individual-signing.email-max-recipients", {
          defaultValue: "You can send up to {{count}} recipients at a time.",
          count: MAX_FORWARD_RECIPIENTS
        })
      );
      return;
    }

    setCompletionEmailError("");
    setIsSendingFinalizeEmail(true);
    try {
      await sendCompletionAttachments({
        recipients: validRecipients,
        snapshot: finalizedDoc
      });
      goToCompletedView(finalizedDoc);
    } catch (sendError) {
      console.error("Failed to send completion email", sendError);
      setCompletionEmailError(
        sendError?.message ||
          t("new-ui.individual-signing.email-send-failed", {
            defaultValue: "Could not send the completion email."
          })
      );
    } finally {
      setIsSendingFinalizeEmail(false);
    }
  };

  const renderWidgetEditorContent = ({ inline = false } = {}) => {
    if (!selectedPlaceholder) {
      return (
        <p className="text-xs text-app-ink-muted">
          {t("new-ui.individual-signing.no-selection", {
            defaultValue: "Select a field on the document to edit."
          })}
        </p>
      );
    }

    const canEditSignature = canEditSignaturePlaceholder(selectedPlaceholder);
    const isPrefill = isPrefillTextPlaceholder(selectedPlaceholder);
    const supportsTextStyle = TEXT_STYLE_WIDGET_TYPES.has(
      String(selectedPlaceholder?.type || "").trim().toLowerCase()
    );
    const selectedTextStyle = supportsTextStyle
      ? sanitizeTextStyle(selectedPlaceholder?.textStyle)
      : DEFAULT_WIDGET_TEXT_STYLE;
    const updateSelectedTextStyle = (updates = {}) => {
      if (!supportsTextStyle) {
        return;
      }
      updateSelectedPlaceholder({
        textStyle: sanitizeTextStyle({
          ...selectedTextStyle,
          ...(updates || {})
        })
      });
    };

    return (
      <div className="grid grid-cols-1 gap-2">
        <p className="m-0 text-sm font-semibold text-app-ink">
          {getToolLabel(selectedPlaceholder.type, t)}
        </p>
        {isRequestMode && isPrefill ? (
          <p className="m-0 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs text-slate-600">
            {t("new-ui.request-sign.prefill-readonly-note", {
              defaultValue: "Campo prellenado: se mostrará como solo lectura para los firmantes."
            })}
          </p>
        ) : null}
        {isRequestMode && !isPrefill ? (
          <div className="w-full">
            <label
              htmlFor="field-recipient"
              className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
            >
              {isTemplateEditMode
                ? t("new-ui.request-sign.assigned-role", { defaultValue: "Rol asignado" })
                : t("new-ui.request-sign.assigned-recipient", { defaultValue: "Assigned recipient" })}
            </label>
            <select
              id="field-recipient"
              className="w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
              value={selectedPlaceholder.recipientId || ""}
              onChange={(event) => assignRecipientToSelected(event.target.value)}
              disabled={editorLocked}
            >
              <option value="">
                {isTemplateEditMode
                  ? t("new-ui.request-sign.select-role", {
                      defaultValue: "Seleccionar rol"
                    })
                  : t("new-ui.request-sign.select-recipient", {
                      defaultValue: "Select recipient"
                    })}
              </option>
              {requestRecipients.map((recipient) => (
                <option key={recipient.id} value={recipient.id}>
                  {toRecipientLabel(recipient)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {isRequestMode ? (
          <Input
            id="placeholder-api-name"
            label={t("new-ui.individual-signing.api-name", { defaultValue: "API field name" })}
            value={selectedPlaceholder.apiName || ""}
            onChange={(event) => updateSelectedPlaceholder({ apiName: event.target.value.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase() })}
            placeholder={t("new-ui.individual-signing.api-name-hint", { defaultValue: "ej: pais, direccion, ssn" })}
            disabled={editorLocked}
          />
        ) : null}
        {selectedPlaceholder.type === "signature" && canEditSignature ? (
          <Button type="button" variant="ghost" onClick={openSignatureModal} disabled={editorLocked}>
            {t("new-ui.individual-signing.add-signature", { defaultValue: "Add signature" })}
          </Button>
        ) : null}
        {["image", "stamp"].includes(selectedPlaceholder.type) ? (
          <>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onImageSelected}
            />
            <Button type="button" variant="ghost" onClick={openImagePicker} disabled={editorLocked}>
              {selectedPlaceholder.type === "stamp"
                ? t("new-ui.individual-signing.add-stamp-image", { defaultValue: "Upload stamp image" })
                : t("new-ui.individual-signing.add-image", { defaultValue: "Add image" })}
            </Button>
          </>
        ) : null}
        {TEXT_VALUE_TYPES.has(selectedPlaceholder.type) ? (
          <Input
            id="placeholder-text"
            label={
              isPrefill
                ? t("new-ui.request-sign.prefill-value", { defaultValue: "Texto" })
                : t("new-ui.individual-signing.text-value", { defaultValue: "Text value" })
            }
            value={selectedPlaceholder.textValue || ""}
            onChange={(event) => updateSelectedPlaceholder({ textValue: event.target.value })}
            disabled={editorLocked}
          />
        ) : null}
        {selectedPlaceholder.type === "text" && !isPrefill ? (
          <Input
            id="placeholder-placeholder-text"
            label={t("new-ui.individual-signing.placeholder-text", { defaultValue: "Placeholder text" })}
            value={selectedPlaceholder.placeholderText || ""}
            onChange={(event) => updateSelectedPlaceholder({ placeholderText: event.target.value })}
            placeholder={t("new-ui.individual-signing.placeholder-text-hint", { defaultValue: "Escribe aquí..." })}
            disabled={editorLocked}
          />
        ) : null}
        {selectedPlaceholder.type === "checkbox" ? (
          <>
            <Input
              id="placeholder-checkbox-label"
              label={t("new-ui.individual-signing.checkbox-label", { defaultValue: "Checkbox label" })}
              value={selectedPlaceholder.textValue || ""}
              onChange={(event) => updateSelectedPlaceholder({ textValue: event.target.value })}
              disabled={editorLocked}
            />
            <label className="mt-1 inline-flex items-center gap-2 text-xs text-app-ink-muted">
              <input
                type="checkbox"
                checked={Boolean(selectedPlaceholder.checked)}
                onChange={(event) => updateSelectedPlaceholder({ checked: event.target.checked })}
                disabled={editorLocked}
              />
              {t("new-ui.individual-signing.checkbox-checked", { defaultValue: "Checked by default" })}
            </label>
          </>
        ) : null}
        {selectedPlaceholder.type === "date" ? (
          <Input
            id="placeholder-date"
            type="date"
            label={t("new-ui.individual-signing.date-value", { defaultValue: "Date value" })}
            value={selectedPlaceholder.textValue || ""}
            onChange={(event) => updateSelectedPlaceholder({ textValue: event.target.value })}
            disabled={editorLocked}
          />
        ) : null}
        {supportsTextStyle ? (
          <div className="mt-1 rounded-xl border border-app-line bg-app-surface/45 p-2.5">
            <p className="m-0 mb-2 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("new-ui.request-sign.text-format", { defaultValue: "Formato de texto" })}
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="grid gap-1 text-xs text-app-ink-muted">
                <span>{t("new-ui.request-sign.text-format.font", { defaultValue: "Fuente" })}</span>
                <select
                  className="w-full rounded-lg border border-app-line bg-white px-2.5 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                  value={selectedTextStyle.fontFamily}
                  onChange={(event) => updateSelectedTextStyle({ fontFamily: event.target.value })}
                  disabled={editorLocked}
                >
                  {TEXT_STYLE_FONT_CHOICES.map((fontItem) => (
                    <option key={fontItem.value} value={fontItem.value}>
                      {fontItem.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="grid gap-1 text-xs text-app-ink-muted">
                <span>{t("new-ui.request-sign.text-format.size", { defaultValue: "Tamaño" })}</span>
                <input
                  type="number"
                  min={8}
                  max={48}
                  value={selectedTextStyle.fontSize}
                  onChange={(event) => {
                    const nextSize = Number.parseInt(event.target.value, 10);
                    updateSelectedTextStyle({
                      fontSize: Number.isFinite(nextSize) ? nextSize : selectedTextStyle.fontSize
                    });
                  }}
                  className="w-full rounded-lg border border-app-line bg-white px-2.5 py-2 text-sm text-app-ink outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                  disabled={editorLocked}
                />
              </label>
              <label className="grid gap-1 text-xs text-app-ink-muted">
                <span>{t("new-ui.request-sign.text-format.color", { defaultValue: "Color" })}</span>
                <input
                  type="color"
                  value={selectedTextStyle.color}
                  onChange={(event) => updateSelectedTextStyle({ color: event.target.value })}
                  className="h-[38px] w-full rounded-lg border border-app-line bg-white px-1 py-1"
                  disabled={editorLocked}
                />
              </label>
              <div className="grid gap-1 text-xs text-app-ink-muted">
                <span>{t("new-ui.request-sign.text-format.style", { defaultValue: "Estilo" })}</span>
                <div className="flex flex-wrap gap-2">
                  <label className="inline-flex items-center gap-1.5 rounded-md border border-app-line bg-white px-2 py-1 text-xs text-app-ink">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedTextStyle.bold)}
                      onChange={(event) => updateSelectedTextStyle({ bold: event.target.checked })}
                      disabled={editorLocked}
                    />
                    {t("new-ui.request-sign.text-format.bold", { defaultValue: "Bold" })}
                  </label>
                  <label className="inline-flex items-center gap-1.5 rounded-md border border-app-line bg-white px-2 py-1 text-xs text-app-ink">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedTextStyle.italic)}
                      onChange={(event) => updateSelectedTextStyle({ italic: event.target.checked })}
                      disabled={editorLocked}
                    />
                    {t("new-ui.request-sign.text-format.italic", { defaultValue: "Italic" })}
                  </label>
                  <label className="inline-flex items-center gap-1.5 rounded-md border border-app-line bg-white px-2 py-1 text-xs text-app-ink">
                    <input
                      type="checkbox"
                      checked={Boolean(selectedTextStyle.underline)}
                      onChange={(event) => updateSelectedTextStyle({ underline: event.target.checked })}
                      disabled={editorLocked}
                    />
                    {t("new-ui.request-sign.text-format.underline", { defaultValue: "Underline" })}
                  </label>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        <label className="mt-1 inline-flex items-center gap-2 text-xs text-app-ink-muted">
          <input
            type="checkbox"
            checked={selectedPlaceholder.required !== false}
            onChange={(event) => updateSelectedPlaceholder({ required: event.target.checked })}
            disabled={editorLocked}
          />
          {t("required", { defaultValue: "Required" })}
        </label>
        {!inline ? (
          <div className="mt-2 flex justify-end">
            <Button
              type="button"
              onClick={() => setIsWidgetEditorModalOpen(false)}
              disabled={editorLocked}
            >
              {t("new-ui.individual-signing.accept", { defaultValue: "Accept" })}
            </Button>
          </div>
        ) : null}
      </div>
    );
  };

  if (!docId) {
    return (
      <AppShell>
        <Card>
          <p className="ui-kicker">{t("new-ui.sections.kicker", { defaultValue: "DANASign Internal UI" })}</p>
          <h1 className="ui-title mt-2 text-xl">
            {t("new-ui.individual-signing.title", { defaultValue: "Individual signing" })}
          </h1>
          <p className="ui-copy mt-3">
            {t("new-ui.individual-signing.invalid-doc", {
              defaultValue: "Document ID is missing."
            })}
          </p>
          <div className="mt-4">
            <Link to={resolvedBackTo}>
              <Button>{t("new-ui.individual-signing.back-sign-yourself", { defaultValue: "Back to sign yourself" })}</Button>
            </Link>
          </div>
        </Card>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="dana-section-grid">
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[176px_minmax(0,1fr)_300px]">
          <Card className="dana-editor-sticky-pane hidden animate-fade-up [animation-delay:30ms] xl:block">
            <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
              {t("new-ui.individual-signing.drag-fields", { defaultValue: "Arrastra los campos" })}
            </p>
            <div className="mt-3 flex flex-col gap-2">
              {TOOL_DEFINITIONS.map((toolDefinition) => (
                <button
                  key={toolDefinition.type}
                  type="button"
                  draggable={!editorLocked}
                  onDragStart={(event) => onToolDragStart(toolDefinition.type, event)}
                  onDragEnd={() => {
                    setDraggingToolType("");
                    setDraggingToolIsPrefill(false);
                  }}
                  className="cursor-grab rounded-lg border border-app-line bg-white px-2 py-2 text-left text-xs font-semibold text-app-ink transition hover:border-app-brand/40 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-55"
                  disabled={editorLocked}
                >
                  <span className="flex items-center gap-1.5">
                    <Icon name={toolDefinition.icon} className="h-3.5 w-3.5" />
                    <span className="whitespace-nowrap">{getToolLabel(toolDefinition.type, t)}</span>
                  </span>
                </button>
              ))}
            </div>
            {isRequestMode ? (
              <div className="mt-4 rounded-lg border border-app-line bg-app-surface/65 p-2">
                <div className="flex items-center gap-1">
                  <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                    {t("new-ui.request-sign.prefill-title", {
                      defaultValue: "Campos prellenados"
                    })}
                  </p>
                  <span
                    tabIndex={0}
                    className="dana-action-tooltip-target dana-action-tooltip-target--prefill inline-flex h-4 w-4 items-center justify-center rounded-full text-app-ink-muted"
                    title={t("new-ui.request-sign.prefill-help", {
                      defaultValue:
                        "Solo el creador los edita antes de enviar. El firmante los ve como solo lectura."
                    })}
                    data-tooltip={t("new-ui.request-sign.prefill-help", {
                      defaultValue:
                        "Solo el creador los edita antes de enviar. El firmante los ve como solo lectura."
                    })}
                    aria-label={t("new-ui.request-sign.prefill-help", {
                      defaultValue:
                        "Solo el creador los edita antes de enviar. El firmante los ve como solo lectura."
                    })}
                  >
                    <Icon name="help" className="h-3.5 w-3.5" />
                  </span>
                </div>
                <button
                  type="button"
                  draggable={!editorLocked}
                  onDragStart={(event) =>
                    onToolDragStart("text", event, {
                      isPrefill: true
                    })
                  }
                  onDragEnd={() => {
                    setDraggingToolType("");
                    setDraggingToolIsPrefill(false);
                  }}
                  className="mt-2 w-full cursor-grab rounded-lg border border-app-line bg-white px-2 py-2 text-left text-xs font-semibold text-app-ink transition hover:border-app-brand/40 active:cursor-grabbing disabled:cursor-not-allowed disabled:opacity-55"
                  disabled={editorLocked}
                >
                  <span className="flex items-center gap-1.5">
                    <Icon name="templateDoc" className="h-5 w-5 shrink-0" />
                    <span className="whitespace-nowrap">
                      {t("new-ui.request-sign.prefill-text", {
                        defaultValue: "Texto"
                      })}
                    </span>
                  </span>
                </button>
              </div>
            ) : null}
          </Card>

          <Card className="animate-fade-up [animation-delay:40ms]">
            <input
              ref={addPagesInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={onAddPages}
            />
            <div className="dana-pdf-action-row mb-2 flex items-center gap-2">
              <Link to={resolvedBackTo}>
                <Button variant="ghost" className="h-[34px] px-3 text-xs">
                  <span className="flex items-center gap-1.5">
                    <Icon name="chevronLeft" className="h-3.5 w-3.5" />
                    <span>{t("new-ui.common.back", { defaultValue: "Volver" })}</span>
                  </span>
                </Button>
              </Link>
              <Button
                type="button"
                className="h-[34px] px-3 text-xs"
                onClick={finalizeDocument}
                disabled={editorLocked || isLoadingDoc || isMutatingPdf}
              >
                {isSaving
                  ? isTemplateEditMode
                    ? t("saving", { defaultValue: "Guardando..." })
                    : t("new-ui.individual-signing.finalizing", { defaultValue: "Finalizing..." })
                  : isTemplateEditMode
                    ? t("save", { defaultValue: "Guardar" })
                    : isRequestMode
                    ? t("new-ui.individual-signing.finish", { defaultValue: "Finalizar" })
                    : isFinalized
                      ? t("new-ui.individual-signing.finalized-label", { defaultValue: "Finalized" })
                      : t("new-ui.individual-signing.finish", { defaultValue: "Finalizar" })}
              </Button>

              {/* Verification status indicator */}
              {!isRequestMode && verificationRequired ? (
                <div className="flex items-center gap-1.5 text-xs">
                  {verificationComplete ? (
                    <>
                      <Icon name="check" className="h-4 w-4 text-green-600" />
                      <span className="text-green-700">
                        {t("verification.verified", { defaultValue: "Verified" })}
                      </span>
                    </>
                  ) : (
                    <>
                      <Icon name="alert" className="h-4 w-4 text-amber-600" />
                      <span className="text-amber-700">
                        {t("verification.required", { defaultValue: "Verification required" })}
                      </span>
                    </>
                  )}
                </div>
              ) : null}

              <div className="dana-pdf-toolbar grow" role="toolbar" aria-label={t("new-ui.individual-signing.tools", { defaultValue: "Tools" })}>
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={openAddPagesPicker}
                  disabled={editorLocked || isLoadingDoc || isMutatingPdf}
                  title={t("add-pages", { defaultValue: "Add pages" })}
                  aria-label={t("add-pages", { defaultValue: "Add pages" })}
                >
                  <Icon name="plus" />
                </button>
                <span className="dana-pdf-toolbar-sep" />
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={openDeletePageModal}
                  disabled={editorLocked || isLoadingDoc || isMutatingPdf || Math.max(1, deletePageMax) <= 1}
                  title={t("delete", { defaultValue: "Delete page" })}
                  aria-label={t("delete", { defaultValue: "Delete page" })}
                >
                  <Icon name="trash" />
                </button>
                <span className="dana-pdf-toolbar-sep" />
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={zoomIn}
                  disabled={editorLocked || isLoadingDoc || zoomScale >= MAX_ZOOM}
                  title={t("zoom-in", { defaultValue: "Zoom in" })}
                  aria-label={t("zoom-in", { defaultValue: "Zoom in" })}
                >
                  <Icon name="zoomIn" />
                </button>
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={zoomOut}
                  disabled={editorLocked || isLoadingDoc || zoomScale <= MIN_ZOOM}
                  title={t("zoom-out", { defaultValue: "Zoom out" })}
                  aria-label={t("zoom-out", { defaultValue: "Zoom out" })}
                >
                  <Icon name="zoomOut" />
                </button>
                <span className="dana-pdf-toolbar-sep" />
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={() => rotateCurrentPage(90)}
                  disabled={editorLocked || isLoadingDoc || isMutatingPdf}
                  title={t("rotate-right", { defaultValue: "Rotate right" })}
                  aria-label={t("rotate-right", { defaultValue: "Rotate right" })}
                >
                  <Icon name="rotateRight" />
                </button>
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={() => rotateCurrentPage(-90)}
                  disabled={editorLocked || isLoadingDoc || isMutatingPdf}
                  title={t("rotate-left", { defaultValue: "Rotate left" })}
                  aria-label={t("rotate-left", { defaultValue: "Rotate left" })}
                >
                  <Icon name="rotateLeft" />
                </button>
                <span className="dana-pdf-toolbar-sep" />
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={goToFirstPage}
                  disabled={editorLocked || isLoadingDoc || currentPageNumber <= 1}
                  title={t("first-page", { defaultValue: "Primera página" })}
                  aria-label={t("first-page", { defaultValue: "Primera página" })}
                >
                  <Icon name="pageFirst" />
                </button>
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={goToPreviousPage}
                  disabled={editorLocked || isLoadingDoc || currentPageNumber <= 1}
                  title={t("prev", { defaultValue: "Previous page" })}
                  aria-label={t("prev", { defaultValue: "Previous page" })}
                >
                  <Icon name="chevronLeft" />
                </button>
                <span className="dana-pdf-toolbar-page">{currentPageNumber}/{numPages || 1}</span>
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={goToNextPage}
                  disabled={editorLocked || isLoadingDoc || currentPageNumber >= numPages}
                  title={t("next", { defaultValue: "Next page" })}
                  aria-label={t("next", { defaultValue: "Next page" })}
                >
                  <Icon name="chevronRight" />
                </button>
                <button
                  type="button"
                  className="dana-pdf-toolbar-btn"
                  onClick={goToLastPage}
                  disabled={editorLocked || isLoadingDoc || currentPageNumber >= numPages}
                  title={t("last-page", { defaultValue: "Última página" })}
                  aria-label={t("last-page", { defaultValue: "Última página" })}
                >
                  <Icon name="pageLast" />
                </button>
              </div>
            </div>
            <div ref={mobileToolboxRef} className="relative mb-2 xl:hidden">
              <button
                type="button"
                onClick={() => setIsMobileToolboxOpen((current) => !current)}
                className="inline-flex h-[34px] items-center gap-1.5 rounded-lg border border-app-line bg-white px-3 text-xs font-semibold text-app-ink transition hover:border-app-brand/40"
                disabled={editorLocked}
                aria-expanded={isMobileToolboxOpen}
                aria-label={t("new-ui.individual-signing.drag-fields", { defaultValue: "Campos" })}
              >
                <Icon name="layout" className="h-3.5 w-3.5" />
                <span>{t("new-ui.individual-signing.drag-fields", { defaultValue: "Campos" })}</span>
                <Icon
                  name="chevronDown"
                  className={`h-3.5 w-3.5 transition-transform ${isMobileToolboxOpen ? "rotate-180" : ""}`}
                />
              </button>
              {isMobileToolboxOpen ? (
                <div className="absolute left-0 top-[calc(100%+6px)] z-40 w-full rounded-xl border border-app-line bg-white p-2 shadow-lg">
                  <div className="grid grid-cols-2 gap-1.5">
                    {TOOL_DEFINITIONS.map((toolDefinition) => (
                      <button
                        key={`mobile-tool-${toolDefinition.type}`}
                        type="button"
                        onClick={() => addPlaceholderFromMobileTool(toolDefinition.type)}
                        className="rounded-lg border border-app-line bg-white px-2 py-2 text-left text-xs font-semibold text-app-ink transition hover:border-app-brand/40"
                        disabled={editorLocked}
                      >
                        <span className="flex items-center gap-1.5">
                          <Icon name={toolDefinition.icon} className="h-3.5 w-3.5" />
                          <span className="truncate">{getToolLabel(toolDefinition.type, t)}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                  {isRequestMode ? (
                    <div className="mt-2 border-t border-app-line pt-2">
                      <div className="mb-1 flex items-center gap-1">
                        <p className="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                          {t("new-ui.request-sign.prefill-title", {
                            defaultValue: "Campos prellenados"
                          })}
                        </p>
                        <span
                          tabIndex={0}
                          className="dana-action-tooltip-target dana-action-tooltip-target--prefill inline-flex h-4 w-4 items-center justify-center rounded-full text-app-ink-muted"
                          title={t("new-ui.request-sign.prefill-help", {
                            defaultValue:
                              "Solo el creador los edita antes de enviar. El firmante los ve como solo lectura."
                          })}
                          data-tooltip={t("new-ui.request-sign.prefill-help", {
                            defaultValue:
                              "Solo el creador los edita antes de enviar. El firmante los ve como solo lectura."
                          })}
                          aria-label={t("new-ui.request-sign.prefill-help", {
                            defaultValue:
                              "Solo el creador los edita antes de enviar. El firmante los ve como solo lectura."
                          })}
                        >
                          <Icon name="help" className="h-3.5 w-3.5" />
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          addPlaceholderFromMobileTool("text", {
                            isPrefill: true
                          })
                        }
                        className="w-full rounded-lg border border-app-line bg-app-surface/65 px-2 py-2 text-left text-xs font-semibold text-app-ink transition hover:border-app-brand/40"
                        disabled={editorLocked}
                      >
                        <span className="flex items-center gap-1.5">
                          <Icon name="templateDoc" className="h-5 w-5 shrink-0" />
                          <span>
                            {t("new-ui.request-sign.prefill-text", {
                              defaultValue: "Texto"
                            })}
                          </span>
                        </span>
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
            {isFinalized ? (
              <p className="mb-2 text-sm text-app-ink-muted">
                {t("new-ui.individual-signing.finalized-locked", {
                  defaultValue: "This document is finalized and can no longer be edited."
                })}
              </p>
            ) : null}
            {isLoadingDoc ? (
              <p className="dana-muted">{t("new-ui.individual-signing.loading-editor", { defaultValue: "Loading signing editor..." })}</p>
            ) : null}
            {!isLoadingDoc && documentFileSource ? (
              <div ref={viewerRef} className="w-full overflow-x-auto rounded-xl border border-app-line bg-slate-50 p-2">
                <Document
                  file={documentFileSource}
                  loading={t("loading", { defaultValue: "Loading..." })}
                  onLoadSuccess={({ numPages: pages }) => setNumPages(pages)}
                  onLoadError={async (pdfError) => {
                    console.error("Failed to render PDF", pdfError);
                    if (hasTriedPdfRecovery) {
                      showError(
                        t("new-ui.individual-signing.pdf-render-error", {
                          defaultValue: "Could not render the document. Please reload or upload again."
                        })
                      );
                      return;
                    }
                    setHasTriedPdfRecovery(true);
                    const recovery = await loadPdfBinaryWithFallback({
                      primaryUrl: pdfUrl,
                      fallbackUrl: rawDocumentUrl,
                      docId
                    });
                    if (recovery.buffer) {
                      const recoveredBytes = new Uint8Array(recovery.buffer);
                      pdfBytesRef.current = recoveredBytes;
                      setPdfUrl(recovery.url || pdfUrl);
                      setPdfFile({ data: recoveredBytes });
                      showError("");
                      return;
                    }
                    showError(
                      t("new-ui.individual-signing.pdf-render-error", {
                        defaultValue: "Could not render the document. Please reload or upload again."
                      })
                    );
                  }}
                >
                  <div
                    key={`page-${currentPageNumber}`}
                    ref={pageLayerRef}
                    className="relative mx-auto mb-2 w-fit rounded-md border border-app-line bg-white shadow-soft"
                    onDragOver={(event) => {
                      if (editorLocked) {
                        return;
                      }
                      event.preventDefault();
                      const { incomingTool } = resolveIncomingToolDrag(event.dataTransfer);
                      event.dataTransfer.dropEffect = incomingTool ? "copy" : "move";
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      if (editorLocked) {
                        return;
                      }
                      const { incomingTool, incomingIsPrefill } = resolveIncomingToolDrag(
                        event.dataTransfer
                      );

                      if (incomingTool) {
                        addPlaceholderFromTool(incomingTool, currentPageNumber, event, {
                          isPrefill: incomingIsPrefill
                        });
                        setDraggingToolType("");
                        setDraggingToolIsPrefill(false);
                        return;
                      }
                      if (!draggingId) {
                        setDraggingToolType("");
                        setDraggingToolIsPrefill(false);
                        return;
                      }
                      setPlaceholderPosition(draggingId, currentPageNumber, event);
                      setDraggingId("");
                      setDraggingToolType("");
                      setDraggingToolIsPrefill(false);
                    }}
                  >
                    <Page
                      pageNumber={currentPageNumber}
                      width={Math.round(pageWidth * zoomScale)}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                    <div className="pointer-events-none absolute inset-0">
                      {currentPagePlaceholders.map((placeholder) => {
                            const boundedPlaceholder = fitPlaceholderToCanvas(placeholder);
                            const hasTextStyle = hasExplicitWidgetTextStyle(boundedPlaceholder);
                            const widgetTextStyle = hasTextStyle
                              ? resolveWidgetTextStyle(boundedPlaceholder)
                              : null;
                            const placeholderFontSize = Math.round(
                              clamp((Number(boundedPlaceholder.height) || 0.07) * 220 * zoomScale, 10, 24)
                            );
                            const renderedTextFontSize = widgetTextStyle
                              ? Math.round(
                                  clamp(
                                    Number(widgetTextStyle.fontSize || DEFAULT_WIDGET_TEXT_STYLE.fontSize) *
                                      zoomScale,
                                    8,
                                    56
                                  )
                                )
                              : placeholderFontSize;
                            const renderedTextStyle = widgetTextStyle
                              ? {
                                  fontSize: `${renderedTextFontSize}px`,
                                  color: widgetTextStyle.color,
                                  fontFamily: resolveFontCssFamily(widgetTextStyle.fontFamily),
                                  fontWeight: widgetTextStyle.bold ? 700 : 500,
                                  fontStyle: widgetTextStyle.italic ? "italic" : "normal",
                                  textDecoration: widgetTextStyle.underline ? "underline" : "none"
                                }
                              : { fontSize: `${placeholderFontSize}px` };
                            const checkboxIndicatorSize = Math.round(
                              clamp(placeholderFontSize + 2, 14, 24)
                            );
                            const checkboxCheckSize = Math.max(
                              10,
                              Math.round(checkboxIndicatorSize * 0.72)
                            );

                            return (
                            <div
                              key={placeholder.id}
                              role="button"
                              tabIndex={0}
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedId(placeholder.id);
                              }}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  openWidgetEditorFor(placeholder.id);
                                }
                              }}
                              draggable={!editorLocked && resizingId !== placeholder.id}
                              onDragStart={(event) => {
                                if (editorLocked) {
                                  event.preventDefault();
                                  return;
                                }
                                if (resizingId === placeholder.id) {
                                  event.preventDefault();
                                  return;
                                }
                                event.stopPropagation();
                                setDraggingId(placeholder.id);
                                setDraggingToolType("");
                                setDraggingToolIsPrefill(false);
                                event.dataTransfer.effectAllowed = "move";
                                event.dataTransfer.setData("text/plain", placeholder.id);
                              }}
                              onDragEnd={() => setDraggingId("")}
                              className={`pointer-events-auto absolute overflow-hidden rounded border px-1 font-semibold ${
                                selectedId === placeholder.id
                                  ? `${getPlaceholderToneClass(placeholder)} ring-2 ring-app-brand/60`
                                  : getPlaceholderToneClass(placeholder)
                              } ${
                                editorLocked
                                  ? "cursor-default"
                                  : draggingId === placeholder.id
                                  ? "cursor-grabbing"
                                  : "cursor-grab active:cursor-grabbing"
                              }`}
                              style={{
                                left: `${boundedPlaceholder.x * 100}%`,
                                top: `${boundedPlaceholder.y * 100}%`,
                                width: `${boundedPlaceholder.width * 100}%`,
                                height: `${boundedPlaceholder.height * 100}%`
                              }}
                            >
                              {IMAGE_VALUE_TYPES.has(placeholder.type) ? (
                                placeholder.signUrl ? (
                                  <img src={placeholder.signUrl} alt={placeholder.type} className="h-full w-full object-contain" />
                                ) : (
                                  <span
                                    className="flex h-full w-full items-center justify-center text-center leading-tight"
                                    style={renderedTextStyle}
                                  >
                                    {getToolLabel(placeholder.type, t)}
                                  </span>
                                )
                              ) : (
                                placeholder.type === "checkbox" ? (
                                  <span
                                    className="flex h-full w-full items-center justify-center gap-1 px-1"
                                    style={{ fontSize: `${placeholderFontSize}px` }}
                                  >
                                    <span
                                      className={`inline-flex shrink-0 items-center justify-center rounded border ${
                                        placeholder.checked
                                          ? "border-emerald-600 bg-emerald-500 text-white"
                                          : "border-slate-400 bg-white text-transparent"
                                      }`}
                                      style={{
                                        width: `${checkboxIndicatorSize}px`,
                                        height: `${checkboxIndicatorSize}px`
                                      }}
                                    >
                                      <span
                                        className="leading-none"
                                        style={{ fontSize: `${checkboxCheckSize}px` }}
                                      >
                                        ✓
                                      </span>
                                    </span>
                                    <span className="min-w-0 truncate leading-tight">
                                      {placeholder.textValue ||
                                        t("new-ui.individual-signing.checkbox-label-default", {
                                          defaultValue: "I agree"
                                        })}
                                    </span>
                                  </span>
                                ) : (
                                  <span
                                    className="flex h-full w-full items-center justify-center truncate text-center leading-tight"
                                    style={renderedTextStyle}
                                  >
                                    {placeholder.textValue || (placeholder.type === "text" ? (placeholder.placeholderText || placeholder.type) : placeholder.type)}
                                  </span>
                                )
                              )}
                              {isPrefillTextPlaceholder(placeholder) ? (
                                <span className="pointer-events-none absolute bottom-0.5 left-1 z-20 rounded bg-slate-700/85 px-1 py-[1px] text-[9px] font-semibold uppercase tracking-[0.04em] text-white">
                                  {t("new-ui.request-sign.prefill-badge", {
                                    defaultValue: "Prellenado"
                                  })}
                                </span>
                              ) : null}
                              <button
                                type="button"
                                className="absolute left-0.5 top-0.5 z-20 inline-flex h-4 w-4 items-center justify-center rounded border border-slate-300 bg-white/90 text-slate-600 hover:border-red-300 hover:text-red-600"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  removePlaceholderById(placeholder.id);
                                }}
                                disabled={editorLocked}
                                aria-label={t("delete", { defaultValue: "Delete" })}
                              >
                                <Icon name="trash" className="h-2.5 w-2.5" />
                              </button>
                              <button
                                type="button"
                                className="absolute right-5 top-0.5 z-20 inline-flex h-4 w-4 items-center justify-center rounded border border-slate-300 bg-white/90 text-slate-600 hover:bg-white"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openDuplicateWidgetModalFor(placeholder.id);
                                }}
                                disabled={editorLocked}
                                aria-label={t("new-ui.individual-signing.duplicate-widget", {
                                  defaultValue: "Duplicate widget"
                                })}
                              >
                                <Icon name="copy" className="h-2.5 w-2.5" />
                              </button>
                              {RESIZABLE_TYPES.has(placeholder.type) ? (
                                <span
                                  role="presentation"
                                  className="absolute bottom-0 right-0 z-10 flex h-4 w-4 cursor-se-resize items-center justify-center rounded-tl border-l border-t border-slate-400 bg-slate-200 text-slate-600 shadow-sm"
                                  onMouseDown={(event) => startResizePlaceholder(placeholder, event)}
                                >
                                  <svg
                                    className="h-2.5 w-2.5"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    aria-hidden="true"
                                  >
                                    <path
                                      d="M3.2 8.8 8.8 3.2M6.2 8.8l2.6-2.6M3.2 5.8l2.6-2.6"
                                      stroke="currentColor"
                                      strokeWidth="1.1"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                </span>
                              ) : null}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </Document>
              </div>
            ) : null}
          </Card>

          <Card className="dana-editor-sticky-pane animate-fade-up [animation-delay:50ms]">
            {isRequestMode ? (
              <div className="mb-4 rounded-xl border border-app-line bg-app-surface p-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                      {isTemplateEditMode
                        ? t("new-ui.request-sign.roles", { defaultValue: "Roles" })
                        : t("new-ui.request-sign.recipients", { defaultValue: "Signers" })}
                    </p>
                    <HelpHint
                      text={
                        isTemplateEditMode
                          ? t("new-ui.request-sign.roles-help", {
                              defaultValue:
                                "Crea roles y asigna campos a cada rol del template. Los firmantes reales se asignarán cuando uses el template."
                            })
                          : t("new-ui.request-sign.recipients-help", {
                              defaultValue:
                                "Click on a signer to select them and assign fields. Drag and drop to reorder signers (affects signing order if 'Send in Order' is enabled). Use the user icon to configure identity verification, or the X to remove a signer."
                            })
                      }
                    />
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2">
                  {requestRecipients.length ? (
                    requestRecipients.map((recipient, index) => {
                      const recipientToneClass = getRecipientToneClass(recipient);
                      const recipientMenuToneClass = getRecipientMenuToneClass(recipient);
                      const isRecipientActive = recipient.id === activeRecipientId;
                      const identityVerificationEnabled = Boolean(recipient.verificationSettings?.enabled);
                      return (
                      <div 
                        key={recipient.id} 
                        className="flex items-center gap-1.5"
                        draggable={!editorLocked}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", index.toString());
                          e.currentTarget.style.opacity = "0.5";
                        }}
                        onDragEnd={(e) => {
                          e.currentTarget.style.opacity = "1";
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const fromIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                          const toIndex = index;
                          if (fromIndex !== toIndex && !editorLocked) {
                            setRequestRecipients((current) => {
                              const updated = [...current];
                              const [movedItem] = updated.splice(fromIndex, 1);
                              updated.splice(toIndex, 0, movedItem);
                              return updated;
                            });
                          }
                        }}
                      >
                        <button
                          type="button"
                          className={`flex min-w-0 flex-1 items-center justify-between rounded-lg border px-2 py-1.5 text-left text-xs transition ${recipientMenuToneClass} ${
                            isRecipientActive ? "ring-2 ring-app-brand/45" : ""
                          } ${!editorLocked ? "cursor-move" : ""}`}
                          onClick={() => {
                            setActiveRecipientId(recipient.id);
                            setRecipientError("");
                          }}
                        >
                          <span className="truncate" title={recipient.email || recipient.name}>{recipient.name}</span>
                          <span className="ml-2 inline-flex items-center gap-1">
                            <span className={`inline-flex rounded-full border px-1.5 py-0.5 text-[10px] ${recipientMenuToneClass}`}>
                              {recipientFieldCount[recipient.id] || 0}
                            </span>
                            <button
                              type="button"
                              className={`inline-flex h-5 w-5 items-center justify-center rounded-md ${
                                identityVerificationEnabled
                                  ? "bg-green-500 text-white hover:bg-green-600"
                                  : "text-app-ink-muted hover:text-indigo-600"
                              }`}
                              onClick={(e) => {
                                e.stopPropagation();
                                openBiometricSettings(recipient);
                              }}
                              disabled={editorLocked}
                              aria-label={t('identity-settings-open', {
                                defaultValue: 'Identity verification settings'
                              })}
                              title={
                                identityVerificationEnabled
                                  ? t("identity-verification-enabled", {
                                      defaultValue: "Identity verification enabled"
                                    })
                                  : t("identity-settings-open", {
                                      defaultValue: "Identity verification settings"
                                    })
                              }
                            >
                              <Icon name="user" className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="inline-flex h-5 w-5 items-center justify-center rounded-md text-app-ink-muted hover:text-app-danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                requestRecipientDelete(recipient);
                              }}
                              disabled={editorLocked}
                              aria-label={t("delete", { defaultValue: "Delete" })}
                              title={t("delete", { defaultValue: "Delete" })}
                            >
                              <Icon name="close" className="h-3.5 w-3.5" />
                            </button>
                          </span>
                        </button>
                      </div>
                      );
                    })
                  ) : (
                    <p className="m-0 text-xs text-app-ink-muted">
                      {isTemplateEditMode
                        ? t("new-ui.request-sign.no-roles", {
                            defaultValue: "No hay roles agregados."
                          })
                        : t("new-ui.request-sign.no-recipients", {
                            defaultValue: "No signers added."
                          })}
                    </p>
                  )}
                </div>
                {isTemplateEditMode ? (
                  <div className="mt-3 grid grid-cols-1 gap-2">
                    <div className="mt-[10px] grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <div className="sm:col-span-2 flex items-center gap-1.5">
                        <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted whitespace-nowrap">
                          {t("new-ui.request-sign.add-role", {
                            defaultValue: "Agregar rol"
                          })}
                        </p>
                        <HelpHint
                          text={t("new-ui.request-sign.role-query-help", {
                            defaultValue: "Escribe el nombre del rol y presiona Enter."
                          })}
                        />
                      </div>
                      <Input
                        id="request-role-name"
                        className="py-2.5 text-sm"
                        value={roleNameInput}
                        onChange={(event) => {
                          setRoleNameInput(event.target.value);
                          setRecipientError("");
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            addTemplateRole();
                          }
                        }}
                        disabled={editorLocked}
                        placeholder={t("new-ui.request-sign.role-query-placeholder", {
                          defaultValue: "Nombre del rol"
                        })}
                      />
                      <Button
                        type="button"
                        variant="success"
                        className="h-10 w-10 rounded-full p-0"
                        onClick={addTemplateRole}
                        disabled={editorLocked || !String(roleNameInput || "").trim()}
                        aria-label={t("new-ui.request-sign.add-role", {
                          defaultValue: "Agregar rol"
                        })}
                        title={t("new-ui.request-sign.add-role", {
                          defaultValue: "Agregar rol"
                        })}
                      >
                        <Icon name="plus" className="h-4.5 w-4.5" />
                      </Button>
                    </div>
                    {recipientError ? <p className="m-0 text-xs text-app-danger">{recipientError}</p> : null}
                  </div>
                ) : (
                  <div ref={recipientDropdownRef} className="mt-3 grid grid-cols-1 gap-2">
                    <div className="mt-[10px] grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                      <div className="sm:col-span-2 flex items-center gap-1.5">
                        <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted whitespace-nowrap">
                          {t("new-ui.request-sign.add-recipient", {
                            defaultValue: "Add recipient"
                          })}
                        </p>
                        <HelpHint
                          text={t("new-ui.request-sign.recipient-query-help", {
                            defaultValue: "Autocomplete uses your address book. If it's not there, type name + email and press Enter."
                          })}
                        />
                      </div>
                      <Input
                        id="request-recipient-query"
                        className="py-2.5 text-sm"
                        value={recipientQueryInput}
                        onChange={(event) => {
                          setRecipientQueryInput(event.target.value);
                          setRecipientError("");
                          setIsRecipientDropdownOpen(true);
                        }}
                        onFocus={() => {
                          setIsRecipientDropdownOpen(true);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            addRequestRecipient();
                          }
                          if (event.key === "Escape") {
                            setIsRecipientDropdownOpen(false);
                          }
                        }}
                        disabled={editorLocked}
                        placeholder={t("new-ui.request-sign.recipient-query-placeholder", {
                          defaultValue: "Type name or email"
                        })}
                      />
                      <div className="flex items-end">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={openAddRecipientContactModal}
                          disabled={editorLocked}
                          className="!h-11 !w-11 !rounded-full !border !border-[#1ea34f] !bg-[#22c55e] !px-0 !py-0 !text-white hover:!bg-[#1ea34f] focus-visible:!ring-[rgba(34,197,94,0.35)]"
                          aria-label={t("new-ui.request-sign.add-contact", {
                            defaultValue: "Add contact"
                          })}
                          title={t("new-ui.request-sign.add-contact", {
                            defaultValue: "Add contact"
                          })}
                        >
                          <Icon name="plus" className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                    {isLoadingContactbook ? (
                      <p className="m-0 text-[11px] text-app-ink-muted">
                        {t("loading", { defaultValue: "Loading..." })}
                      </p>
                    ) : null}
                    {isRecipientDropdownOpen && filteredContactbook.length ? (
                      <div className="max-h-44 overflow-auto rounded-lg border border-app-line bg-white">
                        {filteredContactbook.map((contact) => (
                          <button
                            key={contact.objectId}
                            type="button"
                            className="flex w-full items-center justify-between border-b border-app-line px-3 py-2 text-left text-xs text-app-ink transition hover:bg-app-brand/5 last:border-b-0"
                            onClick={() => {
                              addRequestRecipientFromContactbook(contact);
                              setIsRecipientDropdownOpen(false);
                            }}
                            disabled={editorLocked}
                          >
                            <span className="truncate">
                              {contact.Name || contact.Email}
                            </span>
                            <span className="ml-2 shrink-0 text-[11px] text-app-ink-muted">{contact.Email}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      onClick={addRequestRecipient}
                      disabled={editorLocked || !String(recipientQueryInput || "").trim()}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <Icon name="plus" className="h-4 w-4" />
                        <span>{t("add", { defaultValue: "Add" })}</span>
                      </span>
                    </Button>
                    {recipientError ? <p className="m-0 text-xs text-app-danger">{recipientError}</p> : null}
                  </div>
                )}
              </div>
            ) : null}

            {!isRequestMode ? (
              <div className="mb-4 rounded-xl border border-app-line bg-app-surface p-3">
                <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                  {t("new-ui.individual-signing.widget-editor-title", { defaultValue: "Field editor" })}
                </p>
                <div className="mt-2">
                  {renderWidgetEditorContent({ inline: true })}
                </div>
              </div>
            ) : null}

            <p className="m-0 text-xs text-app-ink-muted">
              {t("new-ui.individual-signing.widget-editor-help-direct", {
                defaultValue: "Use the edit icon on each field to open its editor directly."
              })}
            </p>
          </Card>
        </div>
      </div>

      <Modal
        isOpen={isAddContactModalOpen}
        title={t("new-ui.request-sign.add-contact-modal-title", {
          defaultValue: "Add contact"
        })}
        onClose={closeAddRecipientContactModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <Input
            id="request-add-contact-name"
            label={t("name", { defaultValue: "Name" })}
            error={recipientContactFieldErrors.name}
            value={recipientContactForm.name}
            onChange={(event) => {
              setRecipientContactForm((current) => ({ ...current, name: event.target.value }));
              setRecipientContactError("");
              setRecipientContactFieldErrors((current) => ({ ...current, name: "" }));
            }}
            maxLength={120}
          />
          <Input
            id="request-add-contact-email"
            type="email"
            label={t("email", { defaultValue: "Email" })}
            error={recipientContactFieldErrors.email}
            value={recipientContactForm.email}
            onChange={(event) => {
              setRecipientContactForm((current) => ({
                ...current,
                email: String(event.target.value || "").toLowerCase().replace(/\s/g, "")
              }));
              setRecipientContactError("");
              setRecipientContactFieldErrors((current) => ({ ...current, email: "" }));
            }}
            maxLength={160}
          />
          <Input
            id="request-add-contact-phone"
            label={t("phone", { defaultValue: "Phone" })}
            value={recipientContactForm.phone}
            onChange={(event) => {
              setRecipientContactForm((current) => ({ ...current, phone: event.target.value }));
              setRecipientContactError("");
            }}
            placeholder={t("phone-optional", { defaultValue: "optional" })}
            maxLength={40}
          />
          {recipientContactError ? <p className="m-0 text-sm text-app-danger">{recipientContactError}</p> : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={closeAddRecipientContactModal}
              disabled={isSavingRecipientContact}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" onClick={saveRecipientContact} disabled={isSavingRecipientContact}>
              {isSavingRecipientContact
                ? t("loading", { defaultValue: "Loading..." })
                : t("save", { defaultValue: "Save" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeletePageModalOpen}
        title={t("new-ui.individual-signing.delete-page-modal-title", {
          defaultValue: "Delete page"
        })}
        onClose={() => {
          setIsDeletePageModalOpen(false);
          setDeletePageModalError("");
        }}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <Input
            id="delete-page-number"
            type="number"
            min={1}
            max={Math.max(1, deletePageMax || 1)}
            label={t("new-ui.individual-signing.delete-page-number-label", {
              defaultValue: "Page number"
            })}
            value={deletePageInput}
            onChange={(event) => {
              setDeletePageInput(event.target.value);
              setDeletePageModalError("");
            }}
            error={deletePageModalError}
          />
          <p className="m-0 text-xs text-app-ink-muted">
            {t("new-ui.individual-signing.delete-page-number-help", {
              defaultValue: "Available pages: 1 to {{count}}",
              count: Math.max(1, deletePageMax || 1)
            })}
          </p>
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsDeletePageModalOpen(false)}
              disabled={editorLocked || isMutatingPdf}
            >
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={confirmDeletePageFromModal}
              disabled={editorLocked || isMutatingPdf}
            >
              {t("new-ui.individual-signing.delete-page-confirm", { defaultValue: "Delete page" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isDeleteRecipientModalOpen}
        title={
          isTemplateEditMode
            ? t("new-ui.request-sign.delete-role-title", { defaultValue: "Eliminar rol" })
            : t("new-ui.request-sign.delete-recipient-title", { defaultValue: "Eliminar firmante" })
        }
        onClose={closeDeleteRecipientModal}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {isTemplateEditMode
              ? t("new-ui.request-sign.delete-role-confirm", {
                  defaultValue: "¿Seguro que quieres eliminar este rol?"
                })
              : t("new-ui.request-sign.delete-recipient-confirm", {
                  defaultValue: "¿Seguro que quieres eliminar este firmante?"
                })}
          </p>
          {recipientPendingDelete ? (
            <p className="m-0 truncate rounded-lg border border-app-line bg-slate-50 px-2 py-1.5 text-xs text-app-ink">
              {toRecipientLabel(recipientPendingDelete)}
            </p>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={closeDeleteRecipientModal}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="button" variant="danger" onClick={confirmDeleteRecipient}>
              {t("delete", { defaultValue: "Delete" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isRequestMode && isWidgetEditorModalOpen}
        title={t("new-ui.individual-signing.widget-editor-title", { defaultValue: "Field editor" })}
        onClose={() => setIsWidgetEditorModalOpen(false)}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        {renderWidgetEditorContent()}
      </Modal>

      <Modal
        isOpen={isDuplicateWidgetModalOpen}
        title={t("new-ui.individual-signing.duplicate-widget-title", {
          defaultValue: "Copy widget to"
        })}
        onClose={() => setIsDuplicateWidgetModalOpen(false)}
        closeIconOnly
        closeLabel={t("close", { defaultValue: "Close" })}
      >
        <div className="grid grid-cols-1 gap-2">
          <Button type="button" variant="ghost" onClick={() => duplicateWidgetWithOption("all-pages")}>
            {t("new-ui.individual-signing.duplicate-option-all-pages", {
              defaultValue: "All pages"
            })}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => duplicateWidgetWithOption("all-pages-but-last")}
          >
            {t("new-ui.individual-signing.duplicate-option-all-pages-but-last", {
              defaultValue: "All pages but last"
            })}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => duplicateWidgetWithOption("all-pages-but-first")}
          >
            {t("new-ui.individual-signing.duplicate-option-all-pages-but-first", {
              defaultValue: "All pages but first"
            })}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => duplicateWidgetWithOption("next-to-current-widget")}
          >
            {t("new-ui.individual-signing.duplicate-option-next-to-current-widget", {
              defaultValue: "Next to current widget"
            })}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isRequestFinalizeModalOpen}
        title={t("send-mail", { defaultValue: "Send Mail" })}
        onClose={() => setIsRequestFinalizeModalOpen(false)}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("placeholder-alert-3", {
              defaultValue: "Are you sure you want to send out this document for signatures?"
            })}
          </p>
          <p className="m-0 text-xs text-app-ink-muted">
            {t("new-ui.request-sign.recipients", { defaultValue: "Signers" })}: {requestRecipients.length}
          </p>
          {requestRecipients.length ? (
            <div className="max-h-36 overflow-y-auto rounded-lg border border-app-line bg-slate-50 p-2">
              <div className="grid grid-cols-1 gap-1">
                {requestRecipients.map((recipient) => (
                  <p key={recipient.id} className="m-0 truncate text-xs text-app-ink">
                    {toRecipientLabel(recipient)}
                  </p>
                ))}
              </div>
            </div>
          ) : null}
          {userCanUsePersonalRequestEmail ? (
            <div className="rounded-lg border border-app-line bg-app-paper p-3">
              <p className="m-0 text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                {t("new-ui.request-sign.email-template-source.label", {
                  defaultValue: "Email template source"
                })}
              </p>
              <div className="mt-2 flex flex-wrap gap-5 text-sm text-app-ink">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    checked={requestEmailSource === REQUEST_EMAIL_SOURCE.CORPORATE}
                    onChange={() => setRequestEmailSource(REQUEST_EMAIL_SOURCE.CORPORATE)}
                  />
                  {t("new-ui.request-sign.email-template-source.corporate", {
                    defaultValue: "Corporate template"
                  })}
                </label>
                <label className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    checked={requestEmailSource === REQUEST_EMAIL_SOURCE.PERSONAL}
                    onChange={() => setRequestEmailSource(REQUEST_EMAIL_SOURCE.PERSONAL)}
                    disabled={!userHasPersonalRequestEmailTemplate}
                  />
                  {t("new-ui.request-sign.email-template-source.personal", {
                    defaultValue: "My personal template"
                  })}
                </label>
              </div>
              {!userHasPersonalRequestEmailTemplate ? (
                <p className="m-0 mt-2 text-xs text-app-ink-muted">
                  {t("new-ui.request-sign.email-template-source.personal-missing", {
                    defaultValue:
                      "Your personal request email template is not configured yet. Go to Profile > Preferences > Email."
                  })}
                </p>
              ) : null}
            </div>
          ) : null}
          {requestSendError ? <p className="m-0 text-sm text-app-danger">{requestSendError}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={sendRequestNow}
              disabled={isSendingRequestEmail}
            >
              {isSendingRequestEmail
                ? t("new-ui.individual-signing.sending-email", { defaultValue: "Sending..." })
                : t("send", { defaultValue: "Send" })}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsRequestFinalizeModalOpen(false)}
              disabled={isSendingRequestEmail}
            >
              {t("close", { defaultValue: "Close" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPostFinalizeModalOpen}
        title={t("new-ui.individual-signing.post-finalize-title", {
          defaultValue: "Document finalized"
        })}
        onClose={() => goToCompletedView(finalizedDoc)}
      >
        <div className="grid grid-cols-1 gap-3">
          <p className="m-0 text-sm text-app-ink-muted">
            {t("new-ui.individual-signing.post-finalize-description", {
              defaultValue:
                "Do you want to send the signed document and completion certificate by email now?"
            })}
          </p>
          <label className="inline-flex items-center gap-2 text-sm text-app-ink">
            <input
              type="checkbox"
              checked={shouldSendCompletionEmail}
              onChange={(event) => {
                setShouldSendCompletionEmail(event.target.checked);
                setCompletionEmailError("");
              }}
              disabled={isSendingFinalizeEmail}
            />
            {t("new-ui.individual-signing.send-by-email", {
              defaultValue: "Send by email"
            })}
          </label>
          {shouldSendCompletionEmail ? (
            <Input
              id="completion-recipients"
              label={t("new-ui.individual-signing.recipients", { defaultValue: "Recipients" })}
              value={completionRecipientsInput}
              onChange={(event) => {
                setCompletionRecipientsInput(event.target.value);
                setCompletionEmailError("");
              }}
              placeholder={t("new-ui.individual-signing.recipients-placeholder", {
                defaultValue: "email1@company.com, email2@company.com"
              })}
              error={completionEmailError}
              disabled={isSendingFinalizeEmail}
            />
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={continueAfterFinalization}
              disabled={isSendingFinalizeEmail}
            >
              {isSendingFinalizeEmail
                ? t("new-ui.individual-signing.sending-email", {
                    defaultValue: "Sending..."
                  })
                : t("new-ui.individual-signing.continue", { defaultValue: "Continue" })}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => goToCompletedView(finalizedDoc)}
              disabled={isSendingFinalizeEmail}
            >
              {t("new-ui.individual-signing.skip-email", { defaultValue: "Skip email" })}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isSignatureModalOpen}
        title={t("new-ui.individual-signing.signature-modal-title", { defaultValue: "Signature" })}
        onClose={() => setIsSignatureModalOpen(false)}
        panelClassName="max-w-2xl"
      >
        <div className="grid grid-cols-1 gap-3">
          <div className="inline-flex w-fit rounded-xl border border-app-line bg-white p-1">
            {profileSignatureUrl ? (
              <button
                type="button"
                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                  signatureMode === "saved" ? "bg-app-brand text-white" : "text-app-ink-muted"
                }`}
                onClick={() => setSignatureMode("saved")}
              >
                {t("new-ui.individual-signing.saved-signature", { defaultValue: "Saved" })}
              </button>
            ) : null}
            <button
              type="button"
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                signatureMode === "draw" ? "bg-app-brand text-white" : "text-app-ink-muted"
              }`}
              onClick={() => setSignatureMode("draw")}
            >
              {t("draw", { defaultValue: "Draw" })}
            </button>
            <button
              type="button"
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                signatureMode === "type" ? "bg-app-brand text-white" : "text-app-ink-muted"
              }`}
              onClick={() => setSignatureMode("type")}
            >
              {t("type", { defaultValue: "Type" })}
            </button>
            <button
              type="button"
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${
                signatureMode === "upload" ? "bg-app-brand text-white" : "text-app-ink-muted"
              }`}
              onClick={() => setSignatureMode("upload")}
            >
              {t("upload", { defaultValue: "Upload" })}
            </button>
          </div>

          {!profileSignatureUrl ? (
            <p className="m-0 text-xs text-app-ink-muted">
              {t("new-ui.individual-signing.no-saved-signature-hint", {
                defaultValue: "You don't have a saved signature yet. Create one here and optionally save it to profile."
              })}
            </p>
          ) : null}

          {signatureMode === "saved" ? (
            <div className="rounded-xl border border-app-line bg-white p-3">
              <div className="flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-5">
                <img src={profileSignatureUrl} alt="Saved signature" className="max-h-24 w-auto object-contain" />
              </div>
              <p className="mt-2 mb-0 text-xs text-app-ink-muted">
                {t("new-ui.individual-signing.saved-signature-help", {
                  defaultValue: "This signature comes from your profile configuration."
                })}
              </p>
            </div>
          ) : null}

          {signatureMode === "draw" ? (
            <div className="rounded-xl border border-app-line bg-white p-2">
              <SignatureCanvas
                ref={signaturePadRef}
                canvasProps={{ className: "w-full rounded-md border border-slate-200", style: { height: '420px', touchAction: 'none' } }}
                penColor="#111827"
                backgroundColor="rgba(0,0,0,0)"
                onBegin={() => {
                  // Ensure canvas dimensions match CSS layout on first interaction
                  const canvas = signaturePadRef.current?.getCanvas();
                  if (canvas) {
                    const rect = canvas.getBoundingClientRect();
                    if (rect.width > 0 && canvas.width !== Math.floor(rect.width)) {
                      canvas.width = Math.floor(rect.width);
                      canvas.height = Math.floor(rect.height);
                    }
                  }
                }}
              />
              <div className="mt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => signaturePadRef.current?.clear()}
                >
                  {t("reset", { defaultValue: "Reset" })}
                </Button>
              </div>
            </div>
          ) : null}

          {signatureMode === "type" ? (
            <Input
              id="typed-signature"
              label={t("new-ui.individual-signing.type-signature", { defaultValue: "Type your signature" })}
              value={typedSignature}
              onChange={(event) => setTypedSignature(event.target.value)}
            />
          ) : null}

          {signatureMode === "upload" ? (
            <div className="rounded-xl border border-app-line bg-white p-3">
              <input
                ref={signatureUploadInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onSignatureUploadSelected}
              />
              <Button type="button" variant="ghost" onClick={() => signatureUploadInputRef.current?.click()}>
                {t("new-ui.individual-signing.upload-signature-image", { defaultValue: "Upload signature image" })}
              </Button>
              {uploadedSignatureDataUrl ? (
                <div className="mt-2 flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-4">
                  <img src={uploadedSignatureDataUrl} alt="Uploaded signature" className="max-h-24 w-auto object-contain" />
                </div>
              ) : null}
            </div>
          ) : null}

          {signatureMode !== "saved" ? (
            <label className="inline-flex items-center gap-2 text-xs text-app-ink-muted">
              <input
                type="checkbox"
                checked={saveSignatureToProfile}
                onChange={(event) => setSaveSignatureToProfile(event.target.checked)}
                disabled={Boolean(profileSignatureRecordId) && signatureMode === "saved"}
              />
              {t("new-ui.individual-signing.save-to-profile", {
                defaultValue: "Save as my default profile signature"
              })}
            </label>
          ) : null}

          <div className="flex gap-2">
            <Button type="button" onClick={saveSignatureForSelected} disabled={isSavingSignatureChoice}>
              {isSavingSignatureChoice
                ? t("saving", { defaultValue: "Saving..." })
                : t("save", { defaultValue: "Save" })}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setIsSignatureModalOpen(false)}>
              {t("cancel", { defaultValue: "Cancel" })}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Biometric Verification Modal */}
      {showVerificationModal && !isRequestMode ? (
        <Suspense fallback={<div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50"><div className="text-white">Loading verification...</div></div>}>
          <VerificationModal
            isOpen={showVerificationModal}
            onClose={handleVerificationClose}
            onSuccess={handleVerificationSuccess}
            signer={{
              name: Parse.User.current()?.get?.("name") || localStorage.getItem("username") || "",
              email: normalizeEmailAddress(
                Parse.User.current()?.get?.("email") || localStorage.getItem("userEmail") || ""
              ),
              objectId: Parse.User.current()?.id || ""
            }}
            documentId={docId}
            verificationConfig={verificationConfig}
          />
        </Suspense>
      ) : null}

      {/* Biometric Settings Modal */}
      <BiometricSettingsModal
        isOpen={isBiometricSettingsOpen}
        onClose={() => {
          setIsBiometricSettingsOpen(false);
          setSelectedRecipientForBiometric(null);
        }}
        onSave={saveBiometricSettings}
        recipient={selectedRecipientForBiometric}
        initialSettings={selectedRecipientForBiometric?.verificationSettings || {}}
      />
    </AppShell>
  );
}

export default IndividualSigningPage;
